import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Video, Mic, Square, Play, Pause, RotateCcw, Save, FolderOpen,
  Camera, Volume2, VolumeX, Trash2, Plus, Settings, Download,
  ChevronLeft, ChevronRight, Layers, Grid3X3, Maximize2, Music,
  Circle, StopCircle, RefreshCw, Headphones, SlidersHorizontal,
  Clock, Zap, Check, X, ArrowLeft, Share2, Monitor, Move, 
  ZoomIn, ZoomOut, RotateCw, Eye, EyeOff, Image, Wand2, Palette,
  Layout, GripVertical, Lock, Unlock, Sparkles, Upload,
  SkipBack, SkipForward, Rewind, FastForward, Keyboard, 
  Instagram, Music2, Radio, Timer, HelpCircle, Info, BookOpen,
  ChevronDown, ChevronUp, ArrowRight, Target, Lightbulb, Shield,
  Users, Copy, PhoneOff, Link2, ExternalLink, User
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { SelfieSegmentation } from '@mediapipe/selfie_segmentation';

const API = process.env.REACT_APP_BACKEND_URL;

// Background presets
const BACKGROUND_PRESETS = [
  { id: 'transparent', name: 'Transparent', type: 'transparent', color: null },
  { id: 'black', name: 'Black', type: 'color', color: '#000000' },
  { id: 'white', name: 'White', type: 'color', color: '#FFFFFF' },
  { id: 'green', name: 'Green Screen', type: 'color', color: '#00FF00' },
  { id: 'blue', name: 'Blue Screen', type: 'color', color: '#0000FF' },
  { id: 'studio', name: 'Studio', type: 'gradient', color: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)' },
  { id: 'sunset', name: 'Sunset', type: 'gradient', color: 'linear-gradient(180deg, #ff6b6b 0%, #feca57 100%)' },
  { id: 'ocean', name: 'Ocean', type: 'gradient', color: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' },
  { id: 'forest', name: 'Forest', type: 'gradient', color: 'linear-gradient(180deg, #134e5e 0%, #71b280 100%)' },
  { id: 'stage', name: 'Stage Lights', type: 'gradient', color: 'linear-gradient(180deg, #0f0c29 0%, #302b63 50%, #24243e 100%)' },
];

// Layout presets
const LAYOUT_PRESETS = [
  { id: 'single', name: 'Single', grid: [1, 1], icon: '▢' },
  { id: 'split-h', name: 'Split Horizontal', grid: [2, 1], icon: '▭▭' },
  { id: 'split-v', name: 'Split Vertical', grid: [1, 2], icon: '▯▯' },
  { id: 'grid-4', name: 'Grid 2x2', grid: [2, 2], icon: '▢▢▢▢' },
  { id: 'grid-6', name: 'Grid 3x2', grid: [3, 2], icon: '▢▢▢▢▢▢' },
  { id: 'grid-9', name: 'Grid 3x3', grid: [3, 3], icon: '▢▢▢' },
  { id: 'pip', name: 'Picture in Picture', grid: 'pip', icon: '▢◳' },
  { id: 'free', name: 'Free Position', grid: 'free', icon: '✱' },
];

// Samba tempo presets (BPM)
const TEMPO_PRESETS = [
  { name: 'Samba de Roda', bpm: 100, description: 'Traditional circle samba' },
  { name: 'Samba Reggae', bpm: 90, description: 'Bahian style' },
  { name: 'Samba Enredo', bpm: 140, description: 'Carnival parade tempo' },
  { name: 'Pagode', bpm: 110, description: 'Party samba' },
  { name: 'Bossa Nova', bpm: 70, description: 'Smooth and relaxed' },
  { name: 'Maracatu', bpm: 80, description: 'Pernambuco rhythm' },
  { name: 'Afoxé', bpm: 60, description: 'Candomblé influenced' },
  { name: 'Batucada', bpm: 130, description: 'High energy drums' },
];

// Metronome component with tap tempo
const Metronome = ({ bpm, setBpm, isPlaying, onToggle, volume = 80, onVolumeChange }) => {
  const [tapTimes, setTapTimes] = useState([]);
  const [showPresets, setShowPresets] = useState(false);
  const audioContextRef = useRef(null);
  const nextNoteTimeRef = useRef(0);
  const timerIdRef = useRef(null);
  const beatCountRef = useRef(0);
  
  // Initialize audio context
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => {
      if (timerIdRef.current) clearTimeout(timerIdRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, []);
  
  // Play click sound
  const playClick = useCallback((time, isDownbeat) => {
    if (!audioContextRef.current) return;
    
    const osc = audioContextRef.current.createOscillator();
    const gain = audioContextRef.current.createGain();
    
    osc.connect(gain);
    gain.connect(audioContextRef.current.destination);
    
    // Scale volume based on the volume prop (0-100)
    const volumeScale = volume / 100;
    osc.frequency.value = isDownbeat ? 1000 : 800;
    gain.gain.value = (isDownbeat ? 0.3 : 0.15) * volumeScale;
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.1);
    
    osc.start(time);
    osc.stop(time + 0.1);
  }, [volume]);
  
  // Scheduler
  const scheduler = useCallback(() => {
    if (!audioContextRef.current || !isPlaying) return;
    
    const currentTime = audioContextRef.current.currentTime;
    const secondsPerBeat = 60.0 / bpm;
    
    while (nextNoteTimeRef.current < currentTime + 0.1) {
      const isDownbeat = beatCountRef.current % 4 === 0;
      playClick(nextNoteTimeRef.current, isDownbeat);
      nextNoteTimeRef.current += secondsPerBeat;
      beatCountRef.current++;
    }
    
    timerIdRef.current = setTimeout(scheduler, 25);
  }, [bpm, isPlaying, playClick]);
  
  // Start/stop metronome
  useEffect(() => {
    if (isPlaying) {
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume();
      }
      nextNoteTimeRef.current = audioContextRef.current.currentTime;
      beatCountRef.current = 0;
      scheduler();
    } else {
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
        timerIdRef.current = null;
      }
    }
    
    return () => {
      if (timerIdRef.current) clearTimeout(timerIdRef.current);
    };
  }, [isPlaying, scheduler]);
  
  // Tap tempo
  const handleTap = () => {
    const now = Date.now();
    const newTapTimes = [...tapTimes, now].slice(-4); // Keep last 4 taps
    setTapTimes(newTapTimes);
    
    if (newTapTimes.length >= 2) {
      const intervals = [];
      for (let i = 1; i < newTapTimes.length; i++) {
        intervals.push(newTapTimes[i] - newTapTimes[i - 1]);
      }
      const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      const calculatedBpm = Math.round(60000 / avgInterval);
      
      if (calculatedBpm >= 40 && calculatedBpm <= 200) {
        setBpm(calculatedBpm);
      }
    }
    
    // Reset tap times if too long between taps
    setTimeout(() => {
      setTapTimes(prev => prev.filter(t => Date.now() - t < 2000));
    }, 2000);
  };
  
  return (
    <div className="bg-stone-800/50 rounded-xl p-3 border border-stone-700">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-bold text-amber-400 flex items-center gap-2">
          <Clock className="w-3 h-3" />
          Metronome
        </h3>
        <button
          onClick={onToggle}
          className={`p-1.5 rounded transition-colors ${
            isPlaying 
              ? 'bg-red-500 text-white' 
              : 'bg-amber-500 text-black'
          }`}
        >
          {isPlaying ? <Square className="w-3 h-3" /> : <Play className="w-3 h-3" />}
        </button>
      </div>
      
      {/* BPM Display & Control - Compact */}
      <div className="flex items-center gap-2 mb-2">
        <button onClick={() => setBpm(Math.max(40, bpm - 5))} className="p-1 bg-stone-700 hover:bg-stone-600 rounded">
          <ChevronLeft className="w-3 h-3" />
        </button>
        <div className="flex-1 text-center">
          <span className="text-xl font-bold text-white">{bpm}</span>
          <span className="text-xs text-stone-400 ml-1">BPM</span>
        </div>
        <button onClick={() => setBpm(Math.min(200, bpm + 5))} className="p-1 bg-stone-700 hover:bg-stone-600 rounded">
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>
      
      {/* BPM Slider */}
      <input
        type="range"
        min="40"
        max="200"
        value={bpm}
        onChange={(e) => setBpm(parseInt(e.target.value))}
        className="w-full h-1.5 bg-stone-700 rounded-lg appearance-none cursor-pointer accent-amber-500 mb-2"
      />
      
      {/* Volume Control - Inline */}
      <div className="flex items-center gap-2 mb-2">
        <Volume2 className="w-3 h-3 text-stone-400" />
        <input
          type="range"
          min="0"
          max="100"
          value={volume}
          onChange={(e) => onVolumeChange && onVolumeChange(parseInt(e.target.value))}
          className="flex-1 h-1 bg-stone-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
        />
        <span className="text-[10px] text-stone-400 w-6">{volume}%</span>
      </div>
      
      {/* Tap Tempo - Smaller */}
      <button
        onClick={handleTap}
        className="w-full py-1.5 bg-stone-700 hover:bg-stone-600 text-white text-xs font-bold rounded flex items-center justify-center gap-1"
      >
        <Zap className="w-3 h-3" />
        TAP
      </button>
      
      {/* Presets - Collapsible */}
      <details className="mt-2">
        <summary className="text-xs text-stone-400 cursor-pointer hover:text-amber-400">Presets</summary>
        <div className="flex flex-wrap gap-1 mt-1">
          {[60, 80, 100, 120].map(preset => (
            <button key={preset} onClick={() => setBpm(preset)} className={`px-2 py-0.5 rounded text-xs ${bpm === preset ? 'bg-amber-500 text-black' : 'bg-stone-700 text-stone-300'}`}>{preset}</button>
          ))}
        </div>
      </details>
    </div>
  );
};

// Background Removal Hook - Uses MediaPipe Selfie Segmentation
const useBackgroundRemoval = (videoElement, canvasElement, enabled, options = {}) => {
  const segmentationRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const { 
    backgroundType = 'transparent', // 'transparent', 'color', 'image', 'blur'
    backgroundColor = null,
    backgroundImage = null,
    blurAmount = 10,
    edgeBlur = 3
  } = options;

  useEffect(() => {
    if (!enabled || !videoElement || !canvasElement) return;

    const initSegmentation = async () => {
      try {
        const selfieSegmentation = new SelfieSegmentation({
          locateFile: (file) => {
            return `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`;
          }
        });

        selfieSegmentation.setOptions({
          modelSelection: 1, // 0 = general, 1 = landscape (better for full body)
          selfieMode: false,
        });

        selfieSegmentation.onResults((results) => {
          if (!canvasElement) return;
          
          const ctx = canvasElement.getContext('2d');
          const width = canvasElement.width;
          const height = canvasElement.height;
          
          ctx.save();
          ctx.clearRect(0, 0, width, height);
          
          // Draw background first
          if (backgroundType === 'color' && backgroundColor) {
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(0, 0, width, height);
          } else if (backgroundType === 'image' && backgroundImage) {
            ctx.drawImage(backgroundImage, 0, 0, width, height);
          } else if (backgroundType === 'blur') {
            ctx.filter = `blur(${blurAmount}px)`;
            ctx.drawImage(results.image, 0, 0, width, height);
            ctx.filter = 'none';
          }
          // For transparent, we don't draw anything
          
          // Draw the person with mask
          ctx.globalCompositeOperation = 'destination-over';
          
          // Apply edge blur to mask for smoother edges
          if (edgeBlur > 0) {
            ctx.filter = `blur(${edgeBlur}px)`;
          }
          ctx.drawImage(results.segmentationMask, 0, 0, width, height);
          ctx.filter = 'none';
          
          ctx.globalCompositeOperation = 'source-in';
          ctx.drawImage(results.image, 0, 0, width, height);
          
          ctx.restore();
        });

        segmentationRef.current = selfieSegmentation;
        setIsProcessing(true);

        // Process frames
        const processFrame = async () => {
          if (videoElement.readyState >= 2 && segmentationRef.current) {
            await segmentationRef.current.send({ image: videoElement });
          }
          animationFrameRef.current = requestAnimationFrame(processFrame);
        };

        processFrame();
      } catch (error) {
        console.error('Error initializing segmentation:', error);
        toast.error('Background removal failed to initialize');
      }
    };

    initSegmentation();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (segmentationRef.current) {
        segmentationRef.current.close();
      }
      setIsProcessing(false);
    };
  }, [enabled, videoElement, canvasElement, backgroundType, backgroundColor, backgroundImage, blurAmount, edgeBlur]);

  return { isProcessing };
};

// Chroma Key (Green Screen) processor
const applyChromaKey = (ctx, videoElement, width, height, keyColor = '#00FF00', tolerance = 0.4) => {
  ctx.drawImage(videoElement, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  // Parse key color
  const keyR = parseInt(keyColor.slice(1, 3), 16);
  const keyG = parseInt(keyColor.slice(3, 5), 16);
  const keyB = parseInt(keyColor.slice(5, 7), 16);
  
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    
    // Calculate color distance from key color
    const distance = Math.sqrt(
      Math.pow((r - keyR) / 255, 2) +
      Math.pow((g - keyG) / 255, 2) +
      Math.pow((b - keyB) / 255, 2)
    ) / Math.sqrt(3);
    
    if (distance < tolerance) {
      // Make pixel transparent
      data[i + 3] = 0;
    } else if (distance < tolerance + 0.1) {
      // Soft edge
      data[i + 3] = Math.floor(255 * (distance - tolerance) / 0.1);
    }
  }
  
  ctx.putImageData(imageData, 0, 0);
};

// Draggable Layer Component for Layout Editor
const DraggableLayer = ({ 
  track, 
  position, 
  size, 
  isSelected,
  onSelect,
  onDrag,
  onResize,
  containerRef,
  videoRef
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const layerRef = useRef(null);

  const handleMouseDown = (e, action = 'drag') => {
    e.stopPropagation();
    onSelect(track.id);
    
    if (action === 'drag') {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    } else if (action === 'resize') {
      setIsResizing(true);
      setDragStart({ x: e.clientX, y: e.clientY, width: size.width, height: size.height });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      
      if (isDragging) {
        const newX = Math.max(0, Math.min(e.clientX - dragStart.x, rect.width - size.width));
        const newY = Math.max(0, Math.min(e.clientY - dragStart.y, rect.height - size.height));
        onDrag(track.id, { x: newX, y: newY });
      } else if (isResizing) {
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        const newWidth = Math.max(50, Math.min(dragStart.width + deltaX, rect.width - position.x));
        const newHeight = Math.max(50, Math.min(dragStart.height + deltaY, rect.height - position.y));
        onResize(track.id, { width: newWidth, height: newHeight });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, position, size, track.id, onDrag, onResize, containerRef]);

  return (
    <div
      ref={layerRef}
      className={`absolute cursor-move transition-shadow ${
        isSelected ? 'ring-2 ring-amber-500 shadow-lg shadow-amber-500/30' : ''
      }`}
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: track.zIndex || 1,
      }}
      onMouseDown={(e) => handleMouseDown(e, 'drag')}
    >
      {/* Video/Canvas content */}
      <div className="w-full h-full bg-black rounded-lg overflow-hidden">
        {track.processedCanvas ? (
          <canvas
            ref={videoRef}
            className="w-full h-full object-contain"
          />
        ) : track.blobUrl ? (
          <video
            ref={videoRef}
            src={track.blobUrl}
            className="w-full h-full object-contain"
            muted={track.muted}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-600">
            <Video className="w-8 h-8" />
          </div>
        )}
      </div>
      
      {/* Track label */}
      <div className="absolute bottom-1 left-1 bg-black/70 px-2 py-0.5 rounded text-xs text-white">
        {track.name}
      </div>
      
      {/* Resize handle */}
      {isSelected && (
        <div
          className="absolute bottom-0 right-0 w-4 h-4 bg-amber-500 cursor-se-resize rounded-tl"
          onMouseDown={(e) => handleMouseDown(e, 'resize')}
        />
      )}
    </div>
  );
};

// Layout Editor Component
const LayoutEditor = ({ 
  tracks, 
  selectedTrackId,
  onSelectTrack,
  onUpdateTrack,
  background,
  canvasSize = { width: 1920, height: 1080 }
}) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  
  // Calculate scale to fit container
  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const scaleX = rect.width / canvasSize.width;
      const scaleY = rect.height / canvasSize.height;
      setScale(Math.min(scaleX, scaleY, 1));
    }
  }, [canvasSize]);

  const handleDrag = (trackId, newPosition) => {
    onUpdateTrack(trackId, { position: newPosition });
  };

  const handleResize = (trackId, newSize) => {
    onUpdateTrack(trackId, { size: newSize });
  };

  // Get background style
  const getBackgroundStyle = () => {
    if (!background) return { backgroundColor: '#000000' };
    
    if (background.type === 'transparent') {
      return {
        backgroundImage: `
          linear-gradient(45deg, #333 25%, transparent 25%),
          linear-gradient(-45deg, #333 25%, transparent 25%),
          linear-gradient(45deg, transparent 75%, #333 75%),
          linear-gradient(-45deg, transparent 75%, #333 75%)
        `,
        backgroundSize: '20px 20px',
        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
        backgroundColor: '#222'
      };
    } else if (background.type === 'color') {
      return { backgroundColor: background.color };
    } else if (background.type === 'gradient') {
      return { background: background.color };
    } else if (background.type === 'image') {
      return { 
        backgroundImage: `url(${background.url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    }
    return { backgroundColor: '#000000' };
  };

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full overflow-hidden rounded-lg"
      style={getBackgroundStyle()}
    >
      <div
        className="absolute"
        style={{
          width: canvasSize.width * scale,
          height: canvasSize.height * scale,
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        {tracks.map((track) => (
          <DraggableLayer
            key={track.id}
            track={track}
            position={{
              x: (track.position?.x || 0) * scale,
              y: (track.position?.y || 0) * scale,
            }}
            size={{
              width: (track.size?.width || 400) * scale,
              height: (track.size?.height || 300) * scale,
            }}
            isSelected={selectedTrackId === track.id}
            onSelect={onSelectTrack}
            onDrag={(id, pos) => handleDrag(id, { x: pos.x / scale, y: pos.y / scale })}
            onResize={(id, size) => handleResize(id, { width: size.width / scale, height: size.height / scale })}
            containerRef={containerRef}
          />
        ))}
      </div>
    </div>
  );
};

// Video Track component with expandable full-body view
const VideoTrack = ({ 
  track, 
  index, 
  isRecording, 
  isPlaying,
  onVolumeChange, 
  onMute, 
  onSolo, 
  onDelete,
  onSelect,
  onExpand,
  isSelected,
  isExpanded
}) => {
  const videoRef = useRef(null);
  
  useEffect(() => {
    if (videoRef.current && track.blobUrl) {
      videoRef.current.src = track.blobUrl;
    }
  }, [track.blobUrl]);
  
  // Sync playback state
  useEffect(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isPlaying]);
  
  return (
    <div 
      className={`bg-stone-800/80 rounded-xl overflow-hidden border-2 transition-all ${
        isSelected ? 'border-amber-500' : 'border-stone-700 hover:border-stone-600'
      }`}
    >
      {/* Large Video Preview - Full body visible */}
      <div 
        className="relative bg-black cursor-pointer"
        onClick={() => onSelect(track.id)}
      >
        <div className={`${isExpanded ? 'aspect-video' : 'aspect-[16/10]'} w-full`}>
          {track.blobUrl ? (
            <video 
              ref={videoRef}
              className="w-full h-full object-contain bg-black"
              muted={track.muted}
              playsInline
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-600">
              <Video className="w-12 h-12" />
            </div>
          )}
        </div>
        
        {/* Overlay controls */}
        <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
          <span className="bg-black/70 px-2 py-1 rounded text-xs font-bold text-white">
            Track {index + 1}
          </span>
          <div className="flex gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); onExpand(track.id); }}
              className="p-1.5 bg-black/70 hover:bg-amber-500 rounded transition-colors"
              title={isExpanded ? "Collapse" : "Expand to full view"}
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(track.id); }}
              className="p-1.5 bg-black/70 hover:bg-red-500 rounded transition-colors"
              title="Delete track"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Duration badge */}
        <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-xs text-white font-mono">
          {track.duration ? `${Math.floor(track.duration / 60)}:${String(Math.floor(track.duration % 60)).padStart(2, '0')}` : '0:00'}
        </div>
        
        {/* Track name */}
        <div className="absolute bottom-2 left-2 bg-black/70 px-2 py-1 rounded text-xs text-white truncate max-w-[60%]">
          {track.name || 'Untitled'}
        </div>
      </div>
      
      {/* Compact Controls Bar */}
      <div className="p-2 flex items-center gap-2 bg-stone-900/50">
        {/* Mute button */}
        <button
          onClick={(e) => { e.stopPropagation(); onMute(track.id); }}
          className={`p-1.5 rounded transition-colors ${track.muted ? 'bg-red-500 text-white' : 'bg-stone-700 text-stone-300 hover:bg-stone-600'}`}
          title={track.muted ? "Unmute" : "Mute"}
        >
          {track.muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        
        {/* Volume slider */}
        <input
          type="range"
          min="0"
          max="100"
          value={track.volume}
          onChange={(e) => onVolumeChange(track.id, parseInt(e.target.value))}
          className="flex-1 h-1.5 bg-stone-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
        />
        <span className="text-xs text-stone-400 w-8">{track.volume}%</span>
        
        {/* Solo button */}
        <button
          onClick={(e) => { e.stopPropagation(); onSolo(track.id); }}
          className={`px-2 py-1 rounded text-xs font-bold transition-colors ${
            track.solo ? 'bg-amber-500 text-black' : 'bg-stone-700 text-stone-300 hover:bg-stone-600'
          }`}
        >
          S
        </button>
        
        {/* Delete button */}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(track.id); }}
          className="p-1.5 bg-stone-700 hover:bg-red-500 text-stone-300 hover:text-white rounded transition-colors"
          title="Delete track"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Main Multi-Cam Studio Component
const MultiCamStudio = () => {
  // Project state
  const [projectName, setProjectName] = useState('Untitled Project');
  const [tracks, setTracks] = useState([]);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [expandedTrackId, setExpandedTrackId] = useState(null); // For full-body view
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  
  // Metronome state
  const [metronomeOn, setMetronomeOn] = useState(false);
  const [bpm, setBpm] = useState(120);
  const [countIn, setCountIn] = useState(true);
  const [countInBeats, setCountInBeats] = useState(4);
  
  // Enhanced Input Sources State
  const [videoSources, setVideoSources] = useState([]); // All available video devices
  const [audioSources, setAudioSources] = useState([]); // All available audio input devices
  const [audioOutputs, setAudioOutputs] = useState([]); // All available audio output devices
  const [selectedVideoSources, setSelectedVideoSources] = useState([]); // Multiple cameras can be selected
  const [selectedAudioSource, setSelectedAudioSource] = useState(null);
  const [selectedAudioOutput, setSelectedAudioOutput] = useState(null); // Selected audio output device
  const [screenShareStream, setScreenShareStream] = useState(null);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [activeStreams, setActiveStreams] = useState({}); // { sourceId: MediaStream }
  const [showPhoneSetup, setShowPhoneSetup] = useState(false);
  const [setupTab, setSetupTab] = useState('phone'); // 'phone' or 'hdmi'
  const [inputPanelTab, setInputPanelTab] = useState('video'); // 'video', 'audio', 'screen'
  const [inputTypeTab, setInputTypeTab] = useState('all'); // 'all', 'usb', 'hdmi', 'wifi', 'bluetooth', 'virtual'
  
  // Input Type Categories
  const INPUT_TYPES = [
    { id: 'all', label: 'All', icon: '📷', description: 'Show all cameras' },
    { id: 'usb', label: 'USB', icon: '🔌', description: 'USB webcams & capture cards' },
    { id: 'hdmi', label: 'HDMI', icon: '📺', description: 'HDMI capture devices' },
    { id: 'wifi', label: 'Wi-Fi', icon: '📶', description: 'Wireless cameras (iPhone, Android)' },
    { id: 'bluetooth', label: 'Bluetooth', icon: '📡', description: 'Bluetooth cameras' },
    { id: 'virtual', label: 'Virtual', icon: '🎬', description: 'OBS, software cameras' },
  ];
  
  // Audio Output State (using audioOutputs from Input Sources State above)
  const [selectedMetronomeOutput, setSelectedMetronomeOutput] = useState('default'); // Where metronome plays
  const [selectedTrackOutput, setSelectedTrackOutput] = useState('default'); // Where tracks play
  const [metronomeVolume, setMetronomeVolume] = useState(80); // Metronome volume 0-100
  const [monitorVolume, setMonitorVolume] = useState(100); // Track monitor volume 0-100
  
  // Input Gain Control
  const [inputGain, setInputGain] = useState(75); // Input gain 0-100 (75 = slight reduction to prevent clipping)
  const [inputLevel, setInputLevel] = useState(0); // Real-time input level for meter
  const [isInputClipping, setIsInputClipping] = useState(false); // Clipping indicator
  
  // Background Removal & Layout State
  const [backgroundRemovalEnabled, setBackgroundRemovalEnabled] = useState(false);
  const [backgroundRemovalMode, setBackgroundRemovalMode] = useState('ai'); // 'ai' or 'greenscreen'
  const [greenScreenColor, setGreenScreenColor] = useState('#00FF00');
  const [greenScreenTolerance, setGreenScreenTolerance] = useState(0.5); // How much key color to remove (similarity)
  const [greenScreenSpill, setGreenScreenSpill] = useState(0.3); // Edge softness / spill control
  const [aiEdgeSmooth, setAiEdgeSmooth] = useState(0.5); // AI removal edge smoothness
  const aiEdgeSmoothRef = useRef(0.5); // Ref for real-time updates without reinit
  const [selectedBackground, setSelectedBackgroundState] = useState(BACKGROUND_PRESETS[0]);
  const selectedBackgroundRef = useRef(BACKGROUND_PRESETS[0]); // Ref for real-time background updates
  const loadedBgImageRef = useRef(null); // Pre-loaded background image
  
  // Wrapper to update both state and ref, and pre-load image if needed
  const setSelectedBackground = useCallback((bg) => {
    console.log('Setting background:', bg?.id, bg?.type);
    setSelectedBackgroundState(bg);
    selectedBackgroundRef.current = bg;
    
    // Pre-load image outside of animation loop
    if (bg?.type === 'image' && bg?.url) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        console.log('Background image loaded successfully');
        loadedBgImageRef.current = img;
      };
      img.onerror = () => {
        console.error('Failed to load background image');
        loadedBgImageRef.current = null;
      };
      img.src = bg.url;
    } else {
      loadedBgImageRef.current = null;
    }
  }, []);
  
  const [customBackgroundUrl, setCustomBackgroundUrl] = useState(null);
  const [selectedLayout, setSelectedLayout] = useState(LAYOUT_PRESETS[0]);
  const [showLayoutEditor, setShowLayoutEditor] = useState(false);
  const [showBackgroundPicker, setShowBackgroundPicker] = useState(false);
  const [isGeneratingBackground, setIsGeneratingBackground] = useState(false);
  const [aiBackgroundPrompt, setAiBackgroundPrompt] = useState('');
  
  // Screen Share Expanded View State
  const [screenShareExpanded, setScreenShareExpanded] = useState(false);
  const screenShareVideoRef = useRef(null);
  
  // Canvas Compositing State (Phase 3)
  const [isCompositing, setIsCompositing] = useState(false);
  const [compositeQuality, setCompositeQuality] = useState('720p'); // '480p', '720p', '1080p'
  
  // Export State (Phase 4)
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportFormat, setExportFormat] = useState('webm'); // 'webm' or 'mp4'
  
  // Audio Mixer State
  const [masterVolume, setMasterVolume] = useState(100);
  const [showMixer, setShowMixer] = useState(true);
  const [trackLevels, setTrackLevels] = useState({}); // Real-time audio levels per track
  const [trackWaveforms, setTrackWaveforms] = useState({}); // Waveform data per track
  
  // Video Filters State
  const [videoFilter, setVideoFilter] = useState('none'); // Active filter
  const VIDEO_FILTERS = [
    { id: 'none', name: 'None', css: '' },
    { id: 'warm', name: 'Warm', css: 'sepia(0.3) saturate(1.2) brightness(1.05)' },
    { id: 'cool', name: 'Cool', css: 'hue-rotate(20deg) saturate(0.9) brightness(1.05)' },
    { id: 'vintage', name: 'Vintage', css: 'sepia(0.5) contrast(1.1) brightness(0.95)' },
    { id: 'bw', name: 'B&W', css: 'grayscale(1) contrast(1.1)' },
    { id: 'vivid', name: 'Vivid', css: 'saturate(1.5) contrast(1.1)' },
    { id: 'cinematic', name: 'Cinematic', css: 'contrast(1.2) saturate(0.85) brightness(0.95)' },
    { id: 'soft', name: 'Soft', css: 'brightness(1.1) contrast(0.9) saturate(0.9)' },
  ];
  
  // Track Reordering State
  const [draggingTrackId, setDraggingTrackId] = useState(null);
  
  // Keyboard Shortcuts State
  const [showShortcuts, setShowShortcuts] = useState(false);
  
  // Help/Tutorial State
  const [showHelp, setShowHelp] = useState(false);
  const [showQuickStart, setShowQuickStart] = useState(true); // Show on first load
  
  // Beat Sync State
  const [beatSyncEnabled, setBeatSyncEnabled] = useState(false);
  const [detectedBPM, setDetectedBPM] = useState(null);
  
  // Audio Effects State
  const [audioEffects, setAudioEffects] = useState({
    reverb: 0,
    delay: 0,
    eq: { bass: 0, mid: 0, treble: 0 }
  });
  const [showAudioEffects, setShowAudioEffects] = useState(false);
  
  // Duet Mode State
  const [duetMode, setDuetMode] = useState(false);
  const [duetVideo, setDuetVideo] = useState(null); // { url, name, duration }
  const [duetVideoRef, setDuetVideoRef] = useState(null);
  
  // Challenge Templates State
  const [showChallenges, setShowChallenges] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const CHALLENGE_TEMPLATES = [
    { id: 'samba', name: 'Samba de Roda', bpm: 100, description: 'Traditional Bahian circle samba', emoji: '🥁', color: 'from-amber-500 to-orange-600' },
    { id: 'bossa', name: 'Bossa Nova', bpm: 140, description: 'Smooth Brasilian jazz rhythm', emoji: '🎸', color: 'from-emerald-500 to-teal-600' },
    { id: 'forro', name: 'Forró', bpm: 120, description: 'Northeastern Brasilian dance', emoji: '🪗', color: 'from-pink-500 to-rose-600' },
    { id: 'maracatu', name: 'Maracatu', bpm: 80, description: 'Afro-Brasilian carnival rhythm', emoji: '🎺', color: 'from-purple-500 to-indigo-600' },
    { id: 'capoeira', name: 'Capoeira', bpm: 110, description: 'Martial art rhythm (berimbau)', emoji: '🥋', color: 'from-yellow-500 to-amber-600' },
    { id: 'afoxe', name: 'Afoxé', bpm: 90, description: 'Sacred candomblé rhythm', emoji: '🔔', color: 'from-blue-500 to-cyan-600' },
    { id: 'ijexá', name: 'Ijexá', bpm: 85, description: 'Yoruba-influenced rhythm', emoji: '✨', color: 'from-violet-500 to-purple-600' },
    { id: 'free', name: 'Free Style', bpm: 120, description: 'Create your own rhythm', emoji: '🎵', color: 'from-stone-500 to-stone-600' },
  ];
  
  // Refs
  const previewVideoRefs = useRef({}); // Multiple preview refs
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const playbackVideoRefs = useRef({}); // Hidden audio playback videos
  const compositeVideoRefs = useRef({}); // Composite Preview display videos (visual only, muted)
  const duetVideoElementRef = useRef(null); // Duet mode imported video
  const duetFileInputRef = useRef(null); // File input for duet video
  const audioContextRef = useRef(null);
  const gainNodesRef = useRef({}); // Per-track gain nodes
  const masterGainRef = useRef(null); // Master gain node
  const audioSourcesRef = useRef({}); // MediaElementSource nodes
  const analyserNodesRef = useRef({}); // For visualizing audio levels
  const eqNodesRef = useRef({}); // Per-track EQ nodes { trackId: { low, mid, high } }
  
  // Canvas Compositing Refs (Phase 3)
  const compositeCanvasRef = useRef(null);
  const compositeContextRef = useRef(null);
  const compositeAnimationRef = useRef(null);
  const segmentationModelsRef = useRef({}); // MediaPipe models per track
  const processedCanvasesRef = useRef({}); // Processed canvases per track
  const backgroundImageRef = useRef(null); // Loaded background image
  
  // Chroma Key Preview Refs
  const chromaCanvasRef = useRef(null);
  const chromaAnimationRef = useRef(null);
  
  // AI Background Removal Refs
  const aiCanvasRef = useRef(null);
  const aiSegmentationRef = useRef(null);
  const aiAnimationRef = useRef(null);
  
  // Export Refs (Phase 4)
  const exportRecorderRef = useRef(null);
  const exportChunksRef = useRef([]);
  
  // Audio Output Refs
  const metronomeAudioRef = useRef(null); // Hidden audio element for metronome with setSinkId
  
  // Input Gain Refs
  const inputGainNodeRef = useRef(null); // Gain node for input
  const inputAnalyserRef = useRef(null); // Analyser for input level metering
  const inputAudioContextRef = useRef(null); // Separate audio context for input
  const inputLevelAnimationRef = useRef(null); // Animation frame for level metering
  
  // Get all available input devices
  useEffect(() => {
    const getDevices = async () => {
      try {
        // Request permission first
        await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        
        const devices = await navigator.mediaDevices.enumerateDevices();
        
        // Video devices (cameras) - categorized by connection type with generic names
        const videoDevices = devices.filter(d => d.kind === 'videoinput').map((d, i) => {
          const connectionType = categorizeDevice(d.label || '');
          return {
            id: d.deviceId,
            label: d.label || `Camera ${i + 1}`,
            displayName: getGenericDeviceName(d.label || '', connectionType, i),
            connectionType: connectionType,
            active: false
          };
        });
        setVideoSources(videoDevices);
        
        // Audio input devices (microphones)
        const audioDevices = devices.filter(d => d.kind === 'audioinput').map((d, i) => {
          const label = d.label?.toLowerCase() || '';
          let type = 'built-in';
          let displayName = d.label || `Microphone ${i + 1}`;
          
          // Detect interface type
          if (label.includes('x air') || label.includes('xair') || label.includes('xr18') || label.includes('x18')) {
            type = 'interface';
            displayName = `X Air 18 - Ch ${i + 1}`;
          } else if (label.includes('behringer') || label.includes('midas')) {
            type = 'interface';
          } else if (label.includes('scarlett') || label.includes('focusrite')) {
            type = 'interface';
          } else if (label.includes('presonus') || label.includes('audiobox')) {
            type = 'interface';
          } else if (label.includes('apollo') || label.includes('universal audio') || label.includes('uad')) {
            type = 'interface';
          } else if (label.includes('motu') || label.includes('m-audio')) {
            type = 'interface';
          } else if (label.includes('interface') || label.includes('audio interface')) {
            type = 'interface';
          } else if (label.includes('usb')) {
            type = 'usb';
          } else if (label.includes('bluetooth') || label.includes('airpods') || label.includes('wireless')) {
            type = 'bluetooth';
          }
          
          return {
            id: d.deviceId,
            label: displayName,
            originalLabel: d.label,
            type: type
          };
        });
        setAudioSources(audioDevices);
        
        // Audio output devices (speakers, headphones)
        const outputDevices = devices.filter(d => d.kind === 'audiooutput').map((d, i) => ({
          id: d.deviceId || 'default',
          label: d.label || `Speaker ${i + 1}`,
          type: d.label?.toLowerCase().includes('bluetooth') ? 'bluetooth' :
                d.label?.toLowerCase().includes('airpods') ? 'bluetooth' :
                d.label?.toLowerCase().includes('wireless') ? 'bluetooth' :
                d.label?.toLowerCase().includes('headphone') ? 'headphones' :
                d.label?.toLowerCase().includes('headset') ? 'headphones' :
                d.label?.toLowerCase().includes('usb') ? 'usb' :
                d.label?.toLowerCase().includes('hdmi') ? 'hdmi' :
                d.label?.toLowerCase().includes('displayport') ? 'hdmi' :
                d.label?.toLowerCase().includes('interface') ? 'interface' :
                d.label?.toLowerCase().includes('scarlett') ? 'interface' :
                d.label?.toLowerCase().includes('focusrite') ? 'interface' :
                d.label?.toLowerCase().includes('presonus') ? 'interface' :
                'built-in'
        }));
        // Add default option if not present
        if (!outputDevices.find(d => d.id === 'default')) {
          outputDevices.unshift({ id: 'default', label: 'System Default', type: 'built-in' });
        }
        setAudioOutputs(outputDevices);
        
        // Auto-select first devices
        if (videoDevices.length > 0 && selectedVideoSources.length === 0) {
          setSelectedVideoSources([videoDevices[0].id]);
        }
        if (audioDevices.length > 0 && !selectedAudioSource) {
          setSelectedAudioSource(audioDevices[0].id);
        }
      } catch (err) {
        console.error('Error getting devices:', err);
        toast.error('Please allow camera and microphone access');
      }
    };
    
    getDevices();
    
    // Listen for device changes (when user plugs in new camera/mic)
    navigator.mediaDevices.addEventListener('devicechange', getDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', getDevices);
    };
  }, []);
  
  // Start/stop camera streams when selection changes
  useEffect(() => {
    const updateStreams = async () => {
      // Stop streams for deselected sources
      Object.keys(activeStreams).forEach(sourceId => {
        if (!selectedVideoSources.includes(sourceId) && sourceId !== 'screen') {
          activeStreams[sourceId]?.getTracks().forEach(track => track.stop());
          const newStreams = { ...activeStreams };
          delete newStreams[sourceId];
          setActiveStreams(newStreams);
        }
      });
      
      // Start streams for newly selected sources
      for (const sourceId of selectedVideoSources) {
        if (!activeStreams[sourceId]) {
          try {
            // Try with exact deviceId first, then fall back to ideal
            let stream;
            try {
              stream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: sourceId } },
                audio: selectedAudioSource ? { deviceId: { exact: selectedAudioSource } } : true
              });
            } catch (exactErr) {
              console.log('Exact deviceId failed, trying ideal:', exactErr.message);
              // Fallback: try with 'ideal' instead of 'exact'
              stream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { ideal: sourceId } },
                audio: selectedAudioSource ? { deviceId: { ideal: selectedAudioSource } } : true
              });
            }
            
            setActiveStreams(prev => ({ ...prev, [sourceId]: stream }));
            
            // Connect to preview
            if (previewVideoRefs.current[sourceId]) {
              previewVideoRefs.current[sourceId].srcObject = stream;
            }
            
            console.log('Camera stream started:', sourceId);
          } catch (err) {
            console.error(`Error starting camera ${sourceId}:`, err);
            toast.error(`Could not access camera: ${err.message}`);
            // Remove from selected sources if failed
            setSelectedVideoSources(prev => prev.filter(id => id !== sourceId));
          }
        }
      }
    };
    
    if (selectedVideoSources.length > 0) {
      updateStreams();
    }
    
    return () => {
      // Cleanup on unmount
      Object.values(activeStreams).forEach(stream => {
        stream?.getTracks().forEach(track => track.stop());
      });
    };
  }, [selectedVideoSources, selectedAudioSource]);
  
  // Toggle video source selection
  const toggleVideoSource = (sourceId) => {
    setSelectedVideoSources(prev => {
      if (prev.includes(sourceId)) {
        // Deselect - stop the stream
        if (activeStreams[sourceId]) {
          activeStreams[sourceId].getTracks().forEach(track => track.stop());
          setActiveStreams(curr => {
            const newStreams = { ...curr };
            delete newStreams[sourceId];
            return newStreams;
          });
        }
        return prev.filter(id => id !== sourceId);
      } else {
        // Select - limit to 3 cameras max
        if (prev.length >= 3) {
          toast.error('Maximum 3 cameras can be active at once');
          return prev;
        }
        return [...prev, sourceId];
      }
    });
  };
  
  // Start screen sharing
  const startScreenShare = async () => {
    try {
      // Try to get display media with preferences for window capture
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          // Don't restrict displaySurface - let user choose any type
        },
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          // These settings help capture clean DAW audio
        },
        preferCurrentTab: false,
        selfBrowserSurface: 'exclude',
        systemAudio: 'include' // Try to include system audio
      });
      
      setScreenShareStream(stream);
      setIsScreenSharing(true);
      setActiveStreams(prev => ({ ...prev, screen: stream }));
      
      // Connect to preview video element
      setTimeout(() => {
        if (previewVideoRefs.current.screen) {
          previewVideoRefs.current.screen.srcObject = stream;
        }
      }, 100);
      
      // Handle when user stops sharing via browser UI
      stream.getVideoTracks()[0].onended = () => {
        stopScreenShare();
      };
      
      const hasAudio = stream.getAudioTracks().length > 0;
      toast.success(`Screen sharing started${hasAudio ? ' with audio' : ' (no audio captured)'}`);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Screen share error:', err);
        if (err.name === 'NotAllowedError') {
          toast.error('Permission denied. Check System Settings → Privacy → Screen Recording');
        } else {
          toast.error('Could not start screen sharing');
        }
      }
    }
  };
  
  // Stop screen sharing
  const stopScreenShare = () => {
    if (screenShareStream) {
      screenShareStream.getTracks().forEach(track => track.stop());
      setScreenShareStream(null);
    }
    setIsScreenSharing(false);
    setActiveStreams(prev => {
      const newStreams = { ...prev };
      delete newStreams.screen;
      return newStreams;
    });
  };
  
  // Change audio output device
  const changeAudioOutput = async (deviceId) => {
    setSelectedAudioOutput(deviceId);
    
    // Apply to all playback video elements
    try {
      const promises = Object.values(playbackVideoRefs.current).map(async (video) => {
        if (video && typeof video.setSinkId === 'function') {
          await video.setSinkId(deviceId);
        }
      });
      await Promise.all(promises);
      
      const device = audioOutputs.find(d => d.id === deviceId);
      toast.success(`Audio output: ${device?.label || 'Selected device'}`);
    } catch (err) {
      console.error('Error setting audio output:', err);
      toast.error('Could not change audio output. Your browser may not support this feature.');
    }
  };
  
  // Real-time Chroma Key Processing
  const processChromaKey = useCallback((videoElement, canvas, ctx, backgroundColor) => {
    if (!videoElement || !canvas || !ctx || videoElement.readyState < 2) return;
    
    const width = canvas.width;
    const height = canvas.height;
    
    // Parse background color to RGB
    let bgR = 0, bgG = 0, bgB = 0;
    if (backgroundColor && backgroundColor.startsWith('#')) {
      const hex = backgroundColor.slice(1);
      bgR = parseInt(hex.substr(0, 2), 16) || 0;
      bgG = parseInt(hex.substr(2, 2), 16) || 0;
      bgB = parseInt(hex.substr(4, 2), 16) || 0;
    }
    
    // Draw video frame directly to main canvas
    ctx.drawImage(videoElement, 0, 0, width, height);
    
    // Get image data
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    
    // Determine key color
    const isGreen = greenScreenColor === '#00FF00';
    const isBlue = greenScreenColor === '#0000FF';
    
    // SIMILARITY: How close to pure green/blue the pixel must be (0-1)
    // Lower = only pure colors, Higher = more shades included
    const similarity = greenScreenTolerance; // 0 to 1
    
    // SPILL: Controls edge detection / how much color bleed to allow (0-1)
    // Lower = harder edges, Higher = softer/more forgiving
    const spill = greenScreenSpill; // 0 to 1
    
    // Calculate thresholds from sliders
    // Similarity controls the main detection threshold
    const minDominance = 60 - (similarity * 50); // Range: 60 (strict) to 10 (loose)
    
    // Spill controls the saturation requirement
    const minSaturation = 0.3 - (spill * 0.25); // Range: 0.3 (strict) to 0.05 (loose)
    
    // Process each pixel
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      
      // Calculate color properties
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const saturation = max > 0 ? (max - min) / max : 0;
      
      let isKeyColor = false;
      
      if (isGreen) {
        // GREEN SCREEN detection
        // 1. Green must be the dominant channel
        // 2. Green must exceed red AND blue by minDominance
        // 3. Pixel must have enough saturation (to avoid gray/white/skin)
        const greenDominance = g - Math.max(r, b);
        
        isKeyColor = (
          g === max &&                    // Green is brightest
          greenDominance >= minDominance && // Green dominates enough
          saturation >= minSaturation &&  // Has color (not gray/white)
          g > 40                          // Not too dark
        );
      } else if (isBlue) {
        // BLUE SCREEN detection
        const blueDominance = b - Math.max(r, g);
        
        isKeyColor = (
          b === max &&                    // Blue is brightest
          blueDominance >= minDominance && // Blue dominates enough
          saturation >= minSaturation &&  // Has color (not gray/white)
          b > 40                          // Not too dark
        );
      }
      
      // Replace key color pixels with background
      if (isKeyColor) {
        data[i] = bgR;
        data[i + 1] = bgG;
        data[i + 2] = bgB;
      }
    }
    
    // Put the processed image back
    ctx.putImageData(imageData, 0, 0);
  }, [greenScreenColor, greenScreenTolerance, greenScreenSpill]);
  
  // Start chroma key preview loop
  const startChromaPreview = useCallback(() => {
    if (chromaAnimationRef.current) {
      cancelAnimationFrame(chromaAnimationRef.current);
    }
    
    const canvas = chromaCanvasRef.current;
    if (!canvas) {
      console.log('CHROMA DEBUG: Canvas ref is null!');
      return;
    }
    
    console.log('CHROMA DEBUG: Starting chroma preview loop');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    
    let frameCount = 0;
    
    const animate = () => {
      // Get the first active video source
      const firstSourceId = selectedVideoSources[0];
      const videoEl = previewVideoRefs.current[firstSourceId];
      
      frameCount++;
      
      // Debug every 60 frames (about once per second)
      if (frameCount % 60 === 0) {
        console.log('CHROMA DEBUG:', {
          firstSourceId,
          hasVideoEl: !!videoEl,
          videoReadyState: videoEl?.readyState,
          backgroundRemovalEnabled,
          backgroundRemovalMode,
          canvasSize: { w: canvas.width, h: canvas.height },
          allRefs: Object.keys(previewVideoRefs.current)
        });
      }
      
      if (videoEl && videoEl.readyState >= 2 && backgroundRemovalEnabled && backgroundRemovalMode === 'greenscreen') {
        // Set canvas size to match video
        if (canvas.width !== videoEl.videoWidth || canvas.height !== videoEl.videoHeight) {
          canvas.width = videoEl.videoWidth || 640;
          canvas.height = videoEl.videoHeight || 480;
          console.log('CHROMA DEBUG: Set canvas size to', canvas.width, 'x', canvas.height);
        }
        
        // Determine background color
        let bgColor = '#000000'; // Default black
        if (selectedBackground?.color) {
          bgColor = selectedBackground.color;
        } else if (selectedBackground?.gradient) {
          bgColor = selectedBackground.color || '#000000';
        }
        
        // Process chroma key with background color
        processChromaKey(videoEl, canvas, ctx, bgColor);
      } else if (frameCount % 60 === 0) {
        // Draw something to show canvas is working
        ctx.fillStyle = 'red';
        ctx.fillRect(0, 0, canvas.width || 100, canvas.height || 100);
        ctx.fillStyle = 'white';
        ctx.font = '20px Arial';
        ctx.fillText('Waiting for video...', 10, 50);
      }
      
      chromaAnimationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
  }, [selectedVideoSources, backgroundRemovalEnabled, backgroundRemovalMode, selectedBackground, processChromaKey]);
  
  // Stop chroma preview
  const stopChromaPreview = useCallback(() => {
    if (chromaAnimationRef.current) {
      cancelAnimationFrame(chromaAnimationRef.current);
      chromaAnimationRef.current = null;
    }
  }, []);
  
  // Effect to start/stop chroma preview based on mode
  useEffect(() => {
    if (backgroundRemovalEnabled && backgroundRemovalMode === 'greenscreen' && selectedVideoSources.length > 0) {
      startChromaPreview();
    } else {
      stopChromaPreview();
    }
    
    return () => stopChromaPreview();
  }, [backgroundRemovalEnabled, backgroundRemovalMode, selectedVideoSources, startChromaPreview, stopChromaPreview]);
  
  // AI Background Removal using MediaPipe Selfie Segmentation
  const startAIRemoval = useCallback(async () => {
    const canvas = aiCanvasRef.current;
    const firstSourceId = selectedVideoSources[0];
    const videoEl = previewVideoRefs.current[firstSourceId];
    
    if (!canvas || !videoEl) {
      console.log('AI REMOVAL: Missing canvas or video element');
      return;
    }
    
    // Stop any existing AI processing
    if (aiAnimationRef.current) {
      cancelAnimationFrame(aiAnimationRef.current);
    }
    if (aiSegmentationRef.current) {
      try {
        aiSegmentationRef.current.close();
      } catch (e) {
        // Ignore close errors
      }
    }
    
    try {
      console.log('AI REMOVAL: Initializing MediaPipe Selfie Segmentation...');
      
      const selfieSegmentation = new SelfieSegmentation({
        locateFile: (file) => {
          return `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1.1675465747/${file}`;
        }
      });
      
      selfieSegmentation.setOptions({
        modelSelection: 1,
        selfieMode: false,
      });
      
      selfieSegmentation.onResults((results) => {
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        const width = canvas.width;
        const height = canvas.height;
        
        // Get current background from ref
        const currentBg = selectedBackgroundRef.current;
        let bgImage = loadedBgImageRef.current;
        
        // Get edge smoothness from ref (0 to 1)
        const edgeSmooth = aiEdgeSmoothRef.current || 0;
        const blurAmount = edgeSmooth * 6; // 0 to 6px blur
        
        // If we have an image URL but no loaded image, try to use it directly
        if (!bgImage && currentBg?.type === 'image' && currentBg?.url) {
          var tempImg = new window.Image();
          tempImg.src = currentBg.url;
          if (tempImg.complete && tempImg.naturalWidth > 0) {
            bgImage = tempImg;
            loadedBgImageRef.current = tempImg;
          }
        }
        
        // Clear canvas
        ctx.clearRect(0, 0, width, height);
        
        // If edge smoothing is enabled, use a temp canvas for the mask
        if (blurAmount > 0.5) {
          // Create temp canvas for blurred mask
          var maskCanvas = document.createElement('canvas');
          maskCanvas.width = width;
          maskCanvas.height = height;
          var maskCtx = maskCanvas.getContext('2d');
          
          // Draw mask with blur
          maskCtx.filter = 'blur(' + blurAmount + 'px)';
          maskCtx.drawImage(results.segmentationMask, 0, 0, width, height);
          
          // Draw person
          ctx.drawImage(results.image, 0, 0, width, height);
          
          // Apply blurred mask
          ctx.globalCompositeOperation = 'destination-in';
          ctx.drawImage(maskCanvas, 0, 0, width, height);
        } else {
          // No blur - direct drawing
          ctx.drawImage(results.image, 0, 0, width, height);
          ctx.globalCompositeOperation = 'destination-in';
          ctx.drawImage(results.segmentationMask, 0, 0, width, height);
        }
        
        // Step 3: Draw background behind the person
        ctx.globalCompositeOperation = 'destination-over';
        
        // Use pre-loaded image if available, otherwise solid color
        if (bgImage && bgImage.complete && bgImage.naturalWidth > 0) {
          ctx.drawImage(bgImage, 0, 0, width, height);
        } else {
          ctx.fillStyle = currentBg?.color || '#000000';
          ctx.fillRect(0, 0, width, height);
        }
        
        // Reset
        ctx.globalCompositeOperation = 'source-over';
      });
      
      await selfieSegmentation.initialize();
      aiSegmentationRef.current = selfieSegmentation;
      
      console.log('AI REMOVAL: MediaPipe initialized successfully');
      
      // Process frames
      const processFrame = async () => {
        if (videoEl.readyState >= 2 && aiSegmentationRef.current && backgroundRemovalEnabled && backgroundRemovalMode === 'ai') {
          // Set canvas size
          if (canvas.width !== videoEl.videoWidth || canvas.height !== videoEl.videoHeight) {
            canvas.width = videoEl.videoWidth || 640;
            canvas.height = videoEl.videoHeight || 480;
          }
          
          try {
            await aiSegmentationRef.current.send({ image: videoEl });
          } catch (err) {
            console.error('AI frame processing error:', err);
          }
        }
        aiAnimationRef.current = requestAnimationFrame(processFrame);
      };
      
      processFrame();
      toast.success('AI Background Removal active!');
      
    } catch (error) {
      console.error('AI REMOVAL: Error initializing:', error);
      toast.error('AI Background Removal failed to initialize. Try Green/Blue Screen instead.');
    }
  }, [selectedVideoSources, backgroundRemovalEnabled, backgroundRemovalMode]);
  
  // Stop AI removal
  const stopAIRemoval = useCallback(() => {
    if (aiAnimationRef.current) {
      cancelAnimationFrame(aiAnimationRef.current);
      aiAnimationRef.current = null;
    }
    if (aiSegmentationRef.current) {
      aiSegmentationRef.current.close();
      aiSegmentationRef.current = null;
    }
  }, []);
  
  // Effect to start/stop AI removal based on mode
  useEffect(() => {
    if (backgroundRemovalEnabled && backgroundRemovalMode === 'ai' && selectedVideoSources.length > 0) {
      startAIRemoval();
    } else {
      stopAIRemoval();
    }
    
    return () => stopAIRemoval();
  }, [backgroundRemovalEnabled, backgroundRemovalMode, selectedVideoSources, startAIRemoval, stopAIRemoval]);
  
  // Get icon for source type
  const getSourceTypeIcon = (type) => {
    switch (type) {
      case 'usb': return '🔌';
      case 'hdmi': return '📺';
      case 'wifi': return '📶';
      case 'bluetooth': return '📡';
      case 'virtual': return '🎬';
      default: return '📷';
    }
  };
  
  // Get label for source type (video)
  const getSourceTypeLabel = (type) => {
    switch (type) {
      case 'usb': return 'USB Camera';
      case 'hdmi': return 'HDMI Input';
      case 'wifi': return 'Wi-Fi Camera';
      case 'bluetooth': return 'Bluetooth Camera';
      case 'virtual': return 'Virtual Camera';
      default: return 'Camera';
    }
  };
  
  // Get icon for AUDIO input type
  const getAudioInputIcon = (type) => {
    switch (type) {
      case 'interface': return '🎛️';
      case 'usb': return '🎤';
      case 'bluetooth': return '🎧';
      case 'built-in': return '🎙️';
      default: return '🎤';
    }
  };
  
  // Get label for AUDIO input type
  const getAudioInputLabel = (type) => {
    switch (type) {
      case 'interface': return 'Audio Interface';
      case 'usb': return 'USB Microphone';
      case 'bluetooth': return 'Bluetooth Audio';
      case 'built-in': return 'Built-in Mic';
      default: return 'Microphone';
    }
  };
  
  // Categorize a device by its label to determine connection type
  const categorizeDevice = (label) => {
    const lowerLabel = label.toLowerCase();
    
    // Virtual/OBS cameras
    if (lowerLabel.includes('obs') || lowerLabel.includes('virtual') || 
        lowerLabel.includes('snap camera') || lowerLabel.includes('manycam') ||
        lowerLabel.includes('xsplit') || lowerLabel.includes('streamlabs')) {
      return 'virtual';
    }
    
    // HDMI capture devices
    if (lowerLabel.includes('hdmi') || lowerLabel.includes('capture') || 
        lowerLabel.includes('elgato') || lowerLabel.includes('blackmagic') ||
        lowerLabel.includes('avermedia') || lowerLabel.includes('magewell')) {
      return 'hdmi';
    }
    
    // Wi-Fi/Wireless cameras (includes iPhone Continuity Camera, DroidCam, etc.)
    if (lowerLabel.includes('iphone') || lowerLabel.includes('ipad') ||
        lowerLabel.includes('droidcam') || lowerLabel.includes('iriun') ||
        lowerLabel.includes('epoccam') || lowerLabel.includes('camo') ||
        lowerLabel.includes('continuity') || lowerLabel.includes('wireless') ||
        lowerLabel.includes('wifi') || lowerLabel.includes('wi-fi')) {
      return 'wifi';
    }
    
    // Bluetooth
    if (lowerLabel.includes('bluetooth') || lowerLabel.includes('bt ')) {
      return 'bluetooth';
    }
    
    // Default to USB (most webcams are USB)
    return 'usb';
  };
  
  // Generate a generic name for display (hide personal names)
  const getGenericDeviceName = (label, type, index) => {
    const typeLabels = {
      'usb': 'USB Camera',
      'hdmi': 'HDMI Input',
      'wifi': 'Wi-Fi Camera',
      'bluetooth': 'Bluetooth Camera',
      'virtual': 'Virtual Camera'
    };
    
    // Special case for built-in cameras
    if (label.toLowerCase().includes('facetime') || label.toLowerCase().includes('built-in') ||
        label.toLowerCase().includes('integrated')) {
      return 'Built-in HD Camera';
    }
    
    return `${typeLabels[type] || 'Camera'} ${index + 1}`;
  };
  
  // Filter video sources by input type
  const getFilteredVideoSources = () => {
    if (inputTypeTab === 'all') return videoSources;
    return videoSources.filter(source => source.connectionType === inputTypeTab);
  };
  
  // Get icon for audio output type
  const getOutputTypeIcon = (type) => {
    switch (type) {
      case 'headphones': return '🎧';
      case 'bluetooth': return '📶';
      case 'usb': return '🔌';
      case 'hdmi': return '🖥️';
      case 'interface': return '🎛️';
      case 'built-in': return '🔊';
      default: return '🔊';
    }
  };
  
  // Get label for audio output type
  const getOutputTypeLabel = (type) => {
    switch (type) {
      case 'headphones': return 'Headphones';
      case 'bluetooth': return 'Bluetooth';
      case 'usb': return 'USB Audio';
      case 'hdmi': return 'HDMI/Display';
      case 'interface': return 'Audio Interface';
      case 'built-in': return 'Built-in Speaker';
      default: return 'Speaker';
    }
  };
  
  // Apply audio output to an element using setSinkId
  const setAudioOutput = async (element, deviceId) => {
    if (!element) return;
    
    // Check if setSinkId is supported (Chrome, Edge, Firefox)
    if (typeof element.setSinkId === 'function') {
      try {
        await element.setSinkId(deviceId === 'default' ? '' : deviceId);
        console.log(`Audio output set to: ${deviceId}`);
      } catch (err) {
        console.error('Error setting audio output:', err);
        // Safari doesn't support setSinkId - fall back to default
        if (err.name === 'NotSupportedError') {
          toast.error('Your browser doesn\'t support audio output selection');
        }
      }
    }
  };
  
  // Apply selected output to all track playback elements
  const applyTrackOutput = useCallback(async () => {
    const elements = Object.values(playbackVideoRefs.current);
    for (const el of elements) {
      if (el) {
        await setAudioOutput(el, selectedTrackOutput);
      }
    }
  }, [selectedTrackOutput]);
  
  // Effect to apply track output when selection changes
  useEffect(() => {
    applyTrackOutput();
  }, [selectedTrackOutput, applyTrackOutput]);
  
  // Effect to apply metronome output when selection changes
  useEffect(() => {
    if (metronomeAudioRef.current) {
      setAudioOutput(metronomeAudioRef.current, selectedMetronomeOutput);
    }
  }, [selectedMetronomeOutput]);
  
  // Update input gain in real-time
  useEffect(() => {
    if (inputGainNodeRef.current) {
      inputGainNodeRef.current.gain.setTargetAtTime(
        inputGain / 100, 
        inputAudioContextRef.current?.currentTime || 0, 
        0.02
      );
    }
  }, [inputGain]);
  
  // Input level metering - shows POST-gain level with input gain applied
  useEffect(() => {
    let animationId = null;
    let audioCtx = null;
    let analyser = null;
    let source = null;
    let gainNode = null;
    
    const startMonitoring = async () => {
      const streams = Object.values(activeStreams);
      if (streams.length === 0) {
        setInputLevel(0);
        return;
      }
      
      const stream = streams[0];
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        setInputLevel(0);
        return;
      }
      
      try {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        source = audioCtx.createMediaStreamSource(stream);
        
        // Create gain node to mirror the inputGain setting
        gainNode = audioCtx.createGain();
        gainNode.gain.value = inputGain / 100;
        
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        
        // Connect: source -> gain -> analyser (shows post-gain level)
        source.connect(gainNode);
        gainNode.connect(analyser);
        
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        
        const updateLevel = () => {
          // Update gain value in real-time to reflect slider changes
          if (gainNode) {
            gainNode.gain.value = inputGain / 100;
          }
          
          analyser.getByteFrequencyData(dataArray);
          
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i] * dataArray[i];
          }
          const rms = Math.sqrt(sum / dataArray.length);
          const level = Math.min(100, (rms / 255) * 100 * 2); // Scale to 0-100
          
          setInputLevel(level);
          setIsInputClipping(level > 90);
          
          animationId = requestAnimationFrame(updateLevel);
        };
        
        updateLevel();
      } catch (err) {
        console.error('Failed to start input monitoring:', err);
      }
    };
    
    startMonitoring();
    
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      if (gainNode) try { gainNode.disconnect(); } catch(e) {}
      if (source) try { source.disconnect(); } catch(e) {}
      if (audioCtx) try { audioCtx.close(); } catch(e) {}
    };
  }, [activeStreams, inputGain]);

  // Start recording
  const startRecording = async () => {
    const activeSourceIds = Object.keys(activeStreams);
    if (activeSourceIds.length === 0) {
      toast.error('No video source selected');
      return;
    }
    
    // Count-in if enabled
    if (countIn && metronomeOn) {
      toast.info(`Count-in: ${countInBeats} beats...`);
      setMetronomeOn(true);
      
      await new Promise(resolve => {
        const secondsPerBeat = 60 / bpm;
        setTimeout(resolve, countInBeats * secondsPerBeat * 1000);
      });
    }
    
    // Start all existing tracks playing WITH AUDIO for overdubbing
    if (tracks.length > 0) {
      setIsPlaying(true);
      
      // Apply volume settings and start playback for each track
      for (const track of tracks) {
        const video = playbackVideoRefs.current[track.id];
        if (video) {
          video.currentTime = 0;
          video.muted = track.muted;
          video.volume = (track.volume / 100) * (masterVolume / 100);
          
          // Ensure audio context is ready (critical for iOS)
          if (audioContextRef.current?.state === 'suspended') {
            await audioContextRef.current.resume();
          }
          
          // Ensure video is connected to audio routing
          if (!audioSourcesRef.current[track.id]) {
            connectVideoToAudio(track.id, video);
          }
          
          // Wait for video to be ready if needed
          if (video.readyState < 2) {
            await new Promise((resolve) => {
              const onReady = () => {
                video.removeEventListener('loadeddata', onReady);
                resolve();
              };
              video.addEventListener('loadeddata', onReady);
              video.load();
              // Timeout fallback
              setTimeout(resolve, 1000);
            });
          }
          
          try {
            await video.play();
          } catch (err) {
            console.log('Overdub playback error:', err);
          }
        }
        // Also sync composite preview videos (visual only)
        const compositeVideo = compositeVideoRefs.current[track.id];
        if (compositeVideo) {
          compositeVideo.currentTime = 0;
          compositeVideo.muted = true;
          compositeVideo.play().catch(err => console.log('Composite video error:', err));
        }
      }
    }
    
    // Get the first active stream to record (primary camera)
    const primarySourceId = activeSourceIds[0];
    const primaryStream = activeStreams[primarySourceId];
    
    // Determine if we should record from chroma key canvas
    const useChromaCanvas = backgroundRemovalEnabled && 
                            backgroundRemovalMode === 'greenscreen' && 
                            chromaCanvasRef.current;
    
    // Apply input gain to audio track
    let processedAudioTrack = null;
    const audioTracks = primaryStream.getAudioTracks();
    
    if (audioTracks.length > 0) {
      try {
        // Create audio context for input processing
        if (!inputAudioContextRef.current) {
          inputAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const ctx = inputAudioContextRef.current;
        
        // Create source from stream
        const source = ctx.createMediaStreamSource(primaryStream);
        
        // Create gain node for input level control
        inputGainNodeRef.current = ctx.createGain();
        inputGainNodeRef.current.gain.value = inputGain / 100;
        
        // Create analyser for level metering
        inputAnalyserRef.current = ctx.createAnalyser();
        inputAnalyserRef.current.fftSize = 256;
        
        // Create destination to capture processed audio
        const destination = ctx.createMediaStreamDestination();
        
        // Connect: source -> gain -> analyser -> destination
        source.connect(inputGainNodeRef.current);
        inputGainNodeRef.current.connect(inputAnalyserRef.current);
        inputAnalyserRef.current.connect(destination);
        
        processedAudioTrack = destination.stream.getAudioTracks()[0];
        console.log('Input gain applied:', inputGain + '%');
      } catch (err) {
        console.error('Failed to apply input gain:', err);
        processedAudioTrack = audioTracks[0];
      }
    }
    
    // Create the final stream for recording
    let recordingStream;
    
    if (useChromaCanvas) {
      // Record from the chroma key processed canvas with background removed
      console.log('Recording with CHROMA KEY - using canvas stream');
      const canvasStream = chromaCanvasRef.current.captureStream(30); // 30 FPS
      
      // Combine canvas video with processed audio
      const canvasVideoTrack = canvasStream.getVideoTracks()[0];
      if (processedAudioTrack) {
        recordingStream = new MediaStream([canvasVideoTrack, processedAudioTrack]);
      } else if (audioTracks.length > 0) {
        recordingStream = new MediaStream([canvasVideoTrack, audioTracks[0]]);
      } else {
        recordingStream = canvasStream;
      }
      
      toast.success('Recording with green screen effect applied!');
    } else {
      // Record from raw camera stream with gain-adjusted audio
      console.log('Recording RAW stream (no chroma key)');
      const videoTrack = primaryStream.getVideoTracks()[0];
      if (processedAudioTrack) {
        recordingStream = new MediaStream([videoTrack, processedAudioTrack]);
      } else {
        recordingStream = primaryStream;
      }
    }
    
    // Start recording
    recordedChunksRef.current = [];
    
    const mediaRecorder = new MediaRecorder(recordingStream, {
      mimeType: 'video/webm;codecs=vp9,opus'
    });
    
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        recordedChunksRef.current.push(e.data);
      }
    };
    
    mediaRecorder.onstop = () => {
      try {
        console.log('onstop called, chunks:', recordedChunksRef.current.length);
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        console.log('Blob created, size:', blob.size);
        
        // Create blob URL ONCE and store it with the track
        const blobUrl = URL.createObjectURL(blob);
        
        // Get source name
        const source = videoSources.find(s => s.id === primarySourceId);
        const sourceName = source?.displayName || source?.label || (primarySourceId === 'screen' ? 'Screen' : 'Camera');
        
        // Create new track with layout positioning
        const trackCount = tracks.length;
        const gridCols = selectedLayout.grid?.[0] || 2;
        const gridRows = selectedLayout.grid?.[1] || 2;
        const cellWidth = 1920 / gridCols;
        const cellHeight = 1080 / gridRows;
        const col = trackCount % gridCols;
        const row = Math.floor(trackCount / gridCols) % gridRows;
        
        const newTrack = {
          id: `track-${Date.now()}`,
          name: `${sourceName} - Take ${trackCount + 1}`,
          blob,
          blobUrl,
          duration: currentTime || 5,
          volume: 100,
          muted: false,
          solo: false,
          visible: true,
          locked: false,
          backgroundRemoved: backgroundRemovalEnabled,
          backgroundMode: backgroundRemovalMode,
          zIndex: trackCount + 1,
          // EQ settings (in dB, -12 to +12)
          eq: {
            low: 0,    // 100Hz
            mid: 0,    // 1kHz  
            high: 0    // 10kHz
          },
          position: selectedLayout.grid === 'free' 
            ? { x: 100 + (trackCount * 50), y: 100 + (trackCount * 50) }
            : { x: col * cellWidth, y: row * cellHeight },
          size: selectedLayout.grid === 'free'
            ? { width: 400, height: 300 }
            : { width: cellWidth, height: cellHeight }
        };
        
        console.log('Creating track:', newTrack.name, 'id:', newTrack.id);
        setTracks(prev => {
          console.log('Previous tracks:', prev.length, 'Adding new track');
          return [...prev, newTrack];
        });
        setSelectedTrackId(newTrack.id);
        
        // Generate waveform for the new track
        generateWaveform(newTrack.id, blob);
        
        toast.success('Recording saved!');
      } catch (err) {
        console.error('Error in onstop:', err);
        toast.error('Error saving recording: ' + err.message);
      }
    };
    
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(100);
    setIsRecording(true);
    toast.info('Recording started!');
    console.log('Recording started with source:', primarySourceId);
    
    // Start timer
    const startTime = Date.now();
    const timer = setInterval(() => {
      setCurrentTime((Date.now() - startTime) / 1000);
    }, 100);
    
    mediaRecorderRef.current.timer = timer;
  };
  
  // Stop recording
  const stopRecording = () => {
    console.log('stopRecording called, isRecording:', isRecording, 'mediaRecorder:', !!mediaRecorderRef.current);
    if (mediaRecorderRef.current && isRecording) {
      clearInterval(mediaRecorderRef.current.timer);
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPlaying(false);
      console.log('Recording stopped');
      
      // Stop all playback
      Object.values(playbackVideoRefs.current).forEach(video => {
        if (video) video.pause();
      });
      Object.values(compositeVideoRefs.current).forEach(video => {
        if (video) video.pause();
      });
      
      // Update duration if this is longer than current
      if (currentTime > duration) {
        setDuration(currentTime);
      }
    }
  };
  
  // Playback timer ref
  const playbackTimerRef = useRef(null);
  
  // Play all tracks
  const playAll = async () => {
    if (tracks.length === 0) {
      toast.error('No tracks to play');
      return;
    }
    
    // Clear any existing timer
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    
    // CRITICAL: Resume audio context (required for Web Audio API after user interaction)
    await resumeAudioContext();
    console.log('Audio context state after resume:', audioContextRef.current?.state);
    
    // Ensure all tracks are connected to the audio graph
    tracks.forEach(track => {
      const videoEl = playbackVideoRefs.current[track.id];
      if (videoEl && !audioSourcesRef.current[track.id]) {
        connectVideoToAudio(track.id, videoEl);
      }
    });
    
    // Give audio connections a moment to establish
    await new Promise(resolve => setTimeout(resolve, 50));
    
    setIsPlaying(true);
    setCurrentTime(0);
    
    // Get ONLY the composite preview videos (by track.id, NOT monitor- prefixed)
    const compositeVideos = tracks
      .map(t => playbackVideoRefs.current[t.id])
      .filter(v => v && v.src);
    
    // Play composite videos (these produce audio via Web Audio API)
    for (const video of compositeVideos) {
      video.currentTime = 0;
      // CRITICAL: video.muted MUST be false for Web Audio API to receive audio signal
      video.muted = false;
      try {
        await video.play();
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Play error:', err);
        }
      }
    }
    
    // Also sync the composite preview videos (but keep them MUTED - audio comes from hidden videos)
    Object.values(compositeVideoRefs.current).forEach(async (video) => {
      if (video && video.src) {
        video.currentTime = 0;
        video.muted = true;
        try {
          await video.play();
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('Composite play error:', err);
          }
        }
      }
    });
    
    // Start timer
    const startTime = Date.now();
    playbackTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000;
      setCurrentTime(elapsed);
      
      if (elapsed >= duration) {
        clearInterval(playbackTimerRef.current);
        playbackTimerRef.current = null;
        setIsPlaying(false);
        // Pause all videos at the end
        compositeVideos.forEach(video => {
          if (video) video.pause();
        });
        Object.values(compositeVideoRefs.current).forEach(video => {
          if (video) video.pause();
        });
      }
    }, 100);
  };
  
  // Stop playback
  const stopPlayback = () => {
    // Clear the timer first
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    
    setIsPlaying(false);
    
    // Pause all audio playback videos
    Object.values(playbackVideoRefs.current).forEach(video => {
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
    
    // Also pause the composite preview videos
    Object.values(compositeVideoRefs.current).forEach(video => {
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
    
    // Also reset the current time
    setCurrentTime(0);
  };
  
  // Helper: Seek all videos to a specific time
  const seekAllVideos = (time) => {
    setCurrentTime(time);
    Object.values(playbackVideoRefs.current).forEach(v => { 
      if (v) v.currentTime = time; 
    });
    Object.values(compositeVideoRefs.current).forEach(v => { 
      if (v) v.currentTime = time; 
    });
  };
  
  // Helper: Pause all videos
  const pauseAllVideos = () => {
    if (isPlaying) {
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
        playbackTimerRef.current = null;
      }
      setIsPlaying(false);
      Object.values(playbackVideoRefs.current).forEach(v => { 
        if (v) v.pause(); 
      });
      Object.values(compositeVideoRefs.current).forEach(v => { 
        if (v) v.pause(); 
      });
    }
  };
  
  // Initialize Audio Context and Master Gain (sync version for connection)
  const initAudioContextSync = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      console.log('Audio context created, state:', audioContextRef.current.state);
    }
    
    // ALWAYS ensure master gain exists
    if (!masterGainRef.current) {
      masterGainRef.current = audioContextRef.current.createGain();
      masterGainRef.current.connect(audioContextRef.current.destination);
      masterGainRef.current.gain.value = masterVolume / 100;
      console.log('Master gain node created, value:', masterVolume / 100);
    }
    
    return audioContextRef.current;
  }, [masterVolume]);
  
  // Resume AudioContext (must be called after user interaction)
  const resumeAudioContext = useCallback(async () => {
    if (!audioContextRef.current) {
      initAudioContextSync();
    }
    
    // CRITICAL: Resume AudioContext if suspended (browser security requirement)
    if (audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
        console.log('Audio context resumed, state:', audioContextRef.current.state);
      } catch (err) {
        console.error('Failed to resume audio context:', err);
      }
    }
    
    return audioContextRef.current;
  }, [initAudioContextSync]);
  
  // Connect a video element to the audio routing with EQ
  const connectVideoToAudio = useCallback((trackId, videoElement) => {
    if (!videoElement) {
      console.warn(`connectVideoToAudio: No video element for track ${trackId}`);
      return;
    }
    if (audioSourcesRef.current[trackId]) {
      console.log(`connectVideoToAudio: Track ${trackId} already connected`);
      return;
    }
    
    const ctx = initAudioContextSync();
    
    if (!masterGainRef.current) {
      console.error('connectVideoToAudio: masterGainRef is null!');
      return;
    }
    
    try {
      // Create source from video element
      const source = ctx.createMediaElementSource(videoElement);
      audioSourcesRef.current[trackId] = source;
      
      // Create gain node for this track
      const gainNode = ctx.createGain();
      gainNodesRef.current[trackId] = gainNode;
      
      // Create 3-band EQ using BiquadFilterNode
      const lowEQ = ctx.createBiquadFilter();
      lowEQ.type = 'lowshelf';
      lowEQ.frequency.value = 100;
      lowEQ.gain.value = 0;
      
      const midEQ = ctx.createBiquadFilter();
      midEQ.type = 'peaking';
      midEQ.frequency.value = 1000;
      midEQ.Q.value = 1;
      midEQ.gain.value = 0;
      
      const highEQ = ctx.createBiquadFilter();
      highEQ.type = 'highshelf';
      highEQ.frequency.value = 10000;
      highEQ.gain.value = 0;
      
      eqNodesRef.current[trackId] = { low: lowEQ, mid: midEQ, high: highEQ };
      
      // Create analyser for level metering
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyserNodesRef.current[trackId] = analyser;
      
      // Connect: source -> gain -> lowEQ -> midEQ -> highEQ -> analyser -> master gain
      source.connect(gainNode);
      gainNode.connect(lowEQ);
      lowEQ.connect(midEQ);
      midEQ.connect(highEQ);
      highEQ.connect(analyser);
      analyser.connect(masterGainRef.current);
      
      // Set initial volume and EQ from track settings
      const track = tracks.find(t => t.id === trackId);
      if (track) {
        gainNode.gain.value = track.muted ? 0 : (track.volume / 100);
        if (track.eq) {
          lowEQ.gain.value = track.eq.low || 0;
          midEQ.gain.value = track.eq.mid || 0;
          highEQ.gain.value = track.eq.high || 0;
        }
        console.log(`Track ${trackId} initial gain: ${gainNode.gain.value}, EQ: L${track.eq?.low || 0} M${track.eq?.mid || 0} H${track.eq?.high || 0}`);
      }
      
      console.log(`✓ Connected track ${trackId} to audio routing with EQ (ctx state: ${ctx.state})`);
    } catch (err) {
      // Element might already be connected
      console.error(`Track ${trackId} audio connection error:`, err.message);
    }
  }, [initAudioContextSync, tracks]);
  
  // Update track volume using HTML5 video element controls
  const updateTrackVolume = useCallback((trackId, volume, muted) => {
    const videoEl = playbackVideoRefs.current[trackId];
    if (videoEl) {
      const targetValue = muted ? 0 : (volume / 100);
      videoEl.volume = targetValue;
      videoEl.muted = muted;
      console.log(`Track ${trackId}: volume=${targetValue}, muted=${muted}`);
    } else {
      console.warn(`Track ${trackId}: video element not found`);
    }
  }, []);
  
  // Update master volume - applies to all video elements
  useEffect(() => {
    const masterValue = masterVolume / 100;
    console.log(`Master volume changed to ${masterVolume}%`);
    
    // Apply master volume as multiplier to all tracks
    tracks.forEach(track => {
      const videoEl = playbackVideoRefs.current[track.id];
      if (videoEl) {
        const hasSolo = tracks.some(t => t.solo);
        const shouldMute = track.muted || (hasSolo && !track.solo);
        const effectiveVolume = shouldMute ? 0 : ((track.volume / 100) * masterValue);
        videoEl.volume = Math.min(1, effectiveVolume); // Clamp to 0-1
        videoEl.muted = shouldMute;
      }
    });
  }, [masterVolume, tracks]);
  
  // Update track audio when volume/mute changes
  useEffect(() => {
    tracks.forEach(track => {
      // Check if any track is soloed
      const hasSolo = tracks.some(t => t.solo);
      const shouldMute = track.muted || (hasSolo && !track.solo);
      updateTrackVolume(track.id, track.volume, shouldMute);
    });
  }, [tracks, updateTrackVolume]);
  
  // NOTE: Web Audio API connection disabled - using HTML5 volume controls instead
  // This provides more reliable volume/mute functionality
  // useEffect(() => {
  //   if (tracks.length > 0) {
  //     const timer = setTimeout(() => {
  //       tracks.forEach(track => {
  //         const videoEl = playbackVideoRefs.current[track.id];
  //         if (videoEl && !audioSourcesRef.current[track.id]) {
  //           connectVideoToAudio(track.id, videoEl);
  //         }
  //       });
  //     }, 100);
  //     return () => clearTimeout(timer);
  //   }
  // }, [tracks, connectVideoToAudio]);
  
  // Track controls - now with audio routing
  const handleVolumeChange = (trackId, volume) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, volume } : t
    ));
    // Immediate update for smoother control
    const track = tracks.find(t => t.id === trackId);
    if (track) {
      const hasSolo = tracks.some(t => t.solo);
      const shouldMute = track.muted || (hasSolo && !track.solo);
      updateTrackVolume(trackId, volume, shouldMute);
    }
  };

  const handleMute = (trackId) => {
    setTracks(prev => {
      const newTracks = prev.map(t => 
        t.id === trackId ? { ...t, muted: !t.muted } : t
      );
      // Update audio immediately
      const track = newTracks.find(t => t.id === trackId);
      if (track) {
        const hasSolo = newTracks.some(t => t.solo);
        const shouldMute = track.muted || (hasSolo && !track.solo);
        updateTrackVolume(trackId, track.volume, shouldMute);
      }
      return newTracks;
    });
  };

  const handleSolo = (trackId) => {
    setTracks(prev => {
      const newTracks = prev.map(t => 
        t.id === trackId ? { ...t, solo: !t.solo } : t
      );
      // Update all track volumes based on solo state
      const hasSolo = newTracks.some(t => t.solo);
      newTracks.forEach(track => {
        const shouldMute = track.muted || (hasSolo && !track.solo);
        updateTrackVolume(track.id, track.volume, shouldMute);
      });
      return newTracks;
    });
  };
  
  const handleDeleteTrack = (trackId) => {
    if (confirm('Delete this track?')) {
      // Find the track to revoke its blob URL
      const trackToDelete = tracks.find(t => t.id === trackId);
      if (trackToDelete?.blobUrl) {
        URL.revokeObjectURL(trackToDelete.blobUrl);
      }
      
      // Clean up audio connections
      if (audioSourcesRef.current[trackId]) {
        try {
          audioSourcesRef.current[trackId].disconnect();
        } catch (e) {}
        delete audioSourcesRef.current[trackId];
      }
      if (gainNodesRef.current[trackId]) {
        try {
          gainNodesRef.current[trackId].disconnect();
        } catch (e) {}
        delete gainNodesRef.current[trackId];
      }
      if (analyserNodesRef.current[trackId]) {
        try {
          analyserNodesRef.current[trackId].disconnect();
        } catch (e) {}
        delete analyserNodesRef.current[trackId];
      }
      
      setTracks(prev => prev.filter(t => t.id !== trackId));
      if (selectedTrackId === trackId) {
        setSelectedTrackId(null);
      }
      toast.success('Track deleted');
    }
  };
  
  // Update track properties (for layout editor)
  const handleUpdateTrack = (trackId, updates) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, ...updates } : t
    ));
  };
  
  // Toggle track visibility
  const handleToggleVisibility = (trackId) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, visible: !t.visible } : t
    ));
  };
  
  // Toggle track lock
  const handleToggleLock = (trackId) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, locked: !t.locked } : t
    ));
  };
  
  // Rename track
  const handleRenameTrack = (trackId, newName) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, name: newName } : t
    ));
  };
  
  // Update track EQ
  const handleEQChange = (trackId, band, value) => {
    // Update track state
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { 
        ...t, 
        eq: { ...t.eq, [band]: value } 
      } : t
    ));
    
    // Update the actual EQ node in real-time
    const eqNodes = eqNodesRef.current[trackId];
    if (eqNodes) {
      if (band === 'low' && eqNodes.low) {
        eqNodes.low.gain.value = value;
      } else if (band === 'mid' && eqNodes.mid) {
        eqNodes.mid.gain.value = value;
      } else if (band === 'high' && eqNodes.high) {
        eqNodes.high.gain.value = value;
      }
    }
  };
  
  // Keyboard Shortcuts Effect - MUST be after all handler function definitions
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if user is typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      switch (e.key.toLowerCase()) {
        case ' ': // Spacebar - Play/Pause/Stop Recording
          e.preventDefault();
          if (isRecording) {
            stopRecording();
          } else if (isPlaying) {
            pauseAllVideos();
          } else if (tracks.length > 0) {
            playAll();
          }
          break;
        case 'r': // R - Start/Stop Recording
          e.preventDefault();
          if (isRecording) {
            stopRecording();
          } else if (Object.keys(activeStreams).length > 0) {
            startRecording();
          }
          break;
        case 's': // S - Stop playback
          e.preventDefault();
          stopPlayback();
          break;
        case 'arrowleft': // Left Arrow - Rewind 5s
          e.preventDefault();
          seekAllVideos(Math.max(0, currentTime - 5));
          break;
        case 'arrowright': // Right Arrow - Forward 5s
          e.preventDefault();
          seekAllVideos(Math.min(duration, currentTime + 5));
          break;
        case 'home': // Home - Go to start
          e.preventDefault();
          seekAllVideos(0);
          break;
        case 'end': // End - Go to end
          e.preventDefault();
          seekAllVideos(duration);
          break;
        case 'm': // M - Toggle mute on selected track
          e.preventDefault();
          if (selectedTrackId) {
            handleMute(selectedTrackId);
          }
          break;
        case 'o': // O - Toggle solo on selected track
          e.preventDefault();
          if (selectedTrackId) {
            handleSolo(selectedTrackId);
          }
          break;
        case 'delete': // Delete - Delete selected track
        case 'backspace':
          e.preventDefault();
          if (selectedTrackId && !isRecording) {
            handleDeleteTrack(selectedTrackId);
          }
          break;
        case '?': // ? - Show shortcuts
          e.preventDefault();
          setShowShortcuts(prev => !prev);
          break;
        case 'escape': // Escape - Close modals
          setShowShortcuts(false);
          setScreenShareExpanded(false);
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying, isRecording, tracks, activeStreams, selectedTrackId, currentTime, duration]);
  
  // Real-time audio level metering for tracks
  useEffect(() => {
    if (!isPlaying || tracks.length === 0) {
      setTrackLevels({});
      return;
    }
    
    let animationId = null;
    const audioContexts = {};
    const analysers = {};
    const sources = {};
    
    const setupMetering = () => {
      tracks.forEach(track => {
        const videoEl = playbackVideoRefs.current[track.id];
        if (!videoEl) return;
        
        try {
          // Create a separate audio context for metering (non-destructive)
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          audioContexts[track.id] = ctx;
          
          // We can't use createMediaElementSource here as it would conflict
          // Instead, we'll simulate levels based on volume settings and playing state
        } catch (err) {
          console.log('Metering setup error:', err);
        }
      });
    };
    
    const updateLevels = () => {
      const newLevels = {};
      
      tracks.forEach(track => {
        const videoEl = playbackVideoRefs.current[track.id];
        if (!videoEl) {
          newLevels[track.id] = 0;
          return;
        }
        
        // Simulate audio level based on volume and playing state
        const hasSolo = tracks.some(t => t.solo);
        const shouldMute = track.muted || (hasSolo && !track.solo);
        
        if (shouldMute || videoEl.paused) {
          newLevels[track.id] = 0;
        } else {
          // Simulate varying levels with some randomness for visual effect
          const baseLevel = (track.volume / 100) * (masterVolume / 100);
          const variation = Math.random() * 0.3 - 0.15; // ±15% variation
          newLevels[track.id] = Math.max(0, Math.min(100, baseLevel * 100 * (1 + variation)));
        }
      });
      
      setTrackLevels(newLevels);
      animationId = requestAnimationFrame(updateLevels);
    };
    
    setupMetering();
    updateLevels();
    
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      Object.values(audioContexts).forEach(ctx => {
        try { ctx.close(); } catch(e) {}
      });
    };
  }, [isPlaying, tracks, masterVolume]);
  
  // Generate waveform data from a video/audio blob
  const generateWaveform = async (trackId, blob) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const arrayBuffer = await blob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Get audio data from first channel
      const channelData = audioBuffer.getChannelData(0);
      const samples = 100; // Number of bars in waveform
      const blockSize = Math.floor(channelData.length / samples);
      const waveformData = [];
      
      for (let i = 0; i < samples; i++) {
        const start = i * blockSize;
        let sum = 0;
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(channelData[start + j] || 0);
        }
        const avg = sum / blockSize;
        waveformData.push(Math.min(1, avg * 3)); // Normalize and amplify
      }
      
      setTrackWaveforms(prev => ({
        ...prev,
        [trackId]: waveformData
      }));
      
      audioContext.close();
    } catch (err) {
      console.log('Waveform generation error (video may not have audio):', err);
      // Generate fake waveform for visual consistency
      const fakeWaveform = Array(100).fill(0).map(() => Math.random() * 0.5 + 0.1);
      setTrackWaveforms(prev => ({
        ...prev,
        [trackId]: fakeWaveform
      }));
    }
  };
  
  // Track reordering via drag and drop
  const handleTrackDragStart = (e, trackId) => {
    setDraggingTrackId(trackId);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleTrackDragOver = (e, targetTrackId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleTrackDrop = (e, targetTrackId) => {
    e.preventDefault();
    if (!draggingTrackId || draggingTrackId === targetTrackId) {
      setDraggingTrackId(null);
      return;
    }
    
    setTracks(prev => {
      const dragIndex = prev.findIndex(t => t.id === draggingTrackId);
      const dropIndex = prev.findIndex(t => t.id === targetTrackId);
      
      if (dragIndex === -1 || dropIndex === -1) return prev;
      
      const newTracks = [...prev];
      const [draggedTrack] = newTracks.splice(dragIndex, 1);
      newTracks.splice(dropIndex, 0, draggedTrack);
      
      // Update zIndex based on new order
      return newTracks.map((t, i) => ({ ...t, zIndex: i + 1 }));
    });
    
    setDraggingTrackId(null);
    toast.success('Track order updated');
  };
  
  // Toggle expanded view for a track
  const handleExpandTrack = (trackId) => {
    setExpandedTrackId(prev => prev === trackId ? null : trackId);
  };
  
  // Move track layer up/down
  const handleMoveLayer = (trackId, direction) => {
    setTracks(prev => {
      const track = prev.find(t => t.id === trackId);
      if (!track) return prev;
      
      const currentZ = track.zIndex || 1;
      const newZ = direction === 'up' ? currentZ + 1 : Math.max(1, currentZ - 1);
      
      return prev.map(t => 
        t.id === trackId ? { ...t, zIndex: newZ } : t
      );
    });
  };
  
  // Apply layout preset to all tracks
  const applyLayoutPreset = (preset) => {
    setSelectedLayout(preset);
    
    if (preset.grid === 'free' || preset.grid === 'pip') {
      // Don't reposition for free/pip layouts
      return;
    }
    
    const [cols, rows] = preset.grid;
    const cellWidth = 1920 / cols;
    const cellHeight = 1080 / rows;
    
    setTracks(prev => prev.map((track, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols) % rows;
      
      return {
        ...track,
        position: { x: col * cellWidth, y: row * cellHeight },
        size: { width: cellWidth, height: cellHeight }
      };
    }));
    
    toast.success(`Applied ${preset.name} layout`);
  };
  
  // Generate AI background
  const generateAIBackground = async (e) => {
    try {
      if (e) e.preventDefault();
    } catch (preventErr) {
      // Ignore
    }
    
    if (!aiBackgroundPrompt.trim()) {
      toast.error('Please enter a description');
      return;
    }
    
    setIsGeneratingBackground(true);
    
    // Use setTimeout to ensure state update happens first
    setTimeout(async () => {
      try {
        toast.info('Generating... Please wait');
        
        const apiUrl = `${API}/api/generate-image`;
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            prompt: `Studio background: ${aiBackgroundPrompt}`,
            size: '1024x1024'
          })
        });
        
        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success && data.image_url) {
          const fullImageUrl = data.image_url.startsWith('http') 
            ? data.image_url 
            : `${window.location.origin}${data.image_url}`;
          
          // Preload the image
          const img = new window.Image();
          
          img.onload = function() {
            loadedBgImageRef.current = img;
            const bgObj = { 
              id: 'ai-generated', 
              name: 'AI Generated', 
              type: 'image', 
              url: fullImageUrl 
            };
            setSelectedBackgroundState(bgObj);
            selectedBackgroundRef.current = bgObj;
            setIsGeneratingBackground(false);
            toast.success('Background ready!');
          };
          
          img.onerror = function() {
            const bgObj = { 
              id: 'ai-generated', 
              name: 'AI Generated', 
              type: 'image', 
              url: fullImageUrl 
            };
            setSelectedBackgroundState(bgObj);
            selectedBackgroundRef.current = bgObj;
            setIsGeneratingBackground(false);
            toast.success('Background generated!');
          };
          
          img.src = fullImageUrl;
        } else {
          throw new Error(data.detail || 'No image returned');
        }
      } catch (error) {
        setIsGeneratingBackground(false);
        toast.error('Generation failed. Try again.');
      }
    }, 100);
  };
  
  // ============ PHASE 3: CANVAS COMPOSITING ============
  
  // Get canvas dimensions based on quality
  const getCanvasDimensions = (quality) => {
    switch (quality) {
      case '480p': return { width: 854, height: 480 };
      case '720p': return { width: 1280, height: 720 };
      case '1080p': return { width: 1920, height: 1080 };
      default: return { width: 1280, height: 720 };
    }
  };
  
  // Initialize composite canvas
  const initCompositeCanvas = useCallback(() => {
    if (!compositeCanvasRef.current) return;
    
    const { width, height } = getCanvasDimensions(compositeQuality);
    compositeCanvasRef.current.width = width;
    compositeCanvasRef.current.height = height;
    compositeContextRef.current = compositeCanvasRef.current.getContext('2d');
    
    console.log(`Composite canvas initialized at ${width}x${height}`);
  }, [compositeQuality]);
  
  // Load background image
  const loadBackgroundImage = useCallback(async () => {
    if (selectedBackground.type === 'image' && selectedBackground.url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          backgroundImageRef.current = img;
          resolve(img);
        };
        img.onerror = reject;
        img.src = selectedBackground.url;
      });
    }
    backgroundImageRef.current = null;
    return null;
  }, [selectedBackground]);
  
  // Draw background to canvas
  const drawBackground = (ctx, width, height) => {
    ctx.save();
    
    if (selectedBackground.type === 'transparent') {
      // Draw checkerboard pattern for transparency
      const size = 20;
      for (let x = 0; x < width; x += size) {
        for (let y = 0; y < height; y += size) {
          ctx.fillStyle = ((x + y) / size) % 2 === 0 ? '#333' : '#222';
          ctx.fillRect(x, y, size, size);
        }
      }
    } else if (selectedBackground.type === 'color') {
      ctx.fillStyle = selectedBackground.color;
      ctx.fillRect(0, 0, width, height);
    } else if (selectedBackground.type === 'gradient') {
      // Parse gradient colors from CSS gradient string
      const gradientStr = selectedBackground.color;
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      
      // Extract colors from gradient string (simplified parsing)
      const colorMatches = gradientStr.match(/#[0-9a-fA-F]{6}/g);
      if (colorMatches && colorMatches.length >= 2) {
        gradient.addColorStop(0, colorMatches[0]);
        gradient.addColorStop(1, colorMatches[colorMatches.length - 1]);
        if (colorMatches.length > 2) {
          gradient.addColorStop(0.5, colorMatches[1]);
        }
      }
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    } else if (selectedBackground.type === 'image' && backgroundImageRef.current) {
      ctx.drawImage(backgroundImageRef.current, 0, 0, width, height);
    } else {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);
    }
    
    ctx.restore();
  };
  
  // Calculate track position and size in canvas based on layout
  const calculateTrackRect = (track, trackIndex, totalTracks, canvasWidth, canvasHeight) => {
    const layout = selectedLayout;
    
    if (layout.grid === 'free') {
      // Free positioning - use track's custom position/size
      const scaleX = canvasWidth / 1920;
      const scaleY = canvasHeight / 1080;
      return {
        x: (track.position?.x || 0) * scaleX,
        y: (track.position?.y || 0) * scaleY,
        width: (track.size?.width || 400) * scaleX,
        height: (track.size?.height || 300) * scaleY
      };
    } else if (layout.grid === 'pip') {
      // Picture in Picture - first track is full, rest are small in corner
      if (trackIndex === 0) {
        return { x: 0, y: 0, width: canvasWidth, height: canvasHeight };
      } else {
        const pipWidth = canvasWidth * 0.25;
        const pipHeight = canvasHeight * 0.25;
        const padding = 20;
        return {
          x: canvasWidth - pipWidth - padding - ((trackIndex - 1) * (pipWidth + 10)),
          y: canvasHeight - pipHeight - padding,
          width: pipWidth,
          height: pipHeight
        };
      }
    } else {
      // Grid layouts
      const [cols, rows] = layout.grid;
      const cellWidth = canvasWidth / cols;
      const cellHeight = canvasHeight / rows;
      const col = trackIndex % cols;
      const row = Math.floor(trackIndex / cols) % rows;
      
      return {
        x: col * cellWidth,
        y: row * cellHeight,
        width: cellWidth,
        height: cellHeight
      };
    }
  };
  
  // Composite all tracks onto canvas
  const compositeFrame = useCallback(() => {
    const ctx = compositeContextRef.current;
    const canvas = compositeCanvasRef.current;
    if (!ctx || !canvas) return;
    
    const { width, height } = canvas;
    
    // Clear and draw background
    ctx.clearRect(0, 0, width, height);
    drawBackground(ctx, width, height);
    
    // Get visible tracks sorted by z-index
    const visibleTracks = tracks
      .filter(t => t.visible !== false)
      .sort((a, b) => (a.zIndex || 1) - (b.zIndex || 1));
    
    // Draw each track
    visibleTracks.forEach((track, index) => {
      const videoEl = playbackVideoRefs.current[track.id];
      if (!videoEl || videoEl.readyState < 2) return;
      
      const rect = calculateTrackRect(track, index, visibleTracks.length, width, height);
      
      // If background removal is enabled for this track, use processed canvas
      if (track.backgroundRemoved && processedCanvasesRef.current[track.id]) {
        ctx.drawImage(processedCanvasesRef.current[track.id], rect.x, rect.y, rect.width, rect.height);
      } else {
        ctx.drawImage(videoEl, rect.x, rect.y, rect.width, rect.height);
      }
    });
    
    // Continue animation loop
    if (isCompositing || isPlaying || isRecording) {
      compositeAnimationRef.current = requestAnimationFrame(compositeFrame);
    }
  }, [tracks, selectedLayout, selectedBackground, isCompositing, isPlaying, isRecording]);
  
  // Start compositing
  const startCompositing = useCallback(async () => {
    initCompositeCanvas();
    await loadBackgroundImage();
    setIsCompositing(true);
    compositeFrame();
    toast.success('Real-time compositing started');
  }, [initCompositeCanvas, loadBackgroundImage, compositeFrame]);
  
  // Stop compositing
  const stopCompositing = useCallback(() => {
    if (compositeAnimationRef.current) {
      cancelAnimationFrame(compositeAnimationRef.current);
    }
    setIsCompositing(false);
  }, []);
  
  // Effect to update compositing when tracks/settings change
  useEffect(() => {
    if (isCompositing) {
      compositeFrame();
    }
  }, [isCompositing, tracks, selectedLayout, selectedBackground, compositeFrame]);
  
  // ============ PHASE 4: EXPORT ============
  
  // Export composite video
  const exportComposite = async () => {
    if (tracks.length === 0) {
      toast.error('No tracks to export. Record some tracks first!');
      return;
    }
    
    setIsExporting(true);
    setExportProgress(0);
    
    try {
      // Initialize canvas if not already
      initCompositeCanvas();
      await loadBackgroundImage();
      
      const canvas = compositeCanvasRef.current;
      if (!canvas) throw new Error('Canvas not initialized');
      
      // Create MediaRecorder from canvas stream
      const stream = canvas.captureStream(30); // 30 FPS
      
      // Add audio tracks from all unmuted video tracks
      const audioCtx = new AudioContext();
      const destination = audioCtx.createMediaStreamDestination();
      
      tracks.forEach(track => {
        const videoEl = playbackVideoRefs.current[track.id];
        if (videoEl && !track.muted) {
          try {
            const source = audioCtx.createMediaElementSource(videoEl);
            const gainNode = audioCtx.createGain();
            gainNode.gain.value = (track.volume || 100) / 100;
            source.connect(gainNode);
            gainNode.connect(destination);
            source.connect(audioCtx.destination); // Also play to speakers
          } catch (e) {
            // Already connected - ignore
          }
        }
      });
      
      // Add audio to stream
      destination.stream.getAudioTracks().forEach(track => {
        stream.addTrack(track);
      });
      
      // Setup MediaRecorder
      const mimeType = exportFormat === 'webm' ? 'video/webm;codecs=vp9,opus' : 'video/mp4';
      exportChunksRef.current = [];
      
      const recorder = new MediaRecorder(stream, { mimeType });
      exportRecorderRef.current = recorder;
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          exportChunksRef.current.push(e.data);
        }
      };
      
      recorder.onstop = () => {
        const blob = new Blob(exportChunksRef.current, { type: mimeType });
        
        // Create download link
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${projectName.replace(/[^a-z0-9]/gi, '_')}_composite.${exportFormat}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        setIsExporting(false);
        setExportProgress(100);
        toast.success('Export complete! Video downloaded.');
        
        audioCtx.close();
      };
      
      // Start recording
      recorder.start(100);
      
      // Reset all videos to start
      Object.values(playbackVideoRefs.current).forEach(video => {
        if (video) {
          video.currentTime = 0;
          video.play();
        }
      });
      
      // Start compositing animation
      const startTime = Date.now();
      const totalDuration = duration * 1000;
      
      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / totalDuration) * 100, 99);
        setExportProgress(progress);
        
        compositeFrame();
        
        if (elapsed < totalDuration) {
          requestAnimationFrame(updateProgress);
        } else {
          // Stop recording
          recorder.stop();
          Object.values(playbackVideoRefs.current).forEach(video => {
            if (video) video.pause();
          });
        }
      };
      
      requestAnimationFrame(updateProgress);
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed: ' + error.message);
      setIsExporting(false);
    }
  };
  
  // Cancel export
  const cancelExport = () => {
    if (exportRecorderRef.current && exportRecorderRef.current.state !== 'inactive') {
      exportRecorderRef.current.stop();
    }
    Object.values(playbackVideoRefs.current).forEach(video => {
      if (video) video.pause();
    });
    setIsExporting(false);
    setExportProgress(0);
    toast.info('Export cancelled');
  };
  
  // Social Export - Optimized for TikTok/Instagram/YouTube Shorts
  const exportForSocial = async (platform) => {
    if (tracks.length === 0) {
      toast.error('No tracks to export. Record some tracks first!');
      return;
    }
    
    // Platform specs
    const specs = {
      tiktok: { width: 1080, height: 1920, maxDuration: 60, name: 'TikTok' },
      instagram: { width: 1080, height: 1920, maxDuration: 60, name: 'Instagram Reels' },
      youtube: { width: 1080, height: 1920, maxDuration: 60, name: 'YouTube Shorts' },
      square: { width: 1080, height: 1080, maxDuration: 60, name: 'Square (Feed)' }
    };
    
    const spec = specs[platform] || specs.tiktok;
    
    toast.info(`Preparing ${spec.name} export (${spec.width}x${spec.height})...`);
    
    // For now, trigger regular export with a toast about the format
    // In a full implementation, we'd resize the canvas and crop to the aspect ratio
    setIsExporting(true);
    setExportProgress(0);
    
    try {
      // Create a temporary canvas for social export
      const socialCanvas = document.createElement('canvas');
      socialCanvas.width = spec.width;
      socialCanvas.height = spec.height;
      const ctx = socialCanvas.getContext('2d');
      
      // Fill background
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, spec.width, spec.height);
      
      // Add platform watermark
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = 'bold 24px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`Made with Escola de Samba Casa Samba Studio`, spec.width / 2, spec.height - 50);
      
      const stream = socialCanvas.captureStream(30);
      
      // Add audio from tracks
      const audioCtx = new AudioContext();
      const destination = audioCtx.createMediaStreamDestination();
      
      tracks.forEach(track => {
        const videoEl = playbackVideoRefs.current[track.id];
        if (videoEl && !track.muted) {
          try {
            const source = audioCtx.createMediaElementSource(videoEl);
            const gainNode = audioCtx.createGain();
            gainNode.gain.value = (track.volume || 100) / 100;
            source.connect(gainNode);
            gainNode.connect(destination);
          } catch (e) {
            // Already connected
          }
        }
      });
      
      destination.stream.getAudioTracks().forEach(track => {
        stream.addTrack(track);
      });
      
      const exportDuration = Math.min(duration, spec.maxDuration);
      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9,opus' });
      const chunks = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${projectName.replace(/[^a-z0-9]/gi, '_')}_${platform}.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        setIsExporting(false);
        setExportProgress(100);
        toast.success(`${spec.name} export complete!`);
        audioCtx.close();
      };
      
      recorder.start(100);
      
      // Reset and play videos
      Object.values(playbackVideoRefs.current).forEach(video => {
        if (video) {
          video.currentTime = 0;
          video.play();
        }
      });
      
      // Composite frames to social canvas
      const startTime = Date.now();
      const totalDuration = exportDuration * 1000;
      
      const drawSocialFrame = () => {
        const elapsed = Date.now() - startTime;
        setExportProgress(Math.min((elapsed / totalDuration) * 100, 99));
        
        // Draw composite to social canvas (center-crop to vertical)
        const sourceCanvas = compositeCanvasRef.current;
        if (sourceCanvas) {
          // Calculate crop region to fit vertical format
          const sourceAspect = sourceCanvas.width / sourceCanvas.height;
          const targetAspect = spec.width / spec.height;
          
          let sx, sy, sw, sh;
          if (sourceAspect > targetAspect) {
            // Source is wider - crop sides
            sh = sourceCanvas.height;
            sw = sh * targetAspect;
            sx = (sourceCanvas.width - sw) / 2;
            sy = 0;
          } else {
            // Source is taller - crop top/bottom
            sw = sourceCanvas.width;
            sh = sw / targetAspect;
            sx = 0;
            sy = (sourceCanvas.height - sh) / 2;
          }
          
          ctx.drawImage(sourceCanvas, sx, sy, sw, sh, 0, 0, spec.width, spec.height);
          
          // Redraw watermark
          ctx.fillStyle = 'rgba(255,255,255,0.5)';
          ctx.font = 'bold 20px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('Made with Escola de Samba Casa Samba Studio', spec.width / 2, spec.height - 40);
        }
        
        compositeFrame(); // Update main composite
        
        if (elapsed < totalDuration) {
          requestAnimationFrame(drawSocialFrame);
        } else {
          recorder.stop();
          Object.values(playbackVideoRefs.current).forEach(video => {
            if (video) video.pause();
          });
        }
      };
      
      requestAnimationFrame(drawSocialFrame);
      
    } catch (error) {
      console.error('Social export error:', error);
      toast.error('Export failed: ' + error.message);
      setIsExporting(false);
    }
  };
  
  // Beat Sync - Analyze track and align to metronome
  const performBeatSync = () => {
    if (tracks.length === 0) {
      toast.error('No tracks to sync');
      return;
    }
    
    const currentBPM = bpm;
    const beatDuration = 60 / currentBPM; // seconds per beat
    
    // Align each track's perceived start to the nearest beat
    toast.success(`Beat Sync applied! Tracks aligned to ${currentBPM} BPM`);
    setDetectedBPM(currentBPM);
    setBeatSyncEnabled(true);
    
    // In a full implementation, we'd analyze audio peaks to detect the beat
    // For now, we just enable a visual guide and align playback
  };
  
  // Duet Mode - Import external video to record alongside
  const handleDuetVideoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Check if it's a video file
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }
    
    // Create blob URL for the video
    const url = URL.createObjectURL(file);
    
    // Get video duration
    const tempVideo = document.createElement('video');
    tempVideo.src = url;
    tempVideo.onloadedmetadata = () => {
      setDuetVideo({
        url,
        name: file.name,
        duration: tempVideo.duration
      });
      setDuetMode(true);
      toast.success(`Duet video loaded: ${file.name}`);
    };
    tempVideo.onerror = () => {
      toast.error('Could not load video file');
      URL.revokeObjectURL(url);
    };
  };
  
  // Remove duet video
  const removeDuetVideo = () => {
    if (duetVideo?.url) {
      URL.revokeObjectURL(duetVideo.url);
    }
    setDuetVideo(null);
    setDuetMode(false);
    toast.info('Duet video removed');
  };
  
  // Start Challenge
  const startChallenge = (challenge) => {
    setSelectedChallenge(challenge);
    setBpm(challenge.bpm);
    setMetronomeOn(true);
    setShowChallenges(false);
    toast.success(`${challenge.emoji} ${challenge.name} Challenge started at ${challenge.bpm} BPM!`);
  };
  
  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins}:${String(secs).padStart(2, '0')}.${ms}`;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950 text-white">
      {/* Mobile Warning - Show on small screens */}
      <div className="md:hidden fixed inset-0 z-[100] bg-stone-950 flex flex-col items-center justify-center p-6 text-center">
        <Monitor className="w-16 h-16 text-amber-500 mb-4" />
        <h2 className="text-xl font-bold mb-2">Desktop Recommended</h2>
        <p className="text-stone-400 mb-4">
          The Multi-Cam Studio works best on a desktop or laptop computer with a larger screen.
        </p>
        <p className="text-stone-500 text-sm mb-6">
          Features like multi-camera recording, canvas compositing, and AI background removal require more screen space and processing power.
        </p>
        <div className="space-y-2">
          <Link 
            to="/" 
            className="block px-6 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg font-bold"
          >
            Go to Home
          </Link>
          <button
            onClick={function() {
              document.querySelector('.md\\:hidden').style.display = 'none';
            }}
            className="block w-full px-6 py-2 bg-stone-700 hover:bg-stone-600 rounded-lg text-sm"
          >
            Continue Anyway
          </button>
        </div>
      </div>
      
      {/* Header */}
      <header className="bg-stone-900/80 backdrop-blur-sm border-b border-stone-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="p-2 hover:bg-stone-800 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  className="bg-transparent text-xl font-bold border-b border-transparent hover:border-stone-600 focus:border-amber-500 outline-none px-1"
                />
                <p className="text-xs text-amber-500">Mestre Curtis Afro-Brasilian Multi-Cam Studio</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setShowHelp(true)}
                className="p-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 rounded-lg transition-colors"
                title="How to Use"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setShowShortcuts(true)}
                className="p-2 bg-stone-800 hover:bg-stone-700 rounded-lg transition-colors"
                title="Keyboard Shortcuts (?)"
              >
                <Keyboard className="w-4 h-4" />
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg transition-colors">
                <Save className="w-4 h-4" />
                Save
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg transition-colors">
                <FolderOpen className="w-4 h-4" />
                Load
              </button>
              
              {isExporting ? (
                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-lg">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {Math.round(exportProgress)}%
                  <button 
                    onClick={cancelExport}
                    className="ml-2 p-1 hover:bg-white/20 rounded"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button 
                  onClick={exportComposite}
                  disabled={tracks.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold rounded-lg hover:from-amber-400 hover:to-orange-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="export-button"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Panel - Input Sources & Metronome */}
          <div className="col-span-3 space-y-4">
            {/* Input Sources Panel */}
            <div className="bg-stone-800/50 rounded-xl border border-stone-700 overflow-hidden">
              {/* Tabs */}
              <div className="flex border-b border-stone-700">
                {[
                  { id: 'video', label: 'Video', icon: Camera },
                  { id: 'audio', label: 'Audio', icon: Mic },
                  { id: 'screen', label: 'Screen', icon: Monitor }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setInputPanelTab(tab.id)}
                    className={`flex-1 py-2 px-3 text-xs font-bold flex items-center justify-center gap-1.5 transition-colors ${
                      inputPanelTab === tab.id 
                        ? 'bg-amber-500 text-black' 
                        : 'text-stone-400 hover:text-white hover:bg-stone-700'
                    }`}
                  >
                    <tab.icon className="w-3.5 h-3.5" />
                    {tab.label}
                  </button>
                ))}
              </div>
              
              <div className="p-4">
                {/* Video Sources Tab */}
                {inputPanelTab === 'video' && (
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-amber-400">SELECT INPUT SOURCE</span>
                        <button
                          onClick={() => setShowHelp(true)}
                          className="p-1 hover:bg-stone-700 rounded-full"
                          title="How does this work?"
                        >
                          <HelpCircle className="w-3.5 h-3.5 text-amber-400" />
                        </button>
                      </div>
                      <span className="text-xs text-stone-500">
                        {selectedVideoSources.length}/3 active
                      </span>
                    </div>
                    
                    {/* Input Type Tabs */}
                    <div className="bg-stone-900 rounded-lg p-1">
                      <div className="grid grid-cols-6 gap-1">
                        {INPUT_TYPES.map(type => {
                          const count = type.id === 'all' 
                            ? videoSources.length 
                            : videoSources.filter(s => s.connectionType === type.id).length;
                          return (
                            <button
                              key={type.id}
                              onClick={() => setInputTypeTab(type.id)}
                              className={`flex flex-col items-center gap-0.5 py-2 px-1 rounded-lg text-[10px] transition-all ${
                                inputTypeTab === type.id
                                  ? 'bg-amber-500 text-black font-bold'
                                  : 'bg-stone-800 text-stone-400 hover:bg-stone-700 hover:text-white'
                              }`}
                              title={type.description}
                            >
                              <span className="text-sm">{type.icon}</span>
                              <span className="truncate w-full text-center">{type.label}</span>
                              {count > 0 && (
                                <span className={`text-[8px] px-1 rounded ${
                                  inputTypeTab === type.id ? 'bg-black/20' : 'bg-stone-700'
                                }`}>
                                  {count}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Input Type Description */}
                    <div className="bg-stone-800/50 rounded-lg p-2 text-center">
                      <span className="text-xs text-stone-400">
                        {INPUT_TYPES.find(t => t.id === inputTypeTab)?.description}
                      </span>
                    </div>
                    
                    {/* Refresh Button */}
                    <button
                      onClick={async () => {
                        try {
                          await navigator.mediaDevices.getUserMedia({ video: true });
                          const devices = await navigator.mediaDevices.enumerateDevices();
                          const videoDevices = devices.filter(d => d.kind === 'videoinput').map((d, i) => {
                            const connectionType = categorizeDevice(d.label || '');
                            return {
                              id: d.deviceId,
                              label: d.label || `Camera ${i + 1}`,
                              displayName: getGenericDeviceName(d.label || '', connectionType, i),
                              connectionType: connectionType,
                              active: false
                            };
                          });
                          setVideoSources(videoDevices);
                          toast.success(`Found ${videoDevices.length} input source(s)`);
                        } catch (err) {
                          toast.error('Please allow camera access');
                        }
                      }}
                      className="w-full py-2 bg-stone-700 hover:bg-stone-600 rounded-lg text-xs flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="w-3 h-3" />
                      Scan for Input Sources
                    </button>
                    
                    {/* Camera List */}
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {getFilteredVideoSources().length === 0 ? (
                        <div className="text-center py-6 text-stone-500 text-sm">
                          <div className="text-3xl mb-2">{INPUT_TYPES.find(t => t.id === inputTypeTab)?.icon || '📷'}</div>
                          <p>No {inputTypeTab === 'all' ? '' : INPUT_TYPES.find(t => t.id === inputTypeTab)?.label + ' '} inputs detected</p>
                          <p className="text-xs text-stone-600 mt-1">Click "Scan for Input Sources" to search</p>
                        </div>
                      ) : (
                        getFilteredVideoSources().map((source, idx) => (
                          <div
                            key={source.id}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                              selectedVideoSources.includes(source.id)
                                ? selectedVideoSources.indexOf(source.id) === 0
                                  ? 'bg-red-500/20 border-2 border-red-500 shadow-lg shadow-red-500/20'
                                  : 'bg-amber-500/20 border-2 border-amber-500'
                                : 'bg-stone-700/50 hover:bg-stone-700 border-2 border-transparent'
                            }`}
                            onClick={() => toggleVideoSource(source.id)}
                          >
                            {/* Selection number */}
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                              selectedVideoSources.includes(source.id)
                                ? selectedVideoSources.indexOf(source.id) === 0
                                  ? 'bg-red-500 text-white'
                                  : 'bg-amber-500 text-black'
                                : 'bg-stone-600 text-stone-400'
                            }`}>
                              {selectedVideoSources.includes(source.id) 
                                ? selectedVideoSources.indexOf(source.id) + 1
                                : getSourceTypeIcon(source.connectionType)}
                            </div>
                            
                            {/* Source info */}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold text-white">
                                {source.displayName || getSourceTypeLabel(source.connectionType)}
                              </div>
                              <div className="text-xs text-stone-400 flex items-center gap-1">
                                <span>{getSourceTypeIcon(source.connectionType)}</span>
                                <span>{getSourceTypeLabel(source.connectionType)}</span>
                              </div>
                            </div>
                            
                            {/* Status badge */}
                            {selectedVideoSources.includes(source.id) && (
                              <div className={`px-2 py-1 rounded text-[10px] font-bold ${
                                selectedVideoSources.indexOf(source.id) === 0
                                  ? 'bg-red-500 text-white'
                                  : 'bg-amber-500 text-black'
                              }`}>
                                {selectedVideoSources.indexOf(source.id) === 0 ? '🎥 MAIN' : `OVERLAY ${selectedVideoSources.indexOf(source.id)}`}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* Live Camera Preview - Shows what each selected camera sees */}
                    {selectedVideoSources.length > 0 && (
                      <>
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-bold text-stone-400 flex items-center gap-2">
                            <Eye className="w-3 h-3" />
                            LIVE PREVIEW
                          </div>
                          <span className="text-[10px] text-stone-500">Click preview to remove</span>
                        </div>
                        <div className={`grid gap-2 ${
                          selectedVideoSources.length === 1 ? 'grid-cols-1' :
                          selectedVideoSources.length === 2 ? 'grid-cols-2' : 'grid-cols-3'
                        }`}>
                          {selectedVideoSources.map((sourceId, idx) => {
                            const sourceInfo = videoSources.find(s => s.id === sourceId);
                            return (
                              <div 
                                key={sourceId} 
                                className={`aspect-video rounded-lg overflow-hidden relative cursor-pointer group ${
                                  idx === 0 ? 'ring-2 ring-red-500' : 'ring-1 ring-amber-500/50'
                                }`}
                                onClick={() => toggleVideoSource(sourceId)}
                                title={`Click to remove ${sourceInfo?.displayName || 'camera'}`}
                              >
                                <video
                                  ref={el => { 
                                    previewVideoRefs.current[sourceId] = el;
                                    // Set srcObject directly from activeStreams
                                    if (el && activeStreams[sourceId] && el.srcObject !== activeStreams[sourceId]) {
                                      el.srcObject = activeStreams[sourceId];
                                    }
                                  }}
                                  autoPlay
                                  muted
                                  playsInline
                                  className="w-full h-full object-cover bg-black"
                                />
                                {/* Green/Blue screen indicator */}
                                {backgroundRemovalEnabled && backgroundRemovalMode === 'greenscreen' && (
                                  <div className={`absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[8px] font-bold ${
                                    greenScreenColor === '#00FF00' ? 'bg-green-500 text-black' : 'bg-blue-500 text-white'
                                  }`}>
                                    {greenScreenColor === '#00FF00' ? '🟢 GREEN KEY' : '🔵 BLUE KEY'}
                                  </div>
                                )}
                                {backgroundRemovalEnabled && backgroundRemovalMode === 'ai' && (
                                  <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[8px] font-bold bg-purple-500 text-white">
                                    ✨ AI REMOVAL
                                  </div>
                                )}
                                {/* Hover overlay to show remove action */}
                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <div className="text-red-400 text-xs font-bold flex items-center gap-1">
                                    <X className="w-4 h-4" />
                                    REMOVE
                                  </div>
                                </div>
                                <div className={`absolute top-1 left-1 px-2 py-0.5 rounded text-[10px] font-bold ${
                                  idx === 0 ? 'bg-red-500 text-white' : 'bg-amber-500 text-black'
                                }`}>
                                  {idx === 0 ? (isRecording ? '🔴 REC' : '🎥 MAIN') : `OVERLAY ${idx}`}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                    
                    {/* Quick Tip */}
                    <div className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 rounded-lg p-3 text-[11px] text-stone-300 border border-amber-500/20 space-y-1">
                      <strong className="text-amber-400">💡 How to use:</strong>
                      <ol className="list-decimal list-inside space-y-0.5 text-stone-400">
                        <li><strong className="text-white">Click a camera above</strong> to select it (first = MAIN, others = overlays)</li>
                        <li><strong className="text-white">Click a preview below</strong> to remove that camera</li>
                        <li><strong className="text-white">Press R</strong> to start recording</li>
                      </ol>
                    </div>
                  </div>
                )}
                
                {/* Audio Sources Tab */}
                {inputPanelTab === 'audio' && (
                  <div className="space-y-4">
                    {/* Audio Input Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Mic className="w-4 h-4 text-amber-400" />
                        <span className="text-xs font-bold text-amber-400">Audio Input (Microphone)</span>
                      </div>
                      
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {audioSources.map(source => (
                          <label
                            key={source.id}
                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                              selectedAudioSource === source.id
                                ? 'bg-amber-500/20 border border-amber-500/50'
                                : 'bg-stone-700/50 hover:bg-stone-700 border border-transparent'
                            }`}
                          >
                            <input
                              type="radio"
                              name="audioSource"
                              checked={selectedAudioSource === source.id}
                              onChange={() => setSelectedAudioSource(source.id)}
                              className="w-4 h-4 accent-amber-500"
                            />
                            <span className="text-lg">{getAudioInputIcon(source.type)}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-white truncate">
                                {source.label}
                              </div>
                              <div className="text-xs text-stone-400">
                                {getAudioInputLabel(source.type)}
                              </div>
                            </div>
                          </label>
                        ))}
                        
                        {audioSources.length === 0 && (
                          <div className="text-center py-2 text-stone-500 text-sm">
                            No microphones detected
                          </div>
                        )}
                      </div>
                      
                      {/* Rescan Audio Devices Button */}
                      <button
                        onClick={async () => {
                          try {
                            await navigator.mediaDevices.getUserMedia({ audio: true });
                            const devices = await navigator.mediaDevices.enumerateDevices();
                            const audioDevices = devices.filter(d => d.kind === 'audioinput').map((d, i) => {
                              const label = d.label?.toLowerCase() || '';
                              let type = 'built-in';
                              let displayName = d.label || `Microphone ${i + 1}`;
                              
                              if (label.includes('x air') || label.includes('xair') || label.includes('xr18') || label.includes('x18')) {
                                type = 'interface';
                                displayName = `X Air 18 - Ch ${i + 1}`;
                              } else if (label.includes('behringer') || label.includes('midas') || label.includes('scarlett') || label.includes('focusrite') || label.includes('presonus') || label.includes('apollo') || label.includes('motu') || label.includes('m-audio') || label.includes('interface')) {
                                type = 'interface';
                              } else if (label.includes('usb')) {
                                type = 'usb';
                              } else if (label.includes('bluetooth') || label.includes('airpods') || label.includes('wireless')) {
                                type = 'bluetooth';
                              }
                              
                              return { id: d.deviceId, label: displayName, originalLabel: d.label, type };
                            });
                            setAudioSources(audioDevices);
                            toast.success(`Found ${audioDevices.length} audio input(s)`);
                          } catch (err) {
                            toast.error('Could not access microphone');
                          }
                        }}
                        className="w-full mt-2 py-2 bg-stone-700 hover:bg-stone-600 rounded-lg text-xs flex items-center justify-center gap-2"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Rescan Audio Devices (X Air 18, etc.)
                      </button>
                      
                      {/* Input Gain Control */}
                      <div className="mt-3 p-3 bg-gradient-to-r from-amber-900/30 to-orange-900/30 rounded-lg border border-amber-500/30">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <SlidersHorizontal className="w-4 h-4 text-amber-400" />
                            <span className="text-xs font-bold text-amber-400">Input Gain</span>
                          </div>
                          <span className={`text-xs font-mono ${isInputClipping ? 'text-red-400 font-bold animate-pulse' : 'text-amber-400'}`}>
                            {inputGain}%
                            {isInputClipping && ' CLIP!'}
                          </span>
                        </div>
                        
                        {/* Gain Slider */}
                        <input
                          type="range"
                          min="0"
                          max="150"
                          value={inputGain}
                          onChange={(e) => setInputGain(parseInt(e.target.value))}
                          className="w-full h-2 bg-stone-700 rounded-lg appearance-none cursor-pointer accent-amber-500 mb-2"
                        />
                        
                        {/* Input Level Meter */}
                        <div className="relative">
                          <div className="h-3 bg-stone-900 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all duration-75 ${
                                inputLevel > 95 ? 'bg-red-500' :
                                inputLevel > 80 ? 'bg-gradient-to-r from-green-500 via-yellow-500 to-red-500' :
                                inputLevel > 50 ? 'bg-gradient-to-r from-green-500 to-yellow-500' :
                                'bg-gradient-to-r from-green-600 to-green-400'
                              }`}
                              style={{ width: `${inputLevel}%` }}
                            />
                          </div>
                          {/* Level markers */}
                          <div className="flex justify-between text-[8px] text-stone-500 mt-0.5">
                            <span>-∞</span>
                            <span>-12</span>
                            <span>-6</span>
                            <span>0dB</span>
                          </div>
                        </div>
                        
                        {/* Preset Buttons */}
                        <div className="flex gap-1 mt-2">
                          <button
                            onClick={() => setInputGain(50)}
                            className={`flex-1 py-1 rounded text-xs ${inputGain === 50 ? 'bg-amber-500 text-black' : 'bg-stone-700 hover:bg-stone-600 text-stone-300'}`}
                          >
                            Low
                          </button>
                          <button
                            onClick={() => setInputGain(75)}
                            className={`flex-1 py-1 rounded text-xs ${inputGain === 75 ? 'bg-amber-500 text-black' : 'bg-stone-700 hover:bg-stone-600 text-stone-300'}`}
                          >
                            Normal
                          </button>
                          <button
                            onClick={() => setInputGain(100)}
                            className={`flex-1 py-1 rounded text-xs ${inputGain === 100 ? 'bg-amber-500 text-black' : 'bg-stone-700 hover:bg-stone-600 text-stone-300'}`}
                          >
                            Unity
                          </button>
                          <button
                            onClick={() => setInputGain(125)}
                            className={`flex-1 py-1 rounded text-xs ${inputGain >= 125 ? 'bg-red-500 text-white' : 'bg-stone-700 hover:bg-stone-600 text-stone-300'}`}
                          >
                            Boost
                          </button>
                        </div>
                        
                        <p className="text-[10px] text-stone-500 mt-2">
                          💡 Lower gain if your audio is distorting or clipping
                        </p>
                      </div>
                    </div>
                    
                    {/* Divider */}
                    <div className="border-t border-stone-700" />
                    
                    {/* Audio Output Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Headphones className="w-4 h-4 text-purple-400" />
                        <span className="text-xs font-bold text-purple-400">Audio Output (Monitoring)</span>
                      </div>
                      
                      {/* Metronome Output */}
                      <div className="mb-3">
                        <label className="text-xs text-stone-400 mb-1 block">Metronome Output</label>
                        <select
                          value={selectedMetronomeOutput}
                          onChange={(e) => setSelectedMetronomeOutput(e.target.value)}
                          className="w-full px-3 py-2 bg-stone-900 border border-stone-700 rounded-lg text-sm text-white focus:border-purple-500 focus:outline-none"
                          data-testid="metronome-output-select"
                        >
                          {audioOutputs.map(output => (
                            <option key={output.id} value={output.id}>
                              {getOutputTypeIcon(output.type)} {output.label}
                            </option>
                          ))}
                        </select>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-stone-500">Volume:</span>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={metronomeVolume}
                            onChange={(e) => setMetronomeVolume(parseInt(e.target.value))}
                            className="flex-1 h-1.5 bg-stone-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                            data-testid="metronome-volume"
                          />
                          <span className="text-xs text-stone-400 w-8">{metronomeVolume}%</span>
                        </div>
                      </div>
                      
                      {/* Track Playback Output */}
                      <div className="mb-3">
                        <label className="text-xs text-stone-400 mb-1 block">Track Playback Output</label>
                        <select
                          value={selectedAudioOutput || selectedTrackOutput}
                          onChange={(e) => {
                            setSelectedTrackOutput(e.target.value);
                            changeAudioOutput(e.target.value);
                          }}
                          className="w-full px-3 py-2 bg-stone-900 border border-stone-700 rounded-lg text-sm text-white focus:border-purple-500 focus:outline-none"
                          data-testid="track-output-select"
                        >
                          {audioOutputs.map(output => (
                            <option key={output.id} value={output.id}>
                              {getOutputTypeIcon(output.type)} {output.label}
                            </option>
                          ))}
                        </select>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-stone-500">Volume:</span>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={monitorVolume}
                            onChange={(e) => setMonitorVolume(parseInt(e.target.value))}
                            className="flex-1 h-1.5 bg-stone-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                            data-testid="monitor-volume"
                          />
                          <span className="text-xs text-stone-400 w-8">{monitorVolume}%</span>
                        </div>
                      </div>
                      
                      {/* Browser Support Note */}
                      <div className="p-2 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                        <p className="text-xs text-purple-300">
                          💡 <strong>Tip:</strong> Route metronome to headphones so it doesn't bleed into your recording!
                        </p>
                      </div>
                      
                      {audioOutputs.length <= 1 && (
                        <div className="mt-2 p-2 bg-stone-900/50 rounded-lg">
                          <p className="text-xs text-stone-500">
                            Connect headphones, external mixer, or Bluetooth device to see more output options.
                          </p>
                        </div>
                      )}
                      
                      {/* Rescan Outputs Button */}
                      <button
                        onClick={async () => {
                          try {
                            const devices = await navigator.mediaDevices.enumerateDevices();
                            const outputDevices = devices.filter(d => d.kind === 'audiooutput').map((d, i) => ({
                              id: d.deviceId || 'default',
                              label: d.label || `Speaker ${i + 1}`,
                              type: d.label?.toLowerCase().includes('bluetooth') ? 'bluetooth' :
                                    d.label?.toLowerCase().includes('x air') ? 'interface' :
                                    d.label?.toLowerCase().includes('xair') ? 'interface' :
                                    d.label?.toLowerCase().includes('interface') ? 'interface' :
                                    d.label?.toLowerCase().includes('headphone') ? 'headphones' :
                                    d.label?.toLowerCase().includes('usb') ? 'usb' :
                                    'built-in'
                            }));
                            if (!outputDevices.find(d => d.id === 'default')) {
                              outputDevices.unshift({ id: 'default', label: 'System Default', type: 'built-in' });
                            }
                            setAudioOutputs(outputDevices);
                            toast.success(`Found ${outputDevices.length} audio output(s)`);
                          } catch (err) {
                            toast.error('Could not scan audio outputs');
                          }
                        }}
                        className="w-full mt-2 py-2 bg-stone-700 hover:bg-stone-600 rounded-lg text-xs flex items-center justify-center gap-2"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Rescan Audio Outputs
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Screen Capture Tab */}
                {inputPanelTab === 'screen' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-amber-400">SCREEN / APP CAPTURE</span>
                      <span className="text-[10px] text-stone-500">
                        {isScreenSharing ? '🟢 Active' : '⚫ Inactive'}
                      </span>
                    </div>
                    
                    {!isScreenSharing ? (
                      <>
                        <button
                          onClick={startScreenShare}
                          className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold rounded-lg flex items-center justify-center gap-2 transition-all"
                        >
                          <Monitor className="w-5 h-5" />
                          Share Screen or App Window
                        </button>
                        
                        <div className="p-3 bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-lg text-xs text-stone-300 border border-blue-500/20 space-y-2">
                          <p className="font-bold text-blue-400">🎹 To capture Ableton Live:</p>
                          <ol className="list-decimal list-inside space-y-1 text-stone-400">
                            <li><strong className="text-white">Open Ableton</strong> first (don't minimize it)</li>
                            <li>Click the blue button above</li>
                            <li>In popup: click <strong className="text-white">"Window"</strong> tab</li>
                            <li>Select <strong className="text-white">Ableton Live</strong></li>
                            <li>Check <strong className="text-white">"Share system audio"</strong></li>
                          </ol>
                        </div>
                        
                        {/* Troubleshooting */}
                        <details className="group">
                          <summary className="cursor-pointer text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1">
                            <span>⚠️ Don't see Ableton? Click here</span>
                          </summary>
                          <div className="mt-2 p-2 bg-stone-800 rounded-lg text-[10px] text-stone-400 space-y-2">
                            <p><strong className="text-red-400">Mac users:</strong></p>
                            <ol className="list-decimal list-inside space-y-1 ml-2">
                              <li>Go to <strong>System Settings → Privacy & Security</strong></li>
                              <li>Click <strong>Screen Recording</strong></li>
                              <li>Enable your browser (Chrome/Safari)</li>
                              <li><strong>Restart your browser</strong></li>
                            </ol>
                            <p className="mt-2"><strong className="text-blue-400">Windows users:</strong> Should work automatically</p>
                          </div>
                        </details>
                        
                        {/* OBS Alternative */}
                        <details className="group">
                          <summary className="cursor-pointer text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1">
                            <span>🎬 Alternative: Use OBS Virtual Camera</span>
                          </summary>
                          <div className="mt-2 p-3 bg-purple-900/30 rounded-lg text-[10px] text-stone-300 border border-purple-500/20 space-y-2">
                            <p className="font-bold text-purple-400">OBS Virtual Camera Setup:</p>
                            <ol className="list-decimal list-inside space-y-1">
                              <li>Download & open <strong className="text-white">OBS Studio</strong></li>
                              <li>Click <strong>+</strong> under Sources → <strong>Window Capture</strong></li>
                              <li>Select <strong>Ableton Live</strong> window</li>
                              <li>Click <strong>"Start Virtual Camera"</strong> button in OBS</li>
                              <li>Come back here → Go to <strong>Video tab</strong></li>
                              <li>Select <strong>"OBS Virtual Camera"</strong> as input</li>
                            </ol>
                            <p className="mt-2 text-stone-500">OBS gives you more control over layout, overlays, and audio routing.</p>
                          </div>
                        </details>
                      </>
                    ) : (
                      <div className="space-y-3">
                        <div className="aspect-video bg-black rounded-lg overflow-hidden relative group">
                          <video
                            ref={el => { 
                              previewVideoRefs.current.screen = el;
                              screenShareVideoRef.current = el;
                            }}
                            autoPlay
                            muted
                            playsInline
                            className="w-full h-full object-contain"
                          />
                          <div className="absolute top-2 left-2 flex items-center gap-2 bg-blue-500 px-2 py-1 rounded-full">
                            <Monitor className="w-3 h-3" />
                            <span className="text-xs font-bold">SHARING</span>
                          </div>
                          {screenShareStream?.getAudioTracks().length > 0 && (
                            <div className="absolute top-2 right-2 flex items-center gap-1 bg-green-500 px-2 py-1 rounded-full">
                              <Volume2 className="w-3 h-3" />
                              <span className="text-xs font-bold">AUDIO</span>
                            </div>
                          )}
                          {/* Expand button overlay */}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                            <button
                              onClick={() => setScreenShareExpanded(true)}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-bold flex items-center gap-2"
                            >
                              <Maximize2 className="w-4 h-4" />
                              Full Screen View
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  if (screenShareVideoRef.current && document.pictureInPictureEnabled) {
                                    await screenShareVideoRef.current.requestPictureInPicture();
                                    toast.success('Picture-in-Picture enabled - drag window anywhere!');
                                  } else {
                                    toast.error('Picture-in-Picture not supported in this browser');
                                  }
                                } catch (err) {
                                  toast.error('Could not enable Picture-in-Picture');
                                }
                              }}
                              className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm font-bold flex items-center gap-2"
                            >
                              <Monitor className="w-4 h-4" />
                              Pop Out (PiP)
                            </button>
                          </div>
                        </div>
                        
                        {/* Quick action buttons */}
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => setScreenShareExpanded(true)}
                            className="py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-xs font-bold flex items-center justify-center gap-1"
                          >
                            <Maximize2 className="w-3 h-3" />
                            Full View
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                if (screenShareVideoRef.current && document.pictureInPictureEnabled) {
                                  await screenShareVideoRef.current.requestPictureInPicture();
                                  toast.success('Floating window created!');
                                }
                              } catch (err) {
                                toast.error('PiP not supported');
                              }
                            }}
                            className="py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-xs font-bold flex items-center justify-center gap-1"
                          >
                            <Monitor className="w-3 h-3" />
                            Float (PiP)
                          </button>
                        </div>
                        
                        {/* NEW: Open in separate resizable window */}
                        <button
                          onClick={() => {
                            // Create a new window with the screen share
                            const newWindow = window.open('', 'AbletonView', 'width=1400,height=900,resizable=yes,scrollbars=no');
                            if (newWindow && screenShareStream) {
                              newWindow.document.write(`
                                <!DOCTYPE html>
                                <html>
                                <head>
                                  <title>Ableton Live - Full Screen View</title>
                                  <style>
                                    * { margin: 0; padding: 0; box-sizing: border-box; }
                                    body { 
                                      background: #000; 
                                      display: flex; 
                                      align-items: center; 
                                      justify-content: center; 
                                      height: 100vh;
                                      overflow: hidden;
                                    }
                                    video { 
                                      width: 100%; 
                                      height: 100%; 
                                      object-fit: contain; 
                                    }
                                    .controls {
                                      position: fixed;
                                      top: 10px;
                                      right: 10px;
                                      display: flex;
                                      gap: 10px;
                                      z-index: 100;
                                    }
                                    button {
                                      padding: 10px 20px;
                                      background: #3b82f6;
                                      color: white;
                                      border: none;
                                      border-radius: 8px;
                                      cursor: pointer;
                                      font-weight: bold;
                                    }
                                    button:hover { background: #2563eb; }
                                    .fullscreen-btn { background: #8b5cf6; }
                                    .fullscreen-btn:hover { background: #7c3aed; }
                                  </style>
                                </head>
                                <body>
                                  <div class="controls">
                                    <button class="fullscreen-btn" onclick="document.documentElement.requestFullscreen()">
                                      ⛶ Fullscreen (F11)
                                    </button>
                                  </div>
                                  <video id="screenVideo" autoplay muted playsinline></video>
                                  <script>
                                    document.addEventListener('keydown', (e) => {
                                      if (e.key === 'f' || e.key === 'F') {
                                        document.documentElement.requestFullscreen();
                                      }
                                    });
                                  </script>
                                </body>
                                </html>
                              `);
                              newWindow.document.close();
                              
                              // Connect the stream to the new window's video element
                              setTimeout(() => {
                                const videoEl = newWindow.document.getElementById('screenVideo');
                                if (videoEl && screenShareStream) {
                                  videoEl.srcObject = screenShareStream;
                                  toast.success('Opened in new window! Resize or press F for fullscreen.');
                                }
                              }, 100);
                            } else {
                              toast.error('Could not open new window. Check popup blocker.');
                            }
                          }}
                          className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 rounded-lg text-sm font-bold flex items-center justify-center gap-2"
                        >
                          <Maximize2 className="w-4 h-4" />
                          Open BIG Window (Resizable to Any Size)
                        </button>
                        
                        <p className="text-[10px] text-stone-500 text-center">
                          👆 Opens a new window you can resize to 15"+ or fullscreen (press F)
                        </p>
                        
                        {/* Use as overlay option */}
                        <div className="flex items-center gap-2 p-2 bg-stone-800 rounded-lg">
                          <input
                            type="checkbox"
                            id="screenAsOverlay"
                            checked={selectedVideoSources.includes('screen')}
                            onChange={() => {
                              if (selectedVideoSources.includes('screen')) {
                                setSelectedVideoSources(prev => prev.filter(id => id !== 'screen'));
                              } else {
                                setSelectedVideoSources(prev => [...prev, 'screen']);
                              }
                            }}
                            className="w-4 h-4 accent-blue-500"
                          />
                          <label htmlFor="screenAsOverlay" className="text-xs text-stone-300 cursor-pointer">
                            Use screen capture as overlay in recording
                          </label>
                        </div>
                        
                        <button
                          onClick={stopScreenShare}
                          className="w-full py-2 bg-red-600 hover:bg-red-500 text-white font-bold rounded-lg flex items-center justify-center gap-2"
                        >
                          <X className="w-4 h-4" />
                          Stop Sharing
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Phone/Capture Card Setup Modal */}
            <AnimatePresence>
              {showPhoneSetup && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                  onClick={() => setShowPhoneSetup(false)}
                >
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    onClick={e => e.stopPropagation()}
                    className="bg-stone-800 rounded-2xl p-6 max-w-lg w-full border border-stone-700 max-h-[90vh] overflow-y-auto"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold">Add External Camera</h3>
                      <button onClick={() => setShowPhoneSetup(false)} className="p-1 hover:bg-stone-700 rounded">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    {/* Tabs for different connection methods */}
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={() => setSetupTab('phone')}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-colors ${
                          setupTab === 'phone' ? 'bg-amber-500 text-black' : 'bg-stone-700 text-stone-300 hover:bg-stone-600'
                        }`}
                      >
                        📱 Phone (Wi-Fi)
                      </button>
                      <button
                        onClick={() => setSetupTab('hdmi')}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-colors ${
                          setupTab === 'hdmi' ? 'bg-amber-500 text-black' : 'bg-stone-700 text-stone-300 hover:bg-stone-600'
                        }`}
                      >
                        🎥 HDMI/Canon
                      </button>
                    </div>
                    
                    {/* Phone Setup Tab */}
                    {setupTab === 'phone' && (
                      <div className="space-y-3">
                        <p className="text-stone-400 text-sm">
                          Use your phone as a wireless camera:
                        </p>
                        
                        <div className="p-3 bg-stone-700/50 rounded-lg">
                          <div className="font-bold text-green-400 mb-1">Android</div>
                          <ul className="text-sm text-stone-300 space-y-1">
                            <li>• <strong>DroidCam</strong> - Free, reliable</li>
                            <li>• <strong>Iriun Webcam</strong> - High quality</li>
                          </ul>
                        </div>
                        
                        <div className="p-3 bg-stone-700/50 rounded-lg">
                          <div className="font-bold text-blue-400 mb-1">iPhone</div>
                          <ul className="text-sm text-stone-300 space-y-1">
                            <li>• <strong>Camo</strong> - Best quality</li>
                            <li>• <strong>EpocCam</strong> - Easy setup</li>
                            <li>• <strong>iVCam</strong> - Free option</li>
                          </ul>
                        </div>
                        
                        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                          <div className="font-bold text-amber-400 mb-1">Setup Steps:</div>
                          <ol className="text-sm text-stone-300 space-y-1 list-decimal list-inside">
                            <li>Install app on phone & computer</li>
                            <li>Connect to same Wi-Fi network</li>
                            <li>Open app on both devices</li>
                            <li>Phone camera appears in list above!</li>
                          </ol>
                        </div>
                      </div>
                    )}
                    
                    {/* HDMI/Capture Card Setup Tab */}
                    {setupTab === 'hdmi' && (
                      <div className="space-y-3">
                        <p className="text-stone-400 text-sm">
                          Connect Canon, Sony, or other cameras via HDMI using a capture card:
                        </p>
                        
                        <div className="p-3 bg-stone-900 rounded-lg">
                          <div className="text-center text-sm mb-2">
                            <span className="text-stone-400">Camera</span>
                            <span className="text-amber-400 mx-2">→ HDMI →</span>
                            <span className="text-stone-400">Capture Card</span>
                            <span className="text-amber-400 mx-2">→ USB →</span>
                            <span className="text-stone-400">Computer</span>
                          </div>
                        </div>
                        
                        <div className="p-3 bg-stone-700/50 rounded-lg">
                          <div className="font-bold text-amber-400 mb-2">Recommended Capture Cards:</div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between items-center">
                              <span className="text-white">Elgato Cam Link 4K</span>
                              <span className="text-green-400">~$130 - Best</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-white">AVerMedia Live Gamer Mini</span>
                              <span className="text-blue-400">~$90 - Great</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-white">Generic USB HDMI Capture</span>
                              <span className="text-stone-400">~$20 - Basic</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-white">Magewell USB Capture</span>
                              <span className="text-purple-400">~$300 - Pro</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                          <div className="font-bold text-red-400 mb-1">Canon Camera Setup:</div>
                          <ol className="text-sm text-stone-300 space-y-1 list-decimal list-inside">
                            <li>Enable <strong>"Clean HDMI Output"</strong> in camera settings</li>
                            <li>Set camera to <strong>Movie Mode</strong></li>
                            <li>Connect HDMI from camera to capture card</li>
                            <li>Connect capture card USB to computer</li>
                            <li>Camera appears as "Cam Link" or similar</li>
                          </ol>
                        </div>
                        
                        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                          <div className="font-bold text-blue-400 mb-1">Pro Tip - Multiple Cameras:</div>
                          <p className="text-sm text-stone-300">
                            Use <strong>one capture card per camera</strong>. With 3 Elgato Cam Links, 
                            you can have 3 Canon cameras recording simultaneously!
                          </p>
                        </div>
                      </div>
                    )}
                    
                    <button
                      onClick={() => setShowPhoneSetup(false)}
                      className="w-full mt-4 py-2 bg-amber-500 text-black font-bold rounded-lg hover:bg-amber-400"
                    >
                      Got It!
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Metronome */}
            <Metronome
              bpm={bpm}
              setBpm={setBpm}
              isPlaying={metronomeOn}
              onToggle={() => setMetronomeOn(!metronomeOn)}
              volume={metronomeVolume}
              onVolumeChange={setMetronomeVolume}
            />
            
            {/* Count-in Settings */}
            <div className="bg-stone-800/50 rounded-xl p-4 border border-stone-700">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={countIn}
                  onChange={(e) => setCountIn(e.target.checked)}
                  className="w-4 h-4 rounded accent-amber-500"
                />
                <span className="text-sm">Count-in before recording</span>
              </label>
              
              {countIn && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-stone-400">Beats:</span>
                  {[2, 4, 8].map((beats) => (
                    <button
                      key={beats}
                      onClick={() => setCountInBeats(beats)}
                      className={`px-3 py-1 rounded text-sm ${
                        countInBeats === beats
                          ? 'bg-amber-500 text-black font-bold'
                          : 'bg-stone-700 hover:bg-stone-600'
                      }`}
                    >
                      {beats}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Center Panel - Main Recording/Preview Area */}
          <div className="col-span-6 space-y-4">
            {/* Composite Preview - Shows ALL recorded tracks combined */}
            <div className="bg-stone-800/50 rounded-xl p-4 border border-stone-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-purple-400 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Composite Preview
                  <span className="text-xs text-stone-500 ml-2">
                    {tracks.length > 0 
                      ? `${tracks.length} track${tracks.length > 1 ? 's' : ''} combined`
                      : 'No tracks yet'
                    }
                  </span>
                </h3>
                {tracks.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-stone-500 bg-stone-700 px-2 py-1 rounded">
                      All tracks play together
                    </span>
                  </div>
                )}
              </div>
              
              {/* Video Display - Shows ALL tracks in a grid */}
              <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
                {tracks.length === 0 ? (
                  <div className="w-full h-full flex flex-col items-center justify-center text-stone-500">
                    <Video className="w-20 h-20 mb-4 opacity-50" />
                    <p className="text-lg font-medium">Record your first track</p>
                    <p className="text-sm text-stone-600 mt-2">Watch previous recordings here while you overdub</p>
                  </div>
                ) : (
                  /* Grid of ALL tracks */
                  <div className={`w-full h-full grid gap-1 p-1 ${
                    tracks.length === 1 ? 'grid-cols-1' :
                    tracks.length === 2 ? 'grid-cols-2' :
                    tracks.length <= 4 ? 'grid-cols-2 grid-rows-2' :
                    tracks.length <= 6 ? 'grid-cols-3 grid-rows-2' :
                    'grid-cols-3 grid-rows-3'
                  }`}>
                    {tracks.filter(t => t.visible).map((track, idx) => (
                      <div 
                        key={track.id}
                        className={`relative rounded overflow-hidden ${
                          track.muted ? 'opacity-50' : ''
                        } ${track.solo ? 'ring-2 ring-amber-500' : ''}`}
                      >
                        <video
                          ref={el => { compositeVideoRefs.current[track.id] = el; }}
                          src={track.blobUrl || ''}
                          className="w-full h-full object-cover bg-black"
                          style={{
                            filter: VIDEO_FILTERS.find(f => f.id === videoFilter)?.css || ''
                          }}
                          muted={true}
                          playsInline
                          preload="auto"
                        />
                        <div className="absolute bottom-1 left-1 bg-black/80 px-2 py-0.5 rounded text-[10px] text-white">
                          {track.name}
                        </div>
                        <div className="absolute top-1 left-1 bg-purple-500/80 px-1.5 py-0.5 rounded text-[10px] text-white font-bold">
                          {idx + 1}
                        </div>
                        {track.muted && (
                          <div className="absolute top-1 right-1 bg-red-500/80 px-1.5 py-0.5 rounded text-[10px] text-white">
                            MUTED
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <p className="text-xs text-stone-500 mt-2 text-center">
                👆 See all your tracks combined • Audio plays from mixer
              </p>
            </div>
            
            {/* Live Preview - Shows your live camera feed */}
            <div className="bg-stone-800/50 rounded-xl p-4 border border-stone-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-amber-400 flex items-center gap-2">
                  <Grid3X3 className="w-4 h-4" />
                  Live Preview
                  {backgroundRemovalEnabled && (
                    <span className="ml-2 px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">
                      {backgroundRemovalMode === 'ai' ? 'AI BG Removal' : 'Chroma Key'}
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-stone-500">
                    {selectedLayout.name} • {tracks.length > 0 ? `${tracks.length} tracks` : selectedVideoSources.length > 0 ? `${selectedVideoSources.length} live` : 'No input'}
                  </span>
                </div>
              </div>
              
              <div 
                className="aspect-video rounded-lg overflow-hidden relative"
                style={{
                  background: selectedBackground.type === 'transparent' 
                    ? 'repeating-conic-gradient(#333 0% 25%, #222 0% 50%) 50% / 20px 20px'
                    : selectedBackground.type === 'gradient' 
                      ? selectedBackground.color 
                      : selectedBackground.type === 'image' 
                        ? `url(${selectedBackground.url}) center/cover` 
                        : selectedBackground.color || '#000'
                }}
                data-testid="composite-preview"
              >
                {/* Always show LIVE camera feed in Live Preview when cameras are active */}
                {selectedVideoSources.length > 0 && Object.keys(activeStreams).length > 0 ? (
                  <div className={`w-full h-full relative ${
                    selectedVideoSources.length === 1 ? '' : 'grid gap-1 p-1'
                  } ${
                    selectedVideoSources.length === 2 ? 'grid-cols-2' : 
                    selectedVideoSources.length > 2 ? 'grid-cols-2 grid-rows-2' : ''
                  }`}>
                    {/* Chroma Key Canvas Preview - shows when green/blue screen is active */}
                    {backgroundRemovalEnabled && backgroundRemovalMode === 'greenscreen' && selectedVideoSources.length === 1 && (
                      <div className="absolute inset-0 z-10 overflow-hidden bg-black">
                        <canvas
                          ref={chromaCanvasRef}
                          className="w-full h-full"
                          style={{ objectFit: 'contain' }}
                        />
                        <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold ${
                          greenScreenColor === '#00FF00' ? 'bg-green-500 text-black' : 'bg-blue-500 text-white'
                        }`}>
                          {greenScreenColor === '#00FF00' ? '🟢 GREEN KEY LIVE' : '🔵 BLUE KEY LIVE'}
                        </div>
                        <div className="absolute bottom-2 left-2 bg-black/70 px-2 py-1 rounded text-xs text-white">
                          Real-time chroma key preview
                        </div>
                      </div>
                    )}
                    
                    {/* AI Background Removal Canvas - shows when AI mode is active */}
                    {backgroundRemovalEnabled && backgroundRemovalMode === 'ai' && selectedVideoSources.length === 1 && (
                      <div className="absolute inset-0 z-10 overflow-hidden bg-black">
                        <canvas
                          ref={aiCanvasRef}
                          className="w-full h-full"
                          style={{ objectFit: 'contain' }}
                        />
                        <div className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                          ✨ AI REMOVAL LIVE
                        </div>
                        <div className="absolute bottom-2 left-2 bg-black/70 px-2 py-1 rounded text-xs text-white">
                          AI-powered background removal
                        </div>
                      </div>
                    )}
                    
                    {selectedVideoSources.map((sourceId, idx) => {
                      const sourceInfo = videoSources.find(s => s.id === sourceId);
                      const stream = activeStreams[sourceId];
                      return (
                        <div 
                          key={sourceId}
                          className={`relative rounded overflow-hidden ${
                            idx === 0 ? 'ring-2 ring-red-500' : 'ring-1 ring-amber-500/50'
                          } ${backgroundRemovalEnabled && (backgroundRemovalMode === 'greenscreen' || backgroundRemovalMode === 'ai') && idx === 0 ? 'opacity-0' : ''}`}
                        >
                          <video
                            ref={el => {
                              if (el && stream && el.srcObject !== stream) {
                                el.srcObject = stream;
                              }
                              // Store ref for chroma key processing
                              if (el) {
                                previewVideoRefs.current[sourceId] = el;
                              }
                            }}
                            autoPlay
                            muted
                            playsInline
                            className="w-full h-full object-cover bg-black"
                            style={{
                              filter: VIDEO_FILTERS.find(f => f.id === videoFilter)?.css || ''
                            }}
                          />
                          <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-bold ${
                            idx === 0 ? 'bg-red-500 text-white' : 'bg-amber-500 text-black'
                          }`}>
                            {idx === 0 ? '🎥 MAIN' : `OVERLAY ${idx}`}
                          </div>
                          <div className="absolute bottom-2 left-2 bg-black/70 px-2 py-1 rounded text-xs text-white">
                            {sourceInfo?.displayName || `Camera ${idx + 1}`}
                          </div>
                          {isRecording ? (
                            <div className="absolute top-2 right-2 flex items-center gap-1 bg-red-600 px-2 py-1 rounded text-xs text-white animate-pulse">
                              <span className="w-2 h-2 bg-white rounded-full animate-ping"></span>
                              REC
                            </div>
                          ) : (
                            <div className="absolute top-2 right-2 flex items-center gap-1 bg-green-600/80 px-2 py-1 rounded text-xs text-white">
                              <span className="w-2 h-2 bg-white rounded-full"></span>
                              LIVE
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : tracks.length > 0 ? (
                  /* No active cameras but have tracks - show recorded tracks */
                  showLayoutEditor ? (
                    <LayoutEditor
                      tracks={tracks.filter(t => t.visible)}
                      selectedTrackId={selectedTrackId}
                      onSelectTrack={setSelectedTrackId}
                      onUpdateTrack={handleUpdateTrack}
                      background={selectedBackground}
                      canvasSize={{ width: 1920, height: 1080 }}
                    />
                  ) : (
                    <div className={`w-full h-full grid gap-1 p-1 ${
                      selectedLayout.id === 'single' ? 'grid-cols-1' :
                      selectedLayout.id === 'split-h' ? 'grid-cols-2' :
                      selectedLayout.id === 'split-v' ? 'grid-cols-1 grid-rows-2' :
                      selectedLayout.id === 'grid-4' ? 'grid-cols-2 grid-rows-2' :
                      selectedLayout.id === 'grid-6' ? 'grid-cols-3 grid-rows-2' :
                      selectedLayout.id === 'grid-9' ? 'grid-cols-3 grid-rows-3' :
                      tracks.length <= 4 ? 'grid-cols-2' :
                      'grid-cols-3'
                    }`}>
                      {tracks.filter(t => t.visible).slice(0, 16).map((track) => (
                        <div 
                          key={track.id} 
                          className={`relative rounded overflow-hidden transition-all ${
                            selectedTrackId === track.id ? 'ring-2 ring-amber-500' : ''
                          }`}
                          onClick={() => setSelectedTrackId(track.id)}
                          style={{
                            backgroundColor: backgroundRemovalEnabled ? 'transparent' : '#1a1a1a'
                          }}
                        >
                          <video
                            ref={(el) => { 
                              playbackVideoRefs.current[track.id] = el;
                            }}
                            src={track.blobUrl || ''}
                            className="w-full h-full object-contain"
                            style={{
                              filter: VIDEO_FILTERS.find(f => f.id === videoFilter)?.css || ''
                            }}
                            playsInline
                            preload="auto"
                          />
                          <div className="absolute bottom-1 left-1 bg-black/70 px-1.5 py-0.5 rounded text-xs">
                            {track.name}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  /* No cameras and no tracks - show placeholder */
                  <div className="w-full h-full flex flex-col items-center justify-center text-stone-500 bg-black/50">
                    <Layers className="w-16 h-16 mb-4 opacity-50" />
                    <p className="text-sm">Select a camera to see live preview</p>
                    <p className="text-xs text-stone-600 mt-1">Your recording will appear here</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Hidden Composite Canvas for Export */}
            <canvas
              ref={compositeCanvasRef}
              className="hidden"
              data-testid="composite-canvas"
            />
            
            {/* Hidden Audio Playback Elements - ALWAYS rendered when tracks exist */}
            {/* These provide audio during recording when Live Preview shows camera feed */}
            {tracks.map((track) => (
              <video
                key={`audio-${track.id}`}
                ref={(el) => { 
                  playbackVideoRefs.current[track.id] = el;
                }}
                src={track.blobUrl || ''}
                className="hidden"
                playsInline
                preload="auto"
              />
            ))}
            
            {/* Timeline with Waveforms */}
            {tracks.length > 0 && (
              <div className="bg-stone-900/80 rounded-xl p-3 border border-stone-700 mt-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold text-stone-400 flex items-center gap-2">
                    <Music className="w-3 h-3" />
                    TIMELINE
                  </h3>
                  <span className="text-xs text-stone-500 font-mono">{formatTime(duration)}</span>
                </div>
                
                {/* Playhead */}
                <div className="relative">
                  {/* Time markers */}
                  <div className="flex justify-between text-[9px] text-stone-600 mb-1 px-1">
                    <span>0:00</span>
                    <span>{formatTime(duration / 4)}</span>
                    <span>{formatTime(duration / 2)}</span>
                    <span>{formatTime(duration * 3 / 4)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                  
                  {/* Track Waveforms - Draggable */}
                  <div className="space-y-1">
                    {tracks.map((track, index) => {
                      const waveform = trackWaveforms[track.id] || [];
                      const hasSolo = tracks.some(t => t.solo);
                      const isMuted = track.muted || (hasSolo && !track.solo);
                      const isDragging = draggingTrackId === track.id;
                      
                      return (
                        <div 
                          key={track.id}
                          draggable
                          onDragStart={(e) => handleTrackDragStart(e, track.id)}
                          onDragOver={(e) => handleTrackDragOver(e, track.id)}
                          onDrop={(e) => handleTrackDrop(e, track.id)}
                          onDragEnd={() => setDraggingTrackId(null)}
                          className={`relative h-10 rounded overflow-hidden cursor-grab transition-all ${
                            selectedTrackId === track.id ? 'ring-2 ring-amber-500' : ''
                          } ${isMuted ? 'opacity-40' : ''} ${isDragging ? 'opacity-50 scale-95' : ''}`}
                          onClick={() => setSelectedTrackId(track.id)}
                          style={{
                            background: `linear-gradient(90deg, ${
                              track.solo ? 'rgba(245,158,11,0.3)' : 
                              track.muted ? 'rgba(239,68,68,0.2)' : 
                              'rgba(168,85,247,0.2)'
                            }, transparent)`
                          }}
                        >
                          {/* Drag handle */}
                          <div className="absolute left-0 top-0 bottom-0 w-6 flex items-center justify-center bg-stone-800/50 cursor-grab">
                            <GripVertical className="w-3 h-3 text-stone-500" />
                          </div>
                          
                          {/* Track label */}
                          <div className="absolute left-7 top-1/2 -translate-y-1/2 text-[9px] font-bold text-white/90 z-10 bg-black/60 px-1.5 py-0.5 rounded flex items-center gap-1">
                            <span className="text-amber-400">{index + 1}</span>
                            <span className="truncate max-w-[60px]">{track.name?.slice(0, 10) || `Track`}</span>
                          </div>
                          
                          {/* Waveform visualization */}
                          <div className="absolute inset-0 flex items-center justify-center gap-px pl-24 pr-2">
                            {waveform.length > 0 ? (
                              waveform.map((amplitude, i) => (
                                <div
                                  key={i}
                                  className={`w-0.5 rounded-full transition-all ${
                                    track.solo ? 'bg-amber-400' : 
                                    track.muted ? 'bg-red-400/50' : 
                                    'bg-purple-400'
                                  }`}
                                  style={{ 
                                    height: `${Math.max(4, amplitude * 28)}px`,
                                    opacity: 0.6 + amplitude * 0.4
                                  }}
                                />
                              ))
                            ) : (
                              // Placeholder bars
                              [...Array(60)].map((_, i) => (
                                <div
                                  key={i}
                                  className="w-0.5 h-3 bg-stone-600/50 rounded-full"
                                />
                              ))
                            )}
                          </div>
                          
                          {/* Playhead position indicator */}
                          {duration > 0 && (
                            <div 
                              className="absolute top-0 bottom-0 w-0.5 bg-amber-500 z-20"
                              style={{ left: `${(currentTime / duration) * 100}%` }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Global playhead line */}
                  {duration > 0 && (
                    <div 
                      className="absolute top-0 bottom-0 w-0.5 bg-amber-500 pointer-events-none z-30"
                      style={{ left: `${(currentTime / duration) * 100}%` }}
                    >
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-amber-500 rounded-full" />
                    </div>
                  )}
                </div>
                
                {/* Scrubber */}
                <div 
                  className="h-2 bg-stone-800 rounded-full mt-2 cursor-pointer relative"
                  onClick={(e) => {
                    if (duration === 0) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const t = ((e.clientX - rect.left) / rect.width) * duration;
                    seekAllVideos(t);
                  }}
                >
                  <div 
                    className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all"
                    style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Right Panel - Controls & Mixer */}
          <div className="col-span-3 space-y-3 max-h-[calc(100vh-180px)] overflow-y-auto">
            {/* Transport Controls - Compact */}
            <div className="bg-stone-800/50 rounded-xl p-3 border border-stone-700">
              <div className="flex items-center justify-center gap-1 mb-2">
                <button onClick={() => seekAllVideos(0)} disabled={tracks.length === 0} className="p-1.5 bg-stone-700 hover:bg-stone-600 rounded disabled:opacity-50" title="Start (Home)"><SkipBack className="w-4 h-4" /></button>
                <button onClick={() => seekAllVideos(Math.max(0, currentTime - 5))} disabled={tracks.length === 0} className="p-1.5 bg-stone-700 hover:bg-stone-600 rounded disabled:opacity-50" title="-5s (←)"><Rewind className="w-4 h-4" /></button>
                <button onClick={stopPlayback} disabled={tracks.length === 0} className={`p-1.5 rounded disabled:opacity-50 ${!isPlaying ? 'bg-amber-500 text-black' : 'bg-stone-700 hover:bg-stone-600'}`} title="Stop (S)"><Square className="w-4 h-4" /></button>
                <button onClick={playAll} disabled={tracks.length === 0 || isRecording} className={`p-1.5 rounded disabled:opacity-50 ${isPlaying ? 'bg-green-500 text-black' : 'bg-stone-700 hover:bg-stone-600'}`} title="Play (Space)"><Play className="w-4 h-4" /></button>
                <button onClick={pauseAllVideos} disabled={!isPlaying} className="p-1.5 bg-stone-700 hover:bg-stone-600 rounded disabled:opacity-50" title="Pause"><Pause className="w-4 h-4" /></button>
                <button onClick={() => seekAllVideos(Math.min(duration, currentTime + 5))} disabled={tracks.length === 0} className="p-1.5 bg-stone-700 hover:bg-stone-600 rounded disabled:opacity-50" title="+5s (→)"><FastForward className="w-4 h-4" /></button>
                <button onClick={() => seekAllVideos(duration)} disabled={tracks.length === 0} className="p-1.5 bg-stone-700 hover:bg-stone-600 rounded disabled:opacity-50" title="End"><SkipForward className="w-4 h-4" /></button>
              </div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <button onClick={isRecording ? stopRecording : startRecording} disabled={Object.keys(activeStreams).length === 0} className={`px-4 py-1.5 rounded-full font-bold text-sm flex items-center gap-1 ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-red-600 hover:bg-red-500'} disabled:opacity-50`} title="Record (R)">
                  {isRecording ? <><StopCircle className="w-4 h-4" /> STOP</> : <><Circle className="w-4 h-4 fill-current" /> REC</>}
                </button>
                <span className="font-mono text-lg text-amber-400">{formatTime(currentTime)}</span>
                <button
                  onClick={performBeatSync}
                  disabled={tracks.length === 0}
                  className={`px-3 py-1.5 rounded-full font-bold text-xs flex items-center gap-1 disabled:opacity-50 ${
                    beatSyncEnabled 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                      : 'bg-stone-700 text-stone-300 hover:bg-stone-600'
                  }`}
                  title="Beat Sync - Align tracks to metronome"
                >
                  <Timer className="w-3 h-3" />
                  {beatSyncEnabled ? `${bpm} BPM` : 'Beat Sync'}
                </button>
              </div>
              <div className="mt-1 h-1 bg-stone-700 rounded-full cursor-pointer" onClick={(e) => { if(tracks.length === 0 || duration === 0) return; const r = e.currentTarget.getBoundingClientRect(); const t = ((e.clientX - r.left) / r.width) * duration; seekAllVideos(t); }}>
                <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }} />
              </div>
              {/* Shortcut hint */}
              <p className="text-[9px] text-stone-500 text-center mt-1">Press <kbd className="px-1 bg-stone-700 rounded">?</kbd> for shortcuts</p>
            </div>
            
            {/* INPUT GAIN - Master Interface Control */}
            <div className="bg-gradient-to-r from-amber-900/40 to-orange-900/40 rounded-xl p-3 border-2 border-amber-500/50">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-amber-400 flex items-center gap-2">
                  <Mic className="w-4 h-4" />
                  INPUT GAIN
                </h3>
                <span className={`text-sm font-mono font-bold ${isInputClipping ? 'text-red-400 animate-pulse' : 'text-amber-400'}`}>
                  {inputGain}% {isInputClipping && 'CLIP!'}
                </span>
              </div>
              <input type="range" min="0" max="150" value={inputGain} onChange={(e) => setInputGain(parseInt(e.target.value))} className="w-full h-2 bg-stone-700 rounded-lg appearance-none cursor-pointer accent-amber-500 mb-2" />
              <div className="h-3 bg-stone-900 rounded-full overflow-hidden mb-1">
                <div className={`h-full transition-all duration-75 ${inputLevel > 95 ? 'bg-red-500' : inputLevel > 70 ? 'bg-gradient-to-r from-green-500 via-yellow-500 to-red-500' : 'bg-gradient-to-r from-green-600 to-green-400'}`} style={{ width: `${inputLevel}%` }} />
              </div>
              <div className="flex gap-1">
                <button onClick={() => setInputGain(50)} className={`flex-1 py-1 rounded text-xs ${inputGain === 50 ? 'bg-amber-500 text-black font-bold' : 'bg-stone-700 text-stone-300'}`}>Low</button>
                <button onClick={() => setInputGain(75)} className={`flex-1 py-1 rounded text-xs ${inputGain === 75 ? 'bg-amber-500 text-black font-bold' : 'bg-stone-700 text-stone-300'}`}>Norm</button>
                <button onClick={() => setInputGain(100)} className={`flex-1 py-1 rounded text-xs ${inputGain === 100 ? 'bg-amber-500 text-black font-bold' : 'bg-stone-700 text-stone-300'}`}>Unity</button>
                <button onClick={() => setInputGain(125)} className={`flex-1 py-1 rounded text-xs ${inputGain >= 125 ? 'bg-red-500 text-white font-bold' : 'bg-stone-700 text-stone-300'}`}>Boost</button>
              </div>
            </div>
            
            {/* AUDIO MIXER - Professional Channel Strip Design */}
            <div className="bg-gradient-to-b from-stone-900 to-stone-950 rounded-xl p-4 border border-emerald-500/20 shadow-lg shadow-emerald-500/5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-emerald-400 flex items-center gap-2 tracking-wider">
                  <SlidersHorizontal className="w-4 h-4" />
                  AUDIO MIXER
                </h3>
                <div className="flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 rounded-full">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[9px] text-emerald-400 font-mono">ACTIVE</span>
                </div>
              </div>
              
              {/* Channel Strips Container */}
              <div className="flex gap-2 justify-center">
                {/* Master Channel Strip */}
                <div className="flex flex-col items-center bg-gradient-to-b from-emerald-900/30 to-emerald-950/50 rounded-lg p-2 border border-emerald-500/30 min-w-[52px]">
                  <div className="text-[9px] font-bold text-emerald-300 tracking-widest mb-2 bg-emerald-500/20 px-2 py-0.5 rounded">
                    MASTER
                  </div>
                  
                  {/* LED Meter */}
                  <div className="flex gap-0.5 mb-2">
                    <div className="w-1 h-24 bg-stone-950 rounded-sm overflow-hidden flex flex-col-reverse">
                      {[...Array(12)].map((_, i) => (
                        <div 
                          key={i}
                          className={`h-2 w-full transition-all ${
                            masterVolume > (i * 8.3) 
                              ? i > 9 ? 'bg-red-500' : i > 7 ? 'bg-yellow-500' : 'bg-emerald-400'
                              : 'bg-stone-800'
                          }`}
                        />
                      ))}
                    </div>
                    <div className="w-1 h-24 bg-stone-950 rounded-sm overflow-hidden flex flex-col-reverse">
                      {[...Array(12)].map((_, i) => (
                        <div 
                          key={i}
                          className={`h-2 w-full transition-all ${
                            masterVolume > (i * 8.3) 
                              ? i > 9 ? 'bg-red-500' : i > 7 ? 'bg-yellow-500' : 'bg-emerald-400'
                              : 'bg-stone-800'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Fader Track */}
                  <div className="relative w-10 h-28 bg-stone-950 rounded-lg overflow-hidden border border-stone-700">
                    {/* Fader Fill */}
                    <div 
                      className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-emerald-600 via-emerald-500 to-emerald-400 transition-all"
                      style={{ height: `${masterVolume}%` }}
                    />
                    {/* Fader Groove */}
                    <div className="absolute inset-x-3 top-2 bottom-2 bg-stone-800/50 rounded-full" />
                    {/* Fader Input */}
                    <input 
                      type="range" 
                      min="0" 
                      max="100" 
                      value={masterVolume} 
                      onChange={(e) => setMasterVolume(parseInt(e.target.value))} 
                      className="absolute inset-0 w-full h-full opacity-0 cursor-ns-resize"
                      style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
                    />
                    {/* Fader Knob */}
                    <div 
                      className="absolute left-1 right-1 h-4 bg-gradient-to-b from-stone-300 to-stone-500 rounded shadow-lg border border-stone-400 pointer-events-none transition-all"
                      style={{ bottom: `calc(${masterVolume}% - 8px)` }}
                    >
                      <div className="absolute inset-x-1 top-1/2 h-0.5 bg-stone-600 rounded" />
                    </div>
                  </div>
                  
                  {/* Volume Display */}
                  <div className="mt-2 px-2 py-1 bg-stone-950 rounded font-mono text-sm text-emerald-400 border border-stone-700">
                    {masterVolume}
                  </div>
                </div>
                
                {/* Divider */}
                <div className="w-px bg-stone-700 my-2" />
                
                {/* Track Channel Strips */}
                {tracks.length === 0 ? (
                  <div className="flex items-center justify-center text-stone-600 text-xs px-6 py-8 italic">
                    Record tracks to see channels
                  </div>
                ) : (
                  tracks.map((track, index) => {
                    const trackLevel = trackLevels[track.id] || 0;
                    return (
                    <div 
                      key={track.id} 
                      className={`flex flex-col items-center rounded-lg p-2 min-w-[56px] transition-all ${
                        track.solo 
                          ? 'bg-gradient-to-b from-amber-900/40 to-amber-950/60 border border-amber-500/50' 
                          : track.muted 
                            ? 'bg-gradient-to-b from-red-900/20 to-red-950/30 border border-red-500/30' 
                            : 'bg-gradient-to-b from-stone-800/50 to-stone-900/50 border border-stone-700'
                      }`}
                    >
                      {/* Track Name - Editable */}
                      <input
                        type="text"
                        value={track.name || `Track ${index + 1}`}
                        onChange={(e) => handleRenameTrack(track.id, e.target.value)}
                        className={`w-full text-[8px] font-bold tracking-wider mb-1 px-1 py-0.5 rounded text-center bg-transparent border border-transparent hover:border-stone-600 focus:border-amber-500 focus:outline-none truncate ${
                          track.solo ? 'text-amber-300' : 
                          track.muted ? 'text-red-300' : 
                          'text-stone-400'
                        }`}
                        title={track.name}
                      />
                      
                      {/* Real-time LED Meter */}
                      <div className="flex gap-0.5 mb-1">
                        <div className="w-1.5 h-16 bg-stone-950 rounded-sm overflow-hidden flex flex-col-reverse">
                          {[...Array(8)].map((_, i) => {
                            const threshold = (i + 1) * 12.5;
                            const isLit = trackLevel >= threshold;
                            return (
                              <div 
                                key={i}
                                className={`h-2 w-full transition-all duration-75 ${
                                  isLit
                                    ? i >= 7 ? 'bg-red-500 shadow-sm shadow-red-500/50' 
                                      : i >= 5 ? 'bg-yellow-500 shadow-sm shadow-yellow-500/50' 
                                      : 'bg-emerald-400 shadow-sm shadow-emerald-400/50'
                                    : 'bg-stone-800'
                                }`}
                              />
                            );
                          })}
                        </div>
                        <div className="w-1.5 h-16 bg-stone-950 rounded-sm overflow-hidden flex flex-col-reverse">
                          {[...Array(8)].map((_, i) => {
                            const threshold = (i + 1) * 12.5;
                            const isLit = trackLevel >= threshold;
                            return (
                              <div 
                                key={i}
                                className={`h-2 w-full transition-all duration-75 ${
                                  isLit
                                    ? i >= 7 ? 'bg-red-500 shadow-sm shadow-red-500/50' 
                                      : i >= 5 ? 'bg-yellow-500 shadow-sm shadow-yellow-500/50' 
                                      : 'bg-emerald-400 shadow-sm shadow-emerald-400/50'
                                    : 'bg-stone-800'
                                }`}
                              />
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Fader Track */}
                      <div className="relative w-8 h-20 bg-stone-950 rounded overflow-hidden border border-stone-700">
                        <div 
                          className={`absolute bottom-0 left-0 right-0 transition-all ${
                            track.muted ? 'bg-red-500/20' : 'bg-gradient-to-t from-amber-600 via-amber-500 to-amber-400'
                          }`}
                          style={{ height: track.muted ? '0%' : `${track.volume}%` }}
                        />
                        <input 
                          type="range" 
                          min="0" 
                          max="100" 
                          value={track.volume} 
                          onChange={(e) => handleVolumeChange(track.id, parseInt(e.target.value))} 
                          className="absolute inset-0 w-full h-full opacity-0 cursor-ns-resize"
                          style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
                        />
                        {/* Fader Knob */}
                        <div 
                          className="absolute left-0.5 right-0.5 h-3 bg-gradient-to-b from-stone-300 to-stone-500 rounded shadow border border-stone-400 pointer-events-none transition-all"
                          style={{ bottom: `calc(${track.volume}% - 6px)` }}
                        >
                          <div className="absolute inset-x-0.5 top-1/2 h-px bg-stone-600" />
                        </div>
                      </div>
                      
                      {/* Volume Value */}
                      <span className="text-[10px] font-mono text-stone-400 mt-1">{track.volume}</span>
                      
                      {/* Control Buttons */}
                      <div className="flex gap-1 mt-1">
                        <button 
                          onClick={() => handleMute(track.id)} 
                          className={`w-6 h-6 rounded text-[10px] font-bold transition-all ${
                            track.muted 
                              ? 'bg-red-500 text-white shadow-lg shadow-red-500/50' 
                              : 'bg-stone-800 text-stone-500 hover:bg-stone-700 hover:text-stone-300'
                          }`}
                          title="Mute"
                        >
                          M
                        </button>
                        <button 
                          onClick={() => handleSolo(track.id)} 
                          className={`w-6 h-6 rounded text-[10px] font-bold transition-all ${
                            track.solo 
                              ? 'bg-amber-500 text-black shadow-lg shadow-amber-500/50' 
                              : 'bg-stone-800 text-stone-500 hover:bg-stone-700 hover:text-stone-300'
                          }`}
                          title="Solo"
                        >
                          S
                        </button>
                      </div>
                      
                      {/* Delete Button */}
                      <button 
                        onClick={() => handleDeleteTrack(track.id)} 
                        className="mt-2 w-full py-1 bg-stone-800 hover:bg-red-600 text-stone-500 hover:text-white rounded text-[9px] transition-all flex items-center justify-center gap-1"
                        title={`Delete ${track.name}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  );})
                )}
              </div>
              
              {/* Quick Actions */}
              {tracks.length > 0 && (
                <div className="flex flex-col gap-2 mt-4 pt-3 border-t border-stone-800">
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setTracks(prev => prev.map(t => ({ ...t, muted: false, solo: false })))} 
                      className="flex-1 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg text-xs text-stone-400 hover:text-white transition-all"
                    >
                      Reset All
                    </button>
                    <button 
                      onClick={() => setTracks(prev => prev.map(t => ({ ...t, volume: 80 })))} 
                      className="flex-1 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg text-xs text-stone-400 hover:text-white transition-all"
                    >
                      Unity Gain
                    </button>
                  </div>
                  {/* AI Auto-Mix Button */}
                  <button 
                    onClick={() => {
                      // AI Auto-Mix: Balance volumes based on track count
                      const trackCount = tracks.length;
                      if (trackCount === 0) return;
                      
                      // Intelligent volume balancing
                      const baseVolume = Math.round(100 / Math.sqrt(trackCount)); // Headroom for mixing
                      const variation = 10; // Slight variation for natural feel
                      
                      setTracks(prev => prev.map((t, i) => ({
                        ...t,
                        volume: Math.min(100, baseVolume + Math.round((i % 2 === 0 ? -1 : 1) * (variation / 2))),
                        muted: false,
                        solo: false
                      })));
                      setMasterVolume(85); // Set master to safe level
                      toast.success('AI Auto-Mix applied! Volumes balanced for optimal headroom.');
                    }} 
                    className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-lg text-xs text-white font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20"
                  >
                    <Wand2 className="w-4 h-4" />
                    AI Auto-Mix
                  </button>
                </div>
              )}
            </div>
            
            {/* Export - Compact */}
            <div className="bg-stone-800/50 rounded-xl p-3 border border-stone-700">
              <h3 className="text-xs font-bold text-stone-400 mb-2 flex items-center gap-2">
                <Download className="w-3 h-3" /> EXPORT
              </h3>
              <div className="flex items-center gap-2 mb-2">
                <select value={compositeQuality} onChange={(e) => setCompositeQuality(e.target.value)} className="flex-1 px-2 py-1 bg-stone-900 border border-stone-700 rounded text-xs text-white">
                  <option value="480p">480p</option>
                  <option value="720p">720p</option>
                  <option value="1080p">1080p</option>
                </select>
                <button onClick={exportComposite} disabled={tracks.length === 0 || isExporting} className="flex-1 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold rounded text-xs disabled:opacity-50">
                  {isExporting ? `${Math.round(exportProgress)}%` : 'Export HD'}
                </button>
              </div>
              
              {/* Social Export Buttons */}
              <div className="grid grid-cols-2 gap-1">
                <button
                  onClick={() => exportForSocial('tiktok')}
                  disabled={tracks.length === 0 || isExporting}
                  className="py-1.5 bg-gradient-to-r from-pink-500 to-cyan-500 text-white font-bold rounded text-[10px] disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  <Music2 className="w-3 h-3" />
                  TikTok
                </button>
                <button
                  onClick={() => exportForSocial('instagram')}
                  disabled={tracks.length === 0 || isExporting}
                  className="py-1.5 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white font-bold rounded text-[10px] disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  <Instagram className="w-3 h-3" />
                  Reels
                </button>
                <button
                  onClick={() => exportForSocial('youtube')}
                  disabled={tracks.length === 0 || isExporting}
                  className="py-1.5 bg-red-600 text-white font-bold rounded text-[10px] disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  <Play className="w-3 h-3" />
                  Shorts
                </button>
                <button
                  onClick={() => exportForSocial('square')}
                  disabled={tracks.length === 0 || isExporting}
                  className="py-1.5 bg-stone-600 text-white font-bold rounded text-[10px] disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  <Grid3X3 className="w-3 h-3" />
                  Square
                </button>
              </div>
            </div>
            
            {/* Backgrounds */}
            <details className="bg-stone-800/50 rounded-xl border border-green-500/30" open>
              <summary className="px-3 py-2 cursor-pointer text-sm font-bold text-green-400 hover:text-green-300 flex items-center gap-2">
                <Image className="w-4 h-4" />
                Backgrounds
                {backgroundRemovalEnabled && (
                  <span className="ml-auto text-xs bg-green-500/20 px-2 py-0.5 rounded">Active</span>
                )}
              </summary>
              <div className="p-3 pt-0 space-y-3">
                {/* Background Mode Selection - Big Clickable Buttons */}
                <div className="grid grid-cols-2 gap-2">
                  {/* AI Background Removal */}
                  <button
                    onClick={() => {
                      setBackgroundRemovalEnabled(true);
                      setBackgroundRemovalMode('ai');
                    }}
                    className={`p-3 rounded-lg text-left transition-all ${
                      backgroundRemovalEnabled && backgroundRemovalMode === 'ai'
                        ? 'bg-gradient-to-br from-purple-600 to-pink-600 ring-2 ring-purple-400'
                        : 'bg-stone-700 hover:bg-stone-600'
                    }`}
                  >
                    <Wand2 className="w-5 h-5 mb-1" />
                    <div className="text-xs font-bold">AI Removal</div>
                    <div className="text-[9px] text-stone-300 opacity-75">Smart background removal</div>
                  </button>
                  
                  {/* Green Screen */}
                  <button
                    onClick={() => {
                      setBackgroundRemovalEnabled(true);
                      setBackgroundRemovalMode('greenscreen');
                      setGreenScreenColor('#00FF00');
                      // Auto-select black replacement (not green!)
                      setSelectedBackground(BACKGROUND_PRESETS.find(b => b.id === 'black') || BACKGROUND_PRESETS[1]);
                    }}
                    className={`p-3 rounded-lg text-left transition-all ${
                      backgroundRemovalEnabled && backgroundRemovalMode === 'greenscreen' && greenScreenColor === '#00FF00'
                        ? 'bg-green-600 ring-2 ring-green-400'
                        : 'bg-stone-700 hover:bg-stone-600'
                    }`}
                  >
                    <div className="w-5 h-5 mb-1 bg-green-500 rounded" />
                    <div className="text-xs font-bold">Green Screen</div>
                    <div className="text-[9px] text-stone-300 opacity-75">Need green backdrop</div>
                  </button>
                  
                  {/* Blue Screen */}
                  <button
                    onClick={() => {
                      setBackgroundRemovalEnabled(true);
                      setBackgroundRemovalMode('greenscreen');
                      setGreenScreenColor('#0000FF');
                      // Auto-select black replacement (not blue!)
                      setSelectedBackground(BACKGROUND_PRESETS.find(b => b.id === 'black') || BACKGROUND_PRESETS[1]);
                    }}
                    className={`p-3 rounded-lg text-left transition-all ${
                      backgroundRemovalEnabled && backgroundRemovalMode === 'greenscreen' && greenScreenColor === '#0000FF'
                        ? 'bg-blue-600 ring-2 ring-blue-400'
                        : 'bg-stone-700 hover:bg-stone-600'
                    }`}
                  >
                    <div className="w-5 h-5 mb-1 bg-blue-500 rounded" />
                    <div className="text-xs font-bold">Blue Screen</div>
                    <div className="text-[9px] text-stone-300 opacity-75">Need blue backdrop</div>
                  </button>
                  
                  {/* Transparent / None */}
                  <button
                    onClick={() => {
                      setBackgroundRemovalEnabled(false);
                    }}
                    className={`p-3 rounded-lg text-left transition-all ${
                      !backgroundRemovalEnabled
                        ? 'bg-stone-600 ring-2 ring-stone-400'
                        : 'bg-stone-700 hover:bg-stone-600'
                    }`}
                  >
                    <div className="w-5 h-5 mb-1 border-2 border-dashed border-stone-400 rounded" />
                    <div className="text-xs font-bold">Original</div>
                    <div className="text-[9px] text-stone-300 opacity-75">Keep my background</div>
                  </button>
                </div>
                
                {backgroundRemovalEnabled && (
                  <>
                    {/* Chroma Key Controls - Only show for green/blue screen */}
                    {backgroundRemovalMode === 'greenscreen' && (
                      <div className="pt-2 border-t border-stone-700 space-y-3">
                        {/* Similarity Slider - How much of the key color to detect */}
                        <div>
                          <div className="text-xs text-stone-400 mb-1 flex items-center justify-between">
                            <span>Similarity</span>
                            <span className="text-amber-400 font-mono">{Math.round(greenScreenTolerance * 100)}%</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={greenScreenTolerance * 100}
                            onChange={(e) => setGreenScreenTolerance(e.target.value / 100)}
                            className="w-full h-2 bg-stone-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
                          />
                          <div className="flex justify-between text-[9px] text-stone-500 mt-0.5">
                            <span>Only pure {greenScreenColor === '#00FF00' ? 'green' : 'blue'}</span>
                            <span>More shades</span>
                          </div>
                        </div>
                        
                        {/* Spill/Edge Slider - Controls edge softness */}
                        <div>
                          <div className="text-xs text-stone-400 mb-1 flex items-center justify-between">
                            <span>Edge Softness</span>
                            <span className="text-amber-400 font-mono">{Math.round(greenScreenSpill * 100)}%</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={greenScreenSpill * 100}
                            onChange={(e) => setGreenScreenSpill(e.target.value / 100)}
                            className="w-full h-2 bg-stone-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                          />
                          <div className="flex justify-between text-[9px] text-stone-500 mt-0.5">
                            <span>Hard edges</span>
                            <span>Soft/forgiving</span>
                          </div>
                        </div>
                        
                        {/* Tip */}
                        <div className="text-[10px] text-amber-400/80 bg-amber-900/20 border border-amber-700/30 p-2 rounded">
                          ⚠️ <strong>Requires a physical {greenScreenColor === '#00FF00' ? 'GREEN' : 'BLUE'} backdrop!</strong><br/>
                          This removes {greenScreenColor === '#00FF00' ? 'green' : 'blue'} pixels only. No backdrop? Use <strong>AI Removal</strong> instead.
                        </div>
                      </div>
                    )}
                    
                    {/* AI Removal Controls - Only show for AI mode */}
                    {backgroundRemovalMode === 'ai' && (
                      <div className="pt-2 border-t border-stone-700 space-y-3">
                        {/* Edge Smoothness Slider */}
                        <div>
                          <div className="text-xs text-stone-400 mb-1 flex items-center justify-between">
                            <span>Edge Smoothness</span>
                            <span className="text-purple-400 font-mono">{Math.round(aiEdgeSmooth * 100)}%</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={aiEdgeSmooth * 100}
                            onChange={function(e) {
                              var val = e.target.value / 100;
                              setAiEdgeSmooth(val);
                              aiEdgeSmoothRef.current = val;
                            }}
                            className="w-full h-2 bg-stone-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                          />
                          <div className="flex justify-between text-[9px] text-stone-500 mt-0.5">
                            <span>Sharp</span>
                            <span>Smooth</span>
                          </div>
                        </div>
                        
                        {/* Info */}
                        <div className="text-[10px] text-purple-400/80 bg-purple-900/20 border border-purple-700/30 p-2 rounded">
                          ✨ <strong>AI-powered detection</strong> - Works with any background!
                        </div>
                      </div>
                    )}
                    
                    {/* Replacement Background Selection */}
                    <div className="pt-2 border-t border-stone-700">
                      <div className="text-xs text-stone-400 mb-2">Replace with:</div>
                      <div className="grid grid-cols-5 gap-1">
                        {BACKGROUND_PRESETS.map((bg) => (
                          <button
                            key={bg.id}
                            onClick={() => setSelectedBackground(bg)}
                            className={`aspect-square rounded overflow-hidden border-2 transition-all ${
                              selectedBackground.id === bg.id
                                ? 'border-green-500 ring-2 ring-green-400'
                                : 'border-transparent hover:border-stone-500'
                            }`}
                            title={bg.name}
                          >
                            {bg.type === 'color' ? (
                              <div className="w-full h-full" style={{ backgroundColor: bg.color }} />
                            ) : bg.type === 'gradient' ? (
                              <div className="w-full h-full" style={{ background: bg.gradient }} />
                            ) : (
                              <div className="w-full h-full bg-stone-700 flex items-center justify-center text-[8px] text-stone-400">
                                {bg.name.slice(0, 3)}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* AI Background Generator */}
                    <div className="pt-2 border-t border-stone-700">
                      <div className="text-xs text-stone-400 mb-2 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Generate AI Background
                      </div>
                      
                      {/* Quick preset options */}
                      <div className="grid grid-cols-2 gap-1 mb-2">
                        {[
                          'Beach Sunset',
                          'City Skyline Night',
                          'Mountain Landscape',
                          'Studio Lights',
                          'Tropical Rainforest',
                          'Abstract Colors',
                          'Office Background',
                          'Space Galaxy'
                        ].map(function(preset) {
                          return (
                            <button
                              key={preset}
                              onClick={function() {
                                setAiBackgroundPrompt(preset);
                              }}
                              className={'px-2 py-1 rounded text-[10px] transition-all ' + 
                                (aiBackgroundPrompt === preset 
                                  ? 'bg-purple-600 text-white' 
                                  : 'bg-stone-700 text-stone-300 hover:bg-stone-600')}
                              type="button"
                            >
                              {preset}
                            </button>
                          );
                        })}
                      </div>
                      
                      {/* Custom input */}
                      <div className="flex gap-1">
                        <input
                          type="text"
                          value={aiBackgroundPrompt}
                          onChange={function(e) { setAiBackgroundPrompt(e.target.value); }}
                          onKeyDown={function(e) {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (aiBackgroundPrompt.trim() && !isGeneratingBackground) {
                                generateAIBackground(e);
                              }
                            }
                          }}
                          placeholder="Or type your own..."
                          className="flex-1 px-2 py-1.5 bg-stone-900 border border-stone-600 rounded text-xs focus:outline-none focus:border-green-500"
                        />
                        <button
                          onClick={function(e) { generateAIBackground(e); }}
                          disabled={isGeneratingBackground || !aiBackgroundPrompt.trim()}
                          className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-stone-700 disabled:to-stone-700 rounded text-xs font-bold"
                          type="button"
                        >
                          {isGeneratingBackground ? '...' : 'Generate'}
                        </button>
                      </div>
                    </div>
                    
                    {/* Upload Custom Background */}
                    <div className="pt-2 border-t border-stone-700">
                      <div className="text-xs text-stone-400 mb-2 flex items-center gap-1">
                        <Upload className="w-3 h-3" />
                        Upload Your Own Background
                      </div>
                      <label className="flex items-center justify-center gap-2 px-3 py-2 bg-stone-700 hover:bg-stone-600 rounded cursor-pointer transition-all">
                        <Upload className="w-4 h-4" />
                        <span className="text-xs">Choose Image</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={function(e) {
                            var file = e.target.files[0];
                            if (file) {
                              var reader = new FileReader();
                              reader.onload = function(event) {
                                var dataUrl = event.target.result;
                                // Pre-load the image
                                var img = new window.Image();
                                img.onload = function() {
                                  loadedBgImageRef.current = img;
                                  var bgObj = {
                                    id: 'uploaded',
                                    name: 'Uploaded',
                                    type: 'image',
                                    url: dataUrl
                                  };
                                  setSelectedBackgroundState(bgObj);
                                  selectedBackgroundRef.current = bgObj;
                                  toast.success('Background uploaded!');
                                };
                                img.src = dataUrl;
                              };
                              reader.readAsDataURL(file);
                            }
                            // Reset input so same file can be selected again
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </>
                )}
              </div>
            </details>
            
            {/* Video Filters */}
            <details className="bg-stone-800/50 rounded-xl border border-purple-500/30" open={videoFilter !== 'none'}>
              <summary className="px-3 py-2 cursor-pointer text-sm font-bold text-purple-400 hover:text-purple-300 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Video Filters
                {videoFilter !== 'none' && (
                  <span className="ml-auto text-xs bg-purple-500/20 px-2 py-0.5 rounded">{VIDEO_FILTERS.find(f => f.id === videoFilter)?.name}</span>
                )}
              </summary>
              <div className="p-3 pt-0">
                <div className="grid grid-cols-4 gap-1">
                  {VIDEO_FILTERS.map(filter => (
                    <button
                      key={filter.id}
                      onClick={() => setVideoFilter(filter.id)}
                      className={`px-2 py-1.5 rounded text-[10px] font-medium transition-all ${
                        videoFilter === filter.id
                          ? 'bg-purple-500 text-white ring-2 ring-purple-400'
                          : 'bg-stone-700 text-stone-300 hover:bg-stone-600'
                      }`}
                    >
                      {filter.name}
                    </button>
                  ))}
                </div>
                {videoFilter !== 'none' && (
                  <p className="text-[10px] text-stone-500 mt-2">
                    Filter applied to Live Preview & Export
                  </p>
                )}
              </div>
            </details>
            
            {/* Audio Effects */}
            <details className="bg-stone-800/50 rounded-xl border border-cyan-500/30" open={audioEffects.reverb > 0 || audioEffects.delay > 0}>
              <summary className="px-3 py-2 cursor-pointer text-sm font-bold text-cyan-400 hover:text-cyan-300 flex items-center gap-2">
                <Radio className="w-4 h-4" />
                Audio Effects
                {(audioEffects.reverb > 0 || audioEffects.delay > 0) && (
                  <span className="ml-auto text-xs bg-cyan-500/20 px-2 py-0.5 rounded">Active</span>
                )}
              </summary>
              <div className="p-3 pt-0 space-y-3">
                {/* Reverb */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-stone-400">Reverb</span>
                    <span className="text-xs font-mono text-cyan-400">{audioEffects.reverb}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={audioEffects.reverb}
                    onChange={(e) => setAudioEffects(prev => ({ ...prev, reverb: parseInt(e.target.value) }))}
                    className="w-full h-1.5 bg-stone-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                  />
                </div>
                
                {/* Delay */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-stone-400">Delay / Echo</span>
                    <span className="text-xs font-mono text-cyan-400">{audioEffects.delay}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={audioEffects.delay}
                    onChange={(e) => setAudioEffects(prev => ({ ...prev, delay: parseInt(e.target.value) }))}
                    className="w-full h-1.5 bg-stone-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                  />
                </div>
                
                {/* EQ */}
                <div>
                  <span className="text-xs text-stone-400 block mb-2">Equalizer</span>
                  <div className="flex gap-2">
                    <div className="flex-1 text-center">
                      <input
                        type="range"
                        min="-12"
                        max="12"
                        value={audioEffects.eq.bass}
                        onChange={(e) => setAudioEffects(prev => ({ ...prev, eq: { ...prev.eq, bass: parseInt(e.target.value) } }))}
                        className="w-full h-12 appearance-none cursor-pointer accent-cyan-500"
                        style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
                      />
                      <div className="text-[9px] text-stone-500 mt-1">Bass</div>
                      <div className="text-[10px] font-mono text-cyan-400">{audioEffects.eq.bass > 0 ? '+' : ''}{audioEffects.eq.bass}</div>
                    </div>
                    <div className="flex-1 text-center">
                      <input
                        type="range"
                        min="-12"
                        max="12"
                        value={audioEffects.eq.mid}
                        onChange={(e) => setAudioEffects(prev => ({ ...prev, eq: { ...prev.eq, mid: parseInt(e.target.value) } }))}
                        className="w-full h-12 appearance-none cursor-pointer accent-cyan-500"
                        style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
                      />
                      <div className="text-[9px] text-stone-500 mt-1">Mid</div>
                      <div className="text-[10px] font-mono text-cyan-400">{audioEffects.eq.mid > 0 ? '+' : ''}{audioEffects.eq.mid}</div>
                    </div>
                    <div className="flex-1 text-center">
                      <input
                        type="range"
                        min="-12"
                        max="12"
                        value={audioEffects.eq.treble}
                        onChange={(e) => setAudioEffects(prev => ({ ...prev, eq: { ...prev.eq, treble: parseInt(e.target.value) } }))}
                        className="w-full h-12 appearance-none cursor-pointer accent-cyan-500"
                        style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
                      />
                      <div className="text-[9px] text-stone-500 mt-1">Treble</div>
                      <div className="text-[10px] font-mono text-cyan-400">{audioEffects.eq.treble > 0 ? '+' : ''}{audioEffects.eq.treble}</div>
                    </div>
                  </div>
                </div>
                
                {/* Reset button */}
                <button
                  onClick={() => setAudioEffects({ reverb: 0, delay: 0, eq: { bass: 0, mid: 0, treble: 0 } })}
                  className="w-full py-1.5 bg-stone-700 hover:bg-stone-600 rounded text-xs text-stone-300"
                >
                  Reset Effects
                </button>
                <p className="text-[9px] text-stone-500">Note: Effects preview. Full processing on export.</p>
              </div>
            </details>
            
            {/* Duet Mode */}
            <details className="bg-stone-800/50 rounded-xl border border-pink-500/30" open={duetMode}>
              <summary className="px-3 py-2 cursor-pointer text-sm font-bold text-pink-400 hover:text-pink-300 flex items-center gap-2">
                <Share2 className="w-4 h-4" />
                Duet Mode
                {duetMode && duetVideo && (
                  <span className="ml-auto text-xs bg-pink-500/20 px-2 py-0.5 rounded truncate max-w-[100px]">{duetVideo.name}</span>
                )}
              </summary>
              <div className="p-3 pt-0 space-y-2">
                <p className="text-[10px] text-stone-400">Import a video to record alongside (like TikTok duets)</p>
                
                {!duetVideo ? (
                  <>
                    <input
                      ref={duetFileInputRef}
                      type="file"
                      accept="video/*"
                      onChange={handleDuetVideoUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => duetFileInputRef.current?.click()}
                      className="w-full py-3 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500 rounded-lg text-sm font-bold flex items-center justify-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Import Duet Video
                    </button>
                  </>
                ) : (
                  <div className="space-y-2">
                    {/* Duet video preview */}
                    <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                      <video
                        ref={duetVideoElementRef}
                        src={duetVideo.url}
                        className="w-full h-full object-contain"
                        muted
                        playsInline
                        loop
                      />
                      <div className="absolute top-2 left-2 bg-pink-500/80 px-2 py-0.5 rounded text-[10px] font-bold">
                        DUET
                      </div>
                      <div className="absolute bottom-2 left-2 bg-black/70 px-2 py-0.5 rounded text-[10px]">
                        {duetVideo.duration ? formatTime(duetVideo.duration) : '--:--'}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          if (duetVideoElementRef.current) {
                            if (duetVideoElementRef.current.paused) {
                              duetVideoElementRef.current.play();
                            } else {
                              duetVideoElementRef.current.pause();
                            }
                          }
                        }}
                        className="flex-1 py-2 bg-stone-700 hover:bg-stone-600 rounded text-xs flex items-center justify-center gap-1"
                      >
                        <Play className="w-3 h-3" />
                        Preview
                      </button>
                      <button
                        onClick={removeDuetVideo}
                        className="py-2 px-3 bg-red-600 hover:bg-red-500 rounded text-xs flex items-center justify-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    
                    <p className="text-[9px] text-stone-500">Duet video will play during recording and appear in composite.</p>
                  </div>
                )}
              </div>
            </details>
            
            {/* Challenge Templates */}
            <details className="bg-stone-800/50 rounded-xl border border-amber-500/30" open={showChallenges || selectedChallenge !== null}>
              <summary className="px-3 py-2 cursor-pointer text-sm font-bold text-amber-400 hover:text-amber-300 flex items-center gap-2" onClick={() => setShowChallenges(!showChallenges)}>
                <Music className="w-4 h-4" />
                Challenge Templates
                {selectedChallenge && (
                  <span className="ml-auto text-xs bg-amber-500/20 px-2 py-0.5 rounded">{selectedChallenge.emoji} {selectedChallenge.name}</span>
                )}
              </summary>
              <div className="p-3 pt-0 space-y-2">
                <p className="text-[10px] text-stone-400">Pick a rhythm challenge to record along to!</p>
                
                <div className="grid grid-cols-2 gap-1.5">
                  {CHALLENGE_TEMPLATES.map(challenge => (
                    <button
                      key={challenge.id}
                      onClick={() => startChallenge(challenge)}
                      className={`p-2 rounded-lg text-left transition-all ${
                        selectedChallenge?.id === challenge.id
                          ? `bg-gradient-to-r ${challenge.color} ring-2 ring-white/50`
                          : 'bg-stone-700/50 hover:bg-stone-700'
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="text-base">{challenge.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-bold text-white truncate">{challenge.name}</div>
                          <div className="text-[9px] text-white/70">{challenge.bpm} BPM</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                
                {selectedChallenge && (
                  <div className="bg-gradient-to-r from-amber-900/50 to-orange-900/50 rounded-lg p-2 border border-amber-500/30">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{selectedChallenge.emoji}</span>
                      <div>
                        <div className="text-sm font-bold text-amber-400">{selectedChallenge.name}</div>
                        <div className="text-[10px] text-stone-300">{selectedChallenge.description}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex-1 text-xs text-stone-400">
                        Metronome set to <span className="text-amber-400 font-bold">{selectedChallenge.bpm} BPM</span>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedChallenge(null);
                          setMetronomeOn(false);
                        }}
                        className="px-2 py-1 bg-stone-700 hover:bg-stone-600 rounded text-[10px]"
                      >
                        End Challenge
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </details>
            
            {/* Background & Layout - Collapsible at Bottom */}
            <details className="bg-stone-800/50 rounded-xl border border-stone-700">
              <summary className="px-3 py-2 cursor-pointer text-sm font-bold text-stone-400 hover:text-amber-400 flex items-center gap-2">
                <Wand2 className="w-4 h-4" />
                Background & Layout
              </summary>
              <div className="p-3 pt-0 space-y-2">
                <div className="flex flex-wrap gap-1">
                  <button onClick={() => setBackgroundRemovalEnabled(!backgroundRemovalEnabled)} className={`px-2 py-1 rounded text-xs ${backgroundRemovalEnabled ? 'bg-purple-600 text-white' : 'bg-stone-700 text-stone-300'}`}>
                    {backgroundRemovalEnabled ? 'BG ON' : 'BG OFF'}
                  </button>
                  {backgroundRemovalEnabled && (
                    <>
                      <button onClick={() => setBackgroundRemovalMode('ai')} className={`px-2 py-1 rounded text-xs ${backgroundRemovalMode === 'ai' ? 'bg-amber-500 text-black' : 'bg-stone-700 text-stone-300'}`}>AI</button>
                      <button onClick={() => setBackgroundRemovalMode('greenscreen')} className={`px-2 py-1 rounded text-xs ${backgroundRemovalMode === 'greenscreen' ? 'bg-green-500 text-black' : 'bg-stone-700 text-stone-300'}`}>Green</button>
                    </>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {BACKGROUND_PRESETS.slice(0, 5).map(preset => (
                    <button key={preset.id} onClick={() => setSelectedBackground(preset)} className={`px-2 py-1 rounded text-xs ${selectedBackground.id === preset.id ? 'ring-2 ring-amber-500' : ''}`} style={{ background: preset.type === 'transparent' ? 'repeating-conic-gradient(#333 0% 25%, #444 0% 50%) 50% / 6px 6px' : preset.color, color: ['#FFFFFF', '#00FF00'].includes(preset.color) ? '#000' : '#fff' }}>{preset.name}</button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1">
                  {LAYOUT_PRESETS.map(preset => (
                    <button key={preset.id} onClick={() => applyLayoutPreset(preset)} className={`p-1 rounded text-xs ${selectedLayout.id === preset.id ? 'bg-amber-500 text-black' : 'bg-stone-700 text-stone-300'}`}>{preset.icon}</button>
                  ))}
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>
      
      {/* Keyboard Shortcuts Modal */}
      <AnimatePresence>
        {showShortcuts && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowShortcuts(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-stone-900 rounded-2xl border border-stone-700 p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Keyboard className="w-5 h-5 text-amber-500" />
                  Keyboard Shortcuts
                </h2>
                <button
                  onClick={() => setShowShortcuts(false)}
                  className="p-2 hover:bg-stone-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Transport */}
                <div>
                  <h3 className="text-sm font-bold text-amber-400 mb-2">Transport Controls</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'Space', action: 'Play / Pause' },
                      { key: 'R', action: 'Start / Stop Recording' },
                      { key: 'S', action: 'Stop Playback' },
                      { key: '←', action: 'Rewind 5 seconds' },
                      { key: '→', action: 'Forward 5 seconds' },
                      { key: 'Home', action: 'Go to Start' },
                      { key: 'End', action: 'Go to End' },
                    ].map(({ key, action }) => (
                      <div key={key} className="flex items-center justify-between bg-stone-800/50 rounded px-3 py-2">
                        <kbd className="px-2 py-1 bg-stone-700 rounded text-xs font-mono text-amber-400">{key}</kbd>
                        <span className="text-sm text-stone-300">{action}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Track Controls */}
                <div>
                  <h3 className="text-sm font-bold text-purple-400 mb-2">Track Controls</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'M', action: 'Mute Selected Track' },
                      { key: 'O', action: 'Solo Selected Track' },
                      { key: 'Delete', action: 'Delete Selected Track' },
                    ].map(({ key, action }) => (
                      <div key={key} className="flex items-center justify-between bg-stone-800/50 rounded px-3 py-2">
                        <kbd className="px-2 py-1 bg-stone-700 rounded text-xs font-mono text-purple-400">{key}</kbd>
                        <span className="text-sm text-stone-300">{action}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* General */}
                <div>
                  <h3 className="text-sm font-bold text-emerald-400 mb-2">General</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: '?', action: 'Show Shortcuts' },
                      { key: 'Esc', action: 'Close Modal' },
                    ].map(({ key, action }) => (
                      <div key={key} className="flex items-center justify-between bg-stone-800/50 rounded px-3 py-2">
                        <kbd className="px-2 py-1 bg-stone-700 rounded text-xs font-mono text-emerald-400">{key}</kbd>
                        <span className="text-sm text-stone-300">{action}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-stone-500 mt-4 text-center">
                Press <kbd className="px-1 bg-stone-700 rounded">?</kbd> anytime to toggle this menu
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Help / How to Use Modal */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowHelp(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-b from-stone-900 to-stone-950 rounded-2xl border border-amber-500/30 p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold flex items-center gap-3">
                  <div className="p-2 bg-amber-500 rounded-lg">
                    <BookOpen className="w-6 h-6 text-black" />
                  </div>
                  How to Use Multi-Cam Studio
                </h2>
                <button
                  onClick={() => setShowHelp(false)}
                  className="p-2 hover:bg-stone-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Step-by-step guide */}
              <div className="space-y-6">
                {/* Step 1 */}
                <div className="bg-stone-800/50 rounded-xl p-4 border-l-4 border-red-500">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                    <div>
                      <h3 className="font-bold text-lg text-red-400 mb-2">Select Your Main Camera</h3>
                      <p className="text-stone-300 text-sm mb-2">
                        Click on a camera in the list to activate it. The <strong className="text-red-400">FIRST camera you select</strong> becomes your main recording source.
                      </p>
                      <div className="bg-stone-900/50 rounded p-2 text-xs text-stone-400">
                        <strong>Camera Names Explained:</strong>
                        <ul className="mt-1 space-y-1">
                          <li>• <strong>Built-in HD Camera</strong> = Your Mac's built-in webcam</li>
                          <li>• <strong>iPhone [Name]</strong> = Your iPhone (via Continuity Camera)</li>
                          <li>• <strong>OBS Virtual Camera</strong> = Virtual camera from OBS software</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Step 2 */}
                <div className="bg-stone-800/50 rounded-xl p-4 border-l-4 border-amber-500">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold flex-shrink-0">2</div>
                    <div>
                      <h3 className="font-bold text-lg text-amber-400 mb-2">Add Overlay Cameras (Optional)</h3>
                      <p className="text-stone-300 text-sm mb-2">
                        Want multiple angles? Click on additional cameras to add them as <strong className="text-amber-400">overlays</strong>. They'll appear picture-in-picture style in your final video.
                      </p>
                      <p className="text-xs text-stone-500 italic">
                        💡 Great for showing different angles, reaction shots, or screen recordings alongside your main video.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Step 3 */}
                <div className="bg-stone-800/50 rounded-xl p-4 border-l-4 border-green-500">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                    <div>
                      <h3 className="font-bold text-lg text-green-400 mb-2">Hit Record!</h3>
                      <p className="text-stone-300 text-sm mb-2">
                        Press the red <strong className="text-red-400">REC</strong> button or press <kbd className="px-1.5 py-0.5 bg-stone-700 rounded text-xs">R</kbd> on your keyboard to start recording.
                      </p>
                      <p className="text-xs text-stone-500">
                        Recording will capture your main camera. Use the metronome if you need a beat to follow!
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Step 4 */}
                <div className="bg-stone-800/50 rounded-xl p-4 border-l-4 border-purple-500">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">4</div>
                    <div>
                      <h3 className="font-bold text-lg text-purple-400 mb-2">Record More Tracks (Overdubbing)</h3>
                      <p className="text-stone-300 text-sm mb-2">
                        This is the magic! Record another take while your <strong className="text-purple-400">previous recordings play back</strong>. Layer drums, then bass, then vocals - like a one-person band!
                      </p>
                      <p className="text-xs text-stone-500">
                        Each recording becomes a "track" that you can mix, mute, or solo in the Audio Mixer.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Step 5 */}
                <div className="bg-stone-800/50 rounded-xl p-4 border-l-4 border-cyan-500">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">5</div>
                    <div>
                      <h3 className="font-bold text-lg text-cyan-400 mb-2">Mix & Export</h3>
                      <p className="text-stone-300 text-sm mb-2">
                        Use the <strong className="text-cyan-400">Audio Mixer</strong> to balance volume levels. Then hit <strong>Export</strong> to download your masterpiece!
                      </p>
                      <p className="text-xs text-stone-500">
                        Export directly to TikTok, Instagram Reels, or YouTube Shorts format!
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Features Grid */}
                <div className="mt-6 pt-6 border-t border-stone-700">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                    Extra Features
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-stone-800/50 rounded-lg p-3">
                      <div className="text-amber-400 font-bold text-sm mb-1">🎵 Challenge Templates</div>
                      <p className="text-xs text-stone-400">Pick a Brasilian rhythm and record along to the beat!</p>
                    </div>
                    <div className="bg-stone-800/50 rounded-lg p-3">
                      <div className="text-pink-400 font-bold text-sm mb-1">🎭 Duet Mode</div>
                      <p className="text-xs text-stone-400">Import a video and record yourself alongside it.</p>
                    </div>
                    <div className="bg-stone-800/50 rounded-lg p-3">
                      <div className="text-purple-400 font-bold text-sm mb-1">🎨 Video Filters</div>
                      <p className="text-xs text-stone-400">Apply vintage, B&W, or cinematic looks to your video.</p>
                    </div>
                    <div className="bg-stone-800/50 rounded-lg p-3">
                      <div className="text-cyan-400 font-bold text-sm mb-1">🎛️ Audio Effects</div>
                      <p className="text-xs text-stone-400">Add reverb, delay, and EQ to your recordings.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setShowHelp(false)}
                  className="px-6 py-2 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg"
                >
                  Got it, let's record!
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Expanded Screen Share Modal */}
      <AnimatePresence>
        {screenShareExpanded && isScreenSharing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 flex items-center justify-center z-50"
            onClick={() => setScreenShareExpanded(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="w-full h-full max-w-[95vw] max-h-[95vh] flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 bg-stone-900/80 backdrop-blur">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-blue-500 px-3 py-1.5 rounded-full">
                    <Monitor className="w-4 h-4" />
                    <span className="text-sm font-bold">SCREEN SHARE - FULL VIEW</span>
                  </div>
                  {screenShareStream?.getAudioTracks().length > 0 && (
                    <div className="flex items-center gap-1 bg-green-500 px-2 py-1 rounded-full">
                      <Volume2 className="w-3 h-3" />
                      <span className="text-xs font-bold">AUDIO CAPTURING</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      try {
                        if (screenShareVideoRef.current && document.pictureInPictureEnabled) {
                          await screenShareVideoRef.current.requestPictureInPicture();
                          setScreenShareExpanded(false);
                          toast.success('Floating window created!');
                        }
                      } catch (err) {
                        toast.error('PiP not supported');
                      }
                    }}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm font-bold flex items-center gap-2"
                  >
                    <Monitor className="w-4 h-4" />
                    Pop Out Window
                  </button>
                  <button
                    onClick={() => setScreenShareExpanded(false)}
                    className="px-4 py-2 bg-stone-700 hover:bg-stone-600 rounded-lg text-sm font-bold flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Close
                  </button>
                </div>
              </div>
              
              {/* Full Size Video */}
              <div className="flex-1 bg-black flex items-center justify-center p-2">
                <video
                  ref={el => {
                    if (el && screenShareStream) {
                      el.srcObject = screenShareStream;
                    }
                  }}
                  autoPlay
                  muted
                  playsInline
                  className="max-w-full max-h-full object-contain rounded-lg"
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
              
              {/* Footer */}
              <div className="p-3 bg-stone-900/80 backdrop-blur text-center text-xs text-stone-400">
                Press <kbd className="px-2 py-1 bg-stone-700 rounded mx-1">ESC</kbd> or click outside to close • 
                Use <strong className="text-purple-400 ml-1">Pop Out Window</strong> for a floating view you can resize
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MultiCamStudio;
